var App = App || {};

App.classes = {
	wrappers: {},
	workflows: {},
	components: {},
};
;// TODO: add getScript support
// TODO: check all interceptions where JQuery is used

/**
 * @typedef {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'|'HEAD'} HttpMethod
 *
 * @typedef {Object} AjaxRequest
 * @property {HttpMethod} [method='GET']
 * @property {string} url
 * @property {any} [body]
 * @property {Object.<string,string>} [headers]
 * @property {'auto'|'json'|'text'|'html'} [dataType='auto']
 * @property {Object.<string,any>} [params]
 * @property {AbortSignal} [signal]
 * @property {number} [timeout]
 * @property {(data:any,res:Response)=>void} [success]
 * @property {(err:any,res:Response|null)=>void} [error]
 * @property {Object} [options]
 *
 * @typedef {Object} AjaxResponse
 * @property {any} data
 * @property {Response} res
 * @property {Function} abort
 * @property {string} requestId
 */

class Ajax extends SingletonMixin() {

	/** @type {{ baseURL:string, headers:Object, credentials:RequestCredentials, timeout:number }} */
	defaults = {
		baseURL: '',
		headers: {'X-Requested-With': 'XMLHttpRequest'},
		credentials: 'same-origin',
		timeout: 0,
	}

	/** @type {{ baseURL:string, headers:Object, credentials:RequestCredentials, timeout:number }} */
	current = {...this.defaults};

	/** @type {{request:Object, response:Object, success:Object, error:Object}} */
	interceptors = {request: {}, response: {}, success: {}, error: {}};

	controllers = new Map();
	_id = 0;

	/** @param {Partial<Ajax['current']>} partial */
	setDefaults(partial) {
		this.current = {...this.current, ...partial};
	}

	useDefaults() {
		this.setDefaults(this.defaults);
	}

	/**
	 * @param {'request'|'response'|'success'|'error'} type
	 * @param {Function} fn
	 * @param {string|number} [code]
	 * @returns {string|number|undefined}
	 */
	useInterceptor(type, fn, code) {
		const key = code ?? (fn.toString().hashCode?.() ?? String(fn));
		this.interceptors[type][key] = fn;
		return key;
	}

	/**
	 * @param {'request'|'response'|'success'|'error'} type
	 * @param {Function|string|number} fnOrCode
	 */
	ejectInterceptor(type, fnOrCode) {
		let key;
		if (typeof fnOrCode === 'function') {
			const maybeHash = fnOrCode.toString().hashCode?.();
			key = typeof maybeHash !== 'undefined' ? maybeHash : String(fnOrCode);
		} else {
			key = String(fnOrCode);
		}
		delete this.interceptors[type][key];
	}

	/** @param {'request'|'response'|'success'|'error'} type
	 *  @param {Object} ctx
	 */
	runInterceptors(type, ctx) {
		const interceptors = this.interceptors[type];
		for (const interceptor of Object.values(interceptors)) {
			try {
				const result = interceptor(ctx);
				if (result?.cancel) {
					throw new DOMException(result.reason || 'Canceled by interceptor', 'AbortError');
				}
				if (result?.redirect && ctx.req) {
					ctx.req.url = result.redirect;
				}
				Object.assign(ctx, result);
			} catch (error) {
				console.error(error);
			}
		}
		return ctx;
	}


	/** @param {string} url
	 * @param {Object} [params]
	 */
	buildURL(url, params) {
		const base = this.current.baseURL && this.current.baseURL.length > 0
			? this.current.baseURL
			: (typeof location !== 'undefined' ? location.origin : undefined);

		const u = new URL(url, base);

		if (params) {
			for (const [k, v] of Object.entries(params)) {
				if (v !== null && v !== undefined) {
					u.searchParams.set(k, String(v));
				}
			}
		}

		return u.toString();
	}

	/** @param {any} body */
	json(body) {
		return {body: JSON.stringify(body), headers: {'Content-Type': 'application/json'}};
	}

	/** @param {AbortSignal[]} signals */
	static anySignal(signals) {
		const valid = signals.filter(Boolean);
		if (valid.length === 0) return undefined;

		const ctrl = new AbortController();
		const onAbort = (e) => {
			const reason = e?.target?.reason ?? e?.reason;
			ctrl.abort(reason);
		};

		for (const s of valid) {
			s.addEventListener('abort', onAbort, {once: true});
		}

		if (valid.some(s => s.aborted)) {
			const first = valid.find(s => s.aborted);
			if (first) ctrl.abort(first.reason);
		}

		return ctrl.signal;
	}

	/** @param {string} text */
	static async parseHTML(text) {
		if (typeof Node !== 'undefined' && typeof Node.from === 'function') {
			return Node.from(text);
		}
		if (typeof DOMParser !== 'undefined') {
			return new DOMParser().parseFromString(text, 'text/html');
		}
		return text;
	}

	/** @param {any} err
	 * @param {Response} [res]
	 */
	static normalizeError(err, res) {
		if ((err && err.name === 'AbortError') || (typeof err === 'string')) {
			return Object.assign(new Error('Request aborted'), {code: 'ABORT_ERR', res});
		}
		if (res && !res.ok) {
			return Object.assign(new Error(`HTTP ${res.status}`), {code: 'HTTP_ERR', res});
		}
		return Object.assign(new Error(err && err.message ? err.message : 'Network error'), {
			code: 'NET_ERR',
			res,
			cause: err
		});
	}

	/** @param {AjaxRequest} args */
	call(args = {}) {
		const {
			type = 'GET',
			headers = {},
			dataType = 'auto',
			timeout = this.current.timeout,
			url,
			body = null,
			params,
			signal,
			success = () => {
			},
			error = () => {
			},
			options = {}
		} = args;

		let req = {
			type: type,
			url: params ? this.buildURL(url, params) : url,
			body,
			headers,
			dataType,
			options
		};

		req = (this.runInterceptors('request', {req}))?.req || req;

		const ctrl = new AbortController();
		const id = String(++this._id);
		this.controllers.set(id, ctrl);

		const combinedSignal = Ajax.anySignal([ctrl.signal, signal]);

		const opts = {
			method: req.type,
			body: req.body,
			headers: {...this.current.headers, ...req.headers},
			credentials: this.current.credentials,
			signal: combinedSignal,
			...req.options
		};

		if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData || opts.body instanceof Blob)) {
			opts.headers['Content-Type'] = opts.headers['Content-Type'] || 'application/json';
			if (opts.headers['Content-Type'] === 'application/json' && typeof opts.body !== 'string') {
				opts.body = JSON.stringify(opts.body);
			}
		}

		const abort = (reason) => ctrl.abort(reason);

		let headerTimeoutId = null;
		if (timeout > 0) {
			headerTimeoutId = setTimeout(() => ctrl.abort(new DOMException('TimeoutError', 'AbortError')), timeout);
		}

		const runner = async () => {
			let res;
			let raw;
			let data;
			try {
				res = await fetch(req.url, opts);

				if (headerTimeoutId) clearTimeout(headerTimeoutId);

				raw = await res.clone().text();
				data = await this._format(res, req.dataType);

				const afterResp = this.runInterceptors('response', {req, res, text: raw, data, requestId: id});
				if (afterResp) {
					if (afterResp.req) req = afterResp.req;
					if (afterResp.res) res = afterResp.res;
					if (typeof afterResp.data !== 'undefined') data = afterResp.data;
				}
				if (res.ok) {
					this.runInterceptors('success', {req, res, text: raw, data, requestId: id});
					try {
						success(data, res);
					} catch (cbErr) {
						console.error(cbErr);
					}
					return {data, res, abort, requestId: id};
				}

				throw Ajax.normalizeError({data}, res);
			} catch (e) {
				const norm = Ajax.normalizeError(e, res);
				this.runInterceptors('error', {req, res: res ?? null, text: raw ?? null, data: data ?? norm, error: norm, requestId: id});
				try {
					error(norm, res ?? null);
				} catch (cbErr) {
					console.error(cbErr);
				}
				throw norm;
			} finally {
				if (headerTimeoutId) clearTimeout(headerTimeoutId);
				this.controllers.delete(id);
			}
		};

		const promise = runner();
		promise.abort = abort;
		promise.requestId = id;
		return promise;
	}


	/** @param {Response} res
	 * @param {'auto'|'json'|'text'|'html'} dataType
	 * */
	async _format(res, dataType) {
		const noContent = res.status === 204 || res.status === 205 || res.status === 304;
		if (noContent) return null;

		if (dataType === 'json') return res.json();
		if (dataType === 'text') return res.text();
		if (dataType === 'html') {
			const txt = await res.text();
			return Ajax.parseHTML(txt);
		}

		const ctRaw = res.headers.get('content-type');
		const ct = ctRaw ? ctRaw.toLowerCase() : '';

		if (ct.includes('application/json')) return res.json();
		if (ct.includes('text/html')) {
			const txt = await res.text();
			return Ajax.parseHTML(txt);
		}
		if (ct.includes('text/')) return res.text();

		try {
			return await res.arrayBuffer();
		} catch {
			return await res.text();
		}
	}

	/** @param {string|number} requestId
	 *  @param {string} [reason]
	 */
	abortById(requestId, reason) {
		const c = this.controllers.get(String(requestId));
		if (c) c.abort(reason);
	}

	/** @param {AjaxRequest} o */
	get(o) {
		return this.call({...o, method: 'GET'});
	}

	/** @param {AjaxRequest} o */
	del(o) {
		return this.call({...o, method: 'DELETE'});
	}

	/** @param {AjaxRequest} o */
	post(o) {
		return this.call({...o, method: 'POST'});
	}

	/** @param {AjaxRequest} o */
	put(o) {
		return this.call({...o, method: 'PUT'});
	}

	/** @param {AjaxRequest} o */
	patch(o) {
		return this.call({...o, method: 'PATCH'});
	}
}

App.classes.ajax = Ajax;
;class DashboardBlockController {
	constructor({host, targetRoot}) {
		this.host = host;
		this.target = targetRoot;
		this._unbind = [];

		this._uid = 'id'+App.helper.random.string(5);
	}

	getAsyncId(name) {
		return `${this._uid}__${name}`;
	}

	isIdleAsync(name) {
		return App.core.async_task.is_idle(this.getAsyncId(name));
	}

	abortAsync(name) {
		App.core.async_task.abort(this.getAsyncId(name));
	}

	ajaxAsync(name, options) {
		return App.core.async_task.ajax({
			...options,
			id: this.getAsyncId(name),
		});
	}

	$(sel, root = this.target) {
		return root?.querySelector(sel) || null;
	}

	$$(sel, root = this.target) {
		return Array.from(root?.querySelectorAll(sel) || []);
	}

	get header() {
		return this.$('.dashboard__block__header');
	}

	get body() {
		return this.$('.dashboard__block__body') || this.$('.dashboard__block_columns');
	}

	get loader() {
		let el = this.$('.dashboard__block__loader');
		if (!el) {
			el = document.createElement('div');
			el.className = 'dashboard__block__loader';
			el.innerHTML = `<ws-icon name="worksection_bold-solid"></ws-icon><div class="loader"></div>`;
			(this.body || this.target).prepend(el);
		}
		return el;
	}

	init() {
		this.initTabs();
		this.initMoreBtn();

		this.$$('[data-do-filter]').forEach((element) => {
			element.addEventListener('click', (e) => {
				e.preventDefault();
				this.doFilter(
					element,
					element.hasAttribute('data-do-filter-full'),
					element.hasAttribute('data-do-filter-resize')
				);
			});
		});

		this.initScrolls();
		this.initMenus();
		this.initClicks();
		this.initBlocks();

		const dash = this.host.closest('.dashboard');
		if (dash && !App.device.is_mobile()) {
			this.initDrag();
		}

		App.core.call_queue.add(`dashboard_block_${this.host.getAttribute('id')}`, () => {
			this.host.closest('.dashboard')?.removeClass('dashboard-loading');
		}, this, null, 1);
	}


	initDrag() {
		$(this.host).closest('.dashboard')
			.find('.dashboard__row__dragLine')
			.bind('mousedown.page touchstart.page', App.controls.dashboard.ontouchstart_height);
		$(document)
			.bind('mouseup.page touchend.page', App.controls.dashboard.ontouchend_height)
			.bind('mousemove.page touchmove.page', App.controls.dashboard.ontouchmove_height);
		this._unbind.push(() => {
			$(this.host).closest('.dashboard')
				.find('.dashboard__row__dragLine')
				.unbind(('mousedown.page touchstart.page', App.controls.dashboard.ontouchstart_height));
			$(document)
				.unbind('mouseup.page touchend.page', App.controls.dashboard.ontouchend_height)
				.unbind('mousemove.page touchmove.page', App.controls.dashboard.ontouchmove_height);
		});
	}

	initBlocks() {
		const fn = this.host.getAttribute('data-init-function');
		if (fn) eval(fn);
	}

	initMenus() {
		this.$$('[data-menu-id]').forEach((value) => {
			const newMenuId = `${this.host.getAttribute('id')}-${value.getAttribute('data-menu-id')}${this.isModalTarget() ? '-modal' : ''}`;
			document.getElementById(newMenuId)?.remove();

			const menu = this.target.querySelector(`#${value.getAttribute('data-menu-id')}.menu`)
				|| this.host.querySelector(`#${value.getAttribute('data-menu-id')}.menu`);
			if (!menu) return;

			menu.setAttribute('id', newMenuId);
			menu.setAttribute('data-block-id', this.host.getAttribute('id'));
			value.setAttribute('data-menu-id', newMenuId);

			value.addMultipleEventListener(['click', 'dblclick', 'contextmenu'], (e) => {
				if ((value.getAttribute('name') || '').includes('btn-disabled')) return false;

				if (newMenuId.includes('extra')) {
					App.controls.dashboard.init_extra_menu($(this.host), newMenuId);
				}
				if (e.type === 'click') {
					App.click_listener.dblclick.call(() => {
						App.menu.show({
							anchor: value,
							box: '#' + newMenuId,
							type: 'mymenu',
							force_dropdown: true,
							onhide: function () {
								if (App.controls.dashboard.block_selected_by_extra) {
									document.querySelectorAll('.dashboard__block')
										.removeClass('dashboard__block-selected', 'route-updown', 'route-leftright');
									App.controls.dashboard.block_selected_by_extra = false;
								}
							}
						});
					});
				}
				if (e.type === 'dblclick') {
					App.click_listener.dblclick.stop(true);
					App.controls.dashboard.extra_dblclick_action(newMenuId);
				}
				return false;
			});
		});
	}

	initScrolls(defaultScroll) {
		this.$$('.scrollZone').forEach((scrollZone) => {
			if (!scrollZone.classList.contains('ps')) App.page.scrollBars.push(App.controls.js_scrollbars.init(scrollZone));
			if (typeof defaultScroll !== 'undefined') scrollZone.scrollTo({top: defaultScroll});

			scrollZone.addMultipleEventListener(["ps-scroll-up", "ps-scroll-down"], () => {
				if (this.target.querySelector('.dashboard__block__header:not(.dashboard__block__header-bottom)')) {
					if (this.target.querySelector('.scrollZone')?.classList?.contains('scrollZone-top')) {
						this.header?.classList?.add('dashboard__block__header-bottomDynamic');
					} else {
						this.header?.classList?.remove('dashboard__block__header-bottomDynamic');
					}
				}
			});
		});
		// this.setupSafeBottom();
	}

	initClicks() {
		if (this.target.__offInitClicks) this.target.__offInitClicks();
		this.target.__offInitClicks = App.ui.onMouse(this.target, '.sidePanel__list__item-task', {
			onBefore(e) {
				e.data.id_task = $(e.el).attr('data-task');
				e.data.url = App.helper.url.task(e.data.id_task);
			},
			click: e => App.task_view.show({url: e.data.url, anchor: e.el}),
			open: e => App.core.new_window(e.data.url)
		});
	}

	initTabs() {
		if (this.target.__offInitTabs) this.target.__offInitTabs();
		this.target.__offInitTabs = App.ui.onMouse(this.target, '.tabs__item', {
			click: e => this.onTabClick(e.el)
		});
	}

	initMoreBtn() {
		if (this.target.__offInitMoreBtn) this.target.__offInitMoreBtn();
		this.target.__offInitMoreBtn = App.ui.onMouse(this.target, '.dashboard__block__more ws-btn', {
			click: e => this.onMore(e.el)
		});
	}

	withLoader(name, run) {
		const body = this.body, header = this.header, loader = this.loader;
		const show = () => {
			if (!body || !header || !loader) return;
			const br = body.getBoundingClientRect(), hr = header.getBoundingClientRect();
			loader.style.height = Math.max(0, br.height - 2) + 'px';
			loader.style.top = this.isModalTarget() ? 0 : hr.height + 'px';
			loader.style.display = 'block';
		};
		const hide = () => {
			if (!loader) return;
			if (this.isIdleAsync(name)) {
				loader.style.display = 'none';
			}
		};

		show();
		try {
			const r = run();

			if (r && typeof r.always === 'function') {
				r.always(hide);
			} else if (r && typeof r.finally === 'function') {
				r.finally(hide);
			} else {
				queueMicrotask(hide);
			}
			return r;
		} catch (e) {
			hide();
			throw e;
		}
	}


	composeUrl(full, {withoutFilter, withoutType, height} = {}) {
		const base = App.helper.endpoint.ajax();
		const params = new URLSearchParams();
		const seed = App.actions.get?.(this.host.getAttribute('data-action'));
		if (seed) {
			(seed instanceof URLSearchParams ? seed : new URLSearchParams(String(seed)))
				.forEach((v, k) => params.append(k, v));
		}
		const add = (k, v) => {
			if (v !== undefined && v !== null && String(v) !== '') params.set(k, String(v));
		};
		const addData = (el, da, qk) => el?.getAttribute && add(qk, el.getAttribute(da));

		add('height', height ?? $(this.host).height?.());

		const body = this.body;
		if (!withoutType) {
			addData(body, 'data-type', 'type');
		}
		addData(body, 'data-limit', 'limit');

		addData(this.host, 'data-grid-name', 'grid_name');
		addData(this.host, 'data-project', 'id_project');
		this.isModalTarget() && add('is_from_modal', 1);

		if (!withoutFilter) {
			addData(body, 'data-sel-user', 'sel_user');
			addData(body, 'data-sel-project', 'sel_project');
			addData(body, 'data-selection', 'selection');
			addData(body, 'data-order', 'order');
		}
		body.getAttributeNames().forEach((name) => {
			if (name.startsWith('data-custom-')) {
				const key = name.substring('data-custom-'.length);
				addData(body, name, key);
			}
		});
		!full && add('is_body_only', 1);
		return `${base}?${params.toString()}`;
	}

	onTabClick(target) {
		if (target.classList.contains('active') && this.loader && this.loader.offsetParent !== null) return false;

		const type = target.getAttribute('data-type');
		let hadScroll = false;
		this.body?.querySelectorAll(':scope > div').forEach((el) => {
			if (!el.isHidden?.() && el.querySelector('.scrollZone')?.classList.contains('ps--active-y')) hadScroll = true;
		});

		this.body?.setAttribute('data-type', type);
		const url = this.composeUrl(false);
		const hide_all = () => this.body?.querySelectorAll(':scope > div').forEach(d => d.hide?.());
		this.$$('.tabs__item').forEach(t => t.classList.toggle('active', Boolean(t.getAttribute('data-type') === type)));

		const div = this.$(`#${this.host.getAttribute('id')}-${type}`);
		if (div) {
			this.abortAsync('getTabContent');
			hide_all();
			div.show?.();
			$.ajax({type: 'POST', url: url + '&save_settings=1&save_tab=1'});
		} else {
			this.withLoader('getTabContent', () => {
				return this.ajaxAsync('getTabContent', {
					type: 'POST',
					url: url + '&save_settings=1',
					success: (html) => {
						hide_all();
						this.body?.append(...Node.from(html));
						if (!hadScroll) {
							this.removeBlockScroll()
						}
						this.initScrolls();
						App.bus.app.emit('block_mounted', {}, {block: this.host});
					},
				});
			});
		}
		return false;
	}

	onMore(targetBtn) {
		if ((targetBtn.getAttribute('name') || '').includes('btn-disabled')) return false;
		const body = this.body;
		if (!body) return false;

		const more = targetBtn.closest('.dashboard__block__more');
		if (more.hasAttribute('data-fake')) {
			this.target.querySelectorAll('.dashboard__block__item-none').forEach(n => n.removeClass('dashboard__block__item-none'));
			more.hide();
			return false;
		}
		const limit = more?.getAttribute('data-limit');
		const type = body.getAttribute('data-type');

		targetBtn.setAttribute('name', `${targetBtn.getAttribute('name') || ''} btn-disabled`);
		body.setAttribute('data-limit', limit);

		this.ajaxAsync('getTabContent', {
			type: 'GET',
			url: this.composeUrl(false),
			success: (html) => {
				const sel = `#${this.host.getAttribute('id')}-${type}`;
				const holder = this.target.querySelector(sel) || document.querySelector(sel);
				const zone = holder?.querySelector('.scrollZone');
				const tmp = (zone && zone.scrollHeight) ? (zone.scrollHeight - zone.clientHeight) : 0;
				if (holder) holder.replaceWith(...Node.from(html));
				this.initScrolls(tmp);
				App.bus.app.emit('block_mounted', {}, {block: this.host});
			}
		});
		return false;
	}

	doFilter(elem, full, resize) {
		App.menu.hide(true);

		const text = elem.getAttribute('data-text');
		const order = elem.getAttribute('data-order');
		const selection = elem.getAttribute('data-selection');
		const selUser = elem.getAttribute('data-sel-user');
		const selProject = elem.getAttribute('data-sel-project');
		const customSettings = {};
		elem.getAttributeNames().forEach((name) => {
			if (name.startsWith('data-custom-')) {
				const key = name.substring('data-custom-'.length);
				customSettings[key] = elem.getAttribute(name);
			}
		})


		const body = this.body;
		if (!body) return;

		let hadScroll = false;
		body.querySelectorAll(':scope > div').forEach((el) => {
			if (!el.isHidden?.() && el.querySelector('.scrollZone')?.classList.contains('ps--active-y')) hadScroll = true;
		});

		if (order != null) body.setAttribute('data-order', order);
		if (selection != null) body.setAttribute('data-selection', selection);
		if (selUser != null) body.setAttribute('data-sel-user', selUser);
		if (selProject != null) body.setAttribute('data-sel-project', selProject);
		Object.keys(customSettings).forEach((key) => {
			body.setAttribute(`data-custom-${key}`, customSettings[key]);
		});
		this.withLoader('getTabContent', () => {
			return this.ajaxAsync('getTabContent', {
				type: 'POST',
				url: this.composeUrl(!!full) + '&save_settings=1' + (resize ? '&resize_reload=1' : ''),
				success: (nodes) => {
					nodes = Node.from(nodes);
					if (full) {
						const newBody = nodes[0].querySelector('.dashboard__block__body') || nodes[0];
						const newHeader = nodes[0].querySelector('.dashboard__block__header');
						if (this.isModalTarget()) {
							this.body?.replaceWith(newBody);
							if (newHeader && this.header) {
								this.header.replaceWith(newHeader);
							}
							this.init();
						} else {
							const block = nodes[0];
							this.host.replaceWith(block);
							block.init?.();
							App.page.blockObjs = document.querySelectorAll('.dashboard__block[data-container]');
						}
					} else {
						const idPart = this.host.getAttribute('id');
						body.querySelectorAll(`[id*="${CSS.escape(idPart)}"]`).forEach(n => n.remove());
						body.append(...nodes);
						if (!hadScroll) {
							this.removeBlockScroll()
						}
					}
					const menu = elem.closest('.menu');
					if (menu) {
						menu.querySelectorAll('.menu__item').forEach(i => i.classList.remove('menu__item-active'));
						elem.classList.add('menu__item-active');
						if (text) this.$(`[data-menu-id="${menu.id}"]`)?.setAttribute('text', text);
					}
					App.controls.dashboard.run_operation({operation: RELOAD_MENU_OPERATION, data: []});
				}
			});
		});
	}

	refresh() {
		this.withLoader('getTabContent', () => {
			return this.ajaxAsync('getTabContent', {
				type: 'POST',
				url: this.composeUrl(true, {withoutFilter: true, withoutType: true}),
				success: (html) => {
					html = Node.from(html);
					if (this.isModalTarget()) {
						const newBody = html.find(n => n?.classList?.contains('dashboard__block__body')) || html[0];
						this.body?.replaceWith(newBody);
						this.initScrolls();
					} else {
						const block = html[0];
						this.host.replaceWith(block);
						block.init?.();
					}
				},
			});
		});
	}

	/**
	 * TODO rename, cause its not real modal now
	 */
	isModalTarget() {
		return !!(this.target.closest?.('.dashboardView') || this.target.querySelector?.('#modal__headerContent'));
	}

	dispose() {
		this._unbind.forEach(fn => fn());
		this._unbind.length = 0;
	}

	removeBlockScroll() {
		// в модалці 2-3 сторінки прогружаються зразу
		if (this.isModalTarget()) return;
		const body = this.body;
		const limit = body.getAttribute('data-limit');
		const itemsSelector = this.host.getItemsSelector({isModalTarget: this.isModalTarget()});
		const itemHeight = Math.max(...Array.from(
			this.$$(itemsSelector).filter((el) => !el.closest('.scrollZone').isHidden())
		).map((el) => el.outerHeight));

		if (itemHeight) {
			const more = Array.from(body.querySelectorAll('.dashboard__block__more'))
				.filter(el => !el.closest('.scrollZone').isHidden())[0];
			let countItems = Math.floor((body.height - (more?.outerHeight || 0)) / itemHeight);
			let removeMoreHidden = false;
			if (limit - countItems < 10) {
				this.$$(itemsSelector)
					.filter((el) => !el.closest('.scrollZone').isHidden())
					.forEach(function (value, index, array) {
						if (index >= countItems) {
							value.classList.add('dashboard__block__item-none');
							removeMoreHidden = true;
						}
					});
				if (removeMoreHidden && more) {
					more.classList.remove('hidden');
				}
			}
		}
		// this.setupSafeBottom();
	}

	// css fix: замінив на padding-bottom у scrollZone
	// setupSafeBottom() {
	// 	if (this.isModalTarget()) {
	// 		this.$$('safe-bottom').forEach((el) => el.classList.remove('safe-bottom'));
	// 		const body = this.body;
	// 		const itemsSelector = this.host.getItemsSelector({isModalTarget: this.isModalTarget()});
	// 		const items = this.$$(itemsSelector).filter((el) => !el.closest('.scrollZone').isHidden());
	// 		const more = Array.from(body.querySelectorAll('.dashboard__block__more'))
	// 			.filter(el => !el.closest('.scrollZone').isHidden())[0];
	// 		const allItems = [...items, more].filter((el) => el && !el.isHidden() && !el.closest('.scrollZone').isHidden());
	// 		const lastItem = allItems[allItems.length - 1];
	// 		lastItem.classList.add('safe-bottom');
	// 	}
	// }
}

App.classes.dashboard_block_controller = DashboardBlockController;

;class DashboardBlockControllerTarget {
	/** @type {HTMLElement|null} */
	host = null;
	/** @type {HTMLElement|null} */
	target = null;
	/** @type {DashboardBlockController|null} */
	controller = null;
	/** @type {function(DashboardBlockControllerTarget)|null} */
	mounted = null;
	/** @type {function(DashboardBlockControllerTarget)|null} */
	unmounted = null;

	constructor({
		beforeMount = null,
		mounted = null,
		beforeUnmount = null,
		unmounted = null,
				} = {}) {
		this.beforeMount = beforeMount;
		this.mounted = mounted;
		this.beforeUnmount = beforeUnmount;
		this.unmounted = unmounted;
	}

	isMounted() { return !!this.target; }


	mount(host, target) {
		if (this.isMounted()) this.unmount();
		this.beforeMount?.(this);
		this.host = host;
		this.target = target;

		this.controller = new DashboardBlockController({ host: host, targetRoot: target });
		this.controller.init();
		this.mounted?.(this);

	}

	unmount() {
		if (!this.isMounted()) return;
		this.beforeUnmount?.(this);
		if (this.controller) {
			this.controller.dispose?.();
			this.controller = null;
		}
		this.target = null;
		this.host = null;
		this.unmounted?.(this);
	}

}

App.classes.dashboard_block_controller_target = DashboardBlockControllerTarget;

;class GlobalEventHub extends SingletonMixin(){

	/** @type {WeakMap<EventTarget, Map<string, { listener: Function, handlers: Set<Function> }>>} */
	_byTarget = new WeakMap();


	/**
	 * @param {EventTarget}                       target
	 * @param {string}                            eventName
	 * @param {Function}                          handler
	 * @param {boolean|AddEventListenerOptions}   [options=false]
	 */
	register(target, eventName, handler, options = false) {
		const key = this._slotKey(eventName, options);

		if (key === null) {
			target.addEventListener(eventName, handler, options);
			return;
		}

		const slots = this._slotsFor(target);
		let   slot  = slots.get(key);

		if (!slot) {
			const listener = (event) => { for (const h of slot.handlers) h(event); };
			slot = { listener, handlers: new Set() };
			slots.set(key, slot);
			target.addEventListener(eventName, listener, options);
		}

		slot.handlers.add(handler);
	}

	/**
	 * @param {EventTarget}                       target
	 * @param {string}                            eventName
	 * @param {Function}                          handler
	 * @param {boolean|AddEventListenerOptions}   [options=false]
	 */
	unregister(target, eventName, handler, options = false) {
		const key = this._slotKey(eventName, options);

		if (key === null) {
			target.removeEventListener(eventName, handler, options);
			return;
		}

		const slots = this._byTarget.get(target);
		if (!slots) return;

		const slot = slots.get(key);
		if (!slot) return;

		slot.handlers.delete(handler);

		if (slot.handlers.size === 0) {
			target.removeEventListener(eventName, slot.listener, options);
			slots.delete(key);
			if (slots.size === 0) this._byTarget.delete(target);
		}
	}


	/** @param {EventTarget} target */
	_slotsFor(target) {
		let slots = this._byTarget.get(target);
		if (!slots) this._byTarget.set(target, (slots = new Map()));
		return slots;
	}

	/**
	 *
	 * @param {string}                          eventName
	 * @param {boolean|AddEventListenerOptions} options
	 * @returns {string|null}
	 */
	_slotKey(eventName, options) {
		if (!options)         return eventName;
		if (options === true) return `${eventName}\x00c`;

		const { capture = false, passive = false, once = false } = options;
		if (once) return null;

		return `${eventName}\x00${capture ? 'c' : ''}${passive ? 'p' : ''}`;
	}
}
;/**
 * @typedef {Object} FormInstanceOptions
 * @property {Object} [initialValues={}] - The initial values of the form fields.
 * @property {(values: Object) => Object} [validate] - A function that takes the current form values and returns an object of validation errors.
 * @property {(values: Object) => Promise<void>} [onSubmit] - An async function that is called when the form is submitted with valid data.
 */

class FormInstance {
	/**
	 * @param {FormInstanceOptions} options
	 */
	constructor({
					initialValues = {},
					validate = null,
					onSubmit = null,
				} = {}) {
		this._initialValues = structuredClone(initialValues);
		this.values = structuredClone(initialValues);

		this.errors = {};
		this.touched = {};
		this.dirty = {};

		this.submitCount = 0;
		this.isSubmitting = false;

		this._validate = validate;
		this._onSubmit = onSubmit;

		/** @type {Set<Function>} */
		this._subscribers = new Set();

		/** @type {Map<string, Set<Function>>} */
		this._fieldSubscribers = new Map();

		this.validate();
	}

	subscribe(listener) {
		this._subscribers.add(listener);
		return () => this._subscribers.delete(listener);
	}

	subscribeField(fieldPath, listener) {
		if (!this._fieldSubscribers.has(fieldPath)) this._fieldSubscribers.set(fieldPath, new Set());
		this._fieldSubscribers.get(fieldPath).add(listener);
		return () => {
			const set = this._fieldSubscribers.get(fieldPath);
			if (!set) return;
			set.delete(listener);
			if (set.size === 0) this._fieldSubscribers.delete(fieldPath);
		};
	}

	_emit(changedFieldPath = null) {
		for (const listener of this._subscribers) listener();

		if (changedFieldPath) {
			const set = this._fieldSubscribers.get(changedFieldPath);
			if (set) for (const listener of set) listener();
		}
	}

	getValue(fieldPath) {
		return App.helper.object.readByPath(this.values, fieldPath);
	}

	setValue(fieldPath, nextValue, { touch = true, validate = true } = {}) {
		App.helper.object.setByPath(this.values, fieldPath, nextValue);

		if (touch) this.touched[fieldPath] = true;

		const initialValue = App.helper.object.readByPath(this._initialValues, fieldPath);
		this.dirty[fieldPath] = !FormInstance._shallowEqual(nextValue, initialValue);

		if (validate) this.validate();

		this._emit(fieldPath);
	}

	isDirty(fieldPath = null) {
		if (fieldPath) {
			return !!this.dirty[fieldPath];
		}
		return Object.values(this.dirty).some(Boolean);
	}

	validate() {
		if (!this._validate) {
			this.errors = {};
			return this.errors;
		}
		const nextErrors = this._validate(this.values) || {};
		this.errors = nextErrors;
		return nextErrors;
	}

	get isValid() {
		return !this.errors || Object.keys(this.errors).length === 0;
	}

	async submit() {
		this.submitCount += 1;

		this.validate();
		this._emit(null);

		if (!this.isValid) return { ok: false, errors: this.errors };
		if (!this._onSubmit) return { ok: true };

		this.isSubmitting = true;
		this._emit(null);

		try {
			await this._onSubmit(this.values);
			return { ok: true };
		} finally {
			this.isSubmitting = false;
			this._emit(null);
		}
	}

	reset(nextInitialValues = null) {
		const initial = nextInitialValues ?? this._initialValues;

		this._initialValues = structuredClone(initial);
		this.values = structuredClone(initial);

		this.errors = {};
		this.touched = {};
		this.dirty = {};

		this.submitCount = 0;
		this.isSubmitting = false;

		this._emit(null);
	}

	static _shallowEqual(a, b) {
		return a === b;

	}
}

;App.classes = App.classes || {};

App.classes.input_menu_command = class InputCommandMenu {

	text = '';
	recording = false;
	type = '';

	constructor({
					input,
					menu,
					onApply
				}) {
		this.input = $(input);
		this.menu = $(menu);
		this.onApply = onApply;
		this.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		this.input
			.on('focus', (e, data = {}) => this.apply())
			.on('blur', (e, data = {}) => {
				this.apply();
			})
			.on('focusout', (e, data = {}) => {
				this.recording = false;
			})
			.on('keydown', (e, data = {}) => {
				if (!App.device.is_mobile()) return this.handleKeyDown(e, data);
			})
			.on('input', (e, data = {}) => {
				if (App.device.is_mobile()) return this.handleInput(e, data);
			});
	}

	hide() {
		if (App.menu.in_editor) {
			App.menu.hide_all();
			this.clearInput();
			this.input.focus();
		}
	}

	reset() {
		this.text = '';
		this.recording = false;
		this.type = '';
	}

	clearInput() {
		const value = this.input.val();
		const caretPos = this.input[0].selectionStart;
		const pos = value.lastIndexOf(this.type, caretPos - this.text.length - 1);
		if (pos !== -1) {
			this.input.val(value.slice(0, pos) + value.slice(pos + this.text.length + 1));
			this.input[0].setSelectionRange(pos, pos);
		}
		this.reset();
	}

	apply() {
		this.onApply?.(this.text, this.menu);
	}

	handleKeyDown(e, data = {}) {
		const key = e.key || '';
		const keyCode = e.which || e.keyCode || e.charCode;

		switch (true) {

			case keyCode === App.keycode.Escape && App.menu.in_editor:
				App.menu.hide();
				this.clearInput();
				this.input.focus();
				return false;

			case key === '/':
				this.reset();
				this.type = '/';
				this.openMenu(data.onshow);
				this.recording = true;
				break;

			case keyCode === App.keycode.Backspace:
				if (this.text && this.recording) {
					this.text = this.text.slice(0, -1);
				} else if (App.menu.in_editor && this.recording) {
					App.menu.hide();
					this.reset();
				}
				break;

			case [App.keycode.ArrowRight, App.keycode.ArrowLeft].includes(keyCode):
				if (this.recording) {
					e.preventDefault();
				}
				break;

			default:
				if (key.length === 1 && this.recording) {
					this.text += key.toLowerCase();
					this.input.trigger('change');
				}
				break;
		}
		App.menu.in_editor && this.apply();

	}

	handleInput(e, data = {}) {
		const key = e.originalEvent.data || e.key || '';

		switch (true) {

			case key === '/':
				this.reset();
				this.type = '/';
				this.openMenu(data.onshow);
				this.recording = true;
				break;

			default:
				if (key.length === 1 && this.recording) {
					this.text += key.toLowerCase();
					this.input.trigger('change');
				}
				break;
		}
		App.menu.in_editor && this.apply();

	}

	openMenu(onshow) {
		App.menu.show({
			anchor: this.input,
			box: this.menu,
			onshow,
		});
		App.menu.in_editor = true;
		this.input.focus();
	}
}
;App.classes = App.classes || {};


class ScheduleView extends SingletonMixin(HasRefsMixin()) {
	container = null;
	limits = null;
	lastDate = null;
	taskId = null;
	loadedDays = 0;
	userIdTo = null;
	userIdFrom = null;
	idProject = null;
	saveQueue = {};
	/**
	 * @type {function(...[*]): void}
	 */
	flushQueueFn = null;

	constructor({container, limits, lastDate, taskId, userIdTo, userIdFrom, projectId}) {
		super();
		this.container = container;
		this.limits = limits ? App.helper.parseTimeToMinutes(limits) : null;
		this.lastDate = lastDate;
		this.taskId = taskId;
		this.userIdTo = userIdTo;
		this.userIdFrom = userIdFrom;
		this.idProject = projectId;
		this.flushQueueFn = App.helper.debounce(() => {
			this.flushSaveQueue();
		}, 300);
		this.setupCallbacks();
	}


	get refsContainer() {
		return this.container;
	}

	get scheduleLines() {
		return this.refs('schedule-date-line');
	}

	get presetsValue() {
		return this.presets.its_value;
	}

	get presets() {
		return this.ref('presets');
	}

	get pie() {
		return this.ref('hours-pie');
	}

	getPresetMinutes(dateLine) {
		const value = this.presetsValue;
		switch (value) {
			case "max":
				return dateLine.timepicker.maxMinutes;
			case "flexible":
				return 60;
			default:
				return (parseInt(value || 0) || 0) * 60;
		}
	}

	setupCallbacks(elems = null) {
		(elems || this.scheduleLines).forEach(dateLine => {
			if (dateLine.nodeName !== 'SCHEDULE-DATE-LINE') {
				return;
			}
			dateLine.addEventListener('add-time', ({detail: {addedMinutes}}) => {
				this.handleAddTime(dateLine, addedMinutes);
			});

			dateLine.addEventListener('remove-time', ({detail: {removedMinutes}}) => {
				this.handleRemoveTime(dateLine, removedMinutes);
			});

			dateLine.addEventListener('checked', () => {
				this.handleChecked(dateLine);
			});
			dateLine.addDelegatedEventListener("click", 'schedule-date-progressbar', () => {
				dateLine.checkbox.toggle();
			});

			dateLine.addEventListener('unchecked', () => {
				this.handleUnchecked(dateLine);
			});

			dateLine.addEventListener('change', ({detail}) => {
				this.handleChange(dateLine, detail);
			});

			dateLine.addEventListener('save', () => {
				this.saveDateLine(dateLine);
			});

			dateLine.addEventListener('time-control-btn-clicked', () => {
				this.presets.its_value = 'flexible';
			});
		});
		if (!elems) {
			this.ref('main-checkbox').addEventListener('change', () => this.handleMainCheckbox());
			this.ref('more-btn').addEventListener('click', () => this.loadMore());
			this.presets.addEventListener('change', () => this.presets.its_value !== 'flexible' && this.applyPreset());
		}
	}


	handleAddTime(dateLine, addedMinutes) {
		dateLine.progressbar.addSelectedNew(addedMinutes);
		this.pie?.track(addedMinutes);
	}

	handleRemoveTime(dateLine, removedMinutes) {
		dateLine.progressbar.removeSelectedNew(removedMinutes);
		this.pie?.untrack(removedMinutes);
	}

	handleChecked(dateLine) {
		this.enableDateLine(dateLine);
		if (!dateLine.timepicker.totalMinutes) {
			dateLine.timepicker.setTotalMinutes(this.getPresetMinutes(dateLine));
		} else {
			this.pie?.track(dateLine.timepicker.totalMinutes);
			this.updateTimeTracked();
		}
		dateLine.dispatchEvent(new Event('save', {bubbles: true, composed: true, cancelable: false}));
	}

	handleUnchecked(dateLine) {
		this.disableDateLine(dateLine);
		if (dateLine.timepicker.totalMinutes) {
			this.pie?.untrack(dateLine.timepicker.totalMinutes);
		}
		this.updateTimeTracked();
		dateLine.dispatchEvent(new Event('save', {bubbles: true, composed: true, cancelable: false}));
	}

	handleChange(dateLine, detail) {
		if (!detail || !('newTotalMinutes' in detail)) return;
		const {newTotalMinutes, oldTotalMinutes} = detail;
		newTotalMinutes > 0 ? dateLine.checkbox.check(true) : dateLine.checkbox.uncheck(true);
		this.updateTimeTracked();
		this.enableDateLine(dateLine);

		oldTotalMinutes !== newTotalMinutes && dateLine.dispatchEvent(new Event('save', {bubbles: true, composed: true, cancelable: false}));
	}

	handleMainCheckbox() {
		let remainingRest = Math.max(this.limits - this.getMinutesTracked(), 0);
		this.scheduleLines.forEach(dateLine => {
			if (this.ref('main-checkbox').checked) {
				if (this.limits && remainingRest > 0) {
					if (!dateLine.timepicker.totalMinutes || !dateLine.timepicker.checked) {
						dateLine.timepicker.setTotalMinutes(this.getPresetMinutes(dateLine));
						dateLine.checkbox.check()
					}
					remainingRest -= dateLine.timepicker.totalMinutes;
				} else if (!this.limits) {
					if (!dateLine.timepicker.totalMinutes || !dateLine.timepicker.checked) {
						dateLine.timepicker.setTotalMinutes(this.getPresetMinutes(dateLine));
						dateLine.checkbox.check()
					}
				}
			} else {
				dateLine.checkbox.uncheck();
			}
		});
		this.updateTimeTracked();

	}



	updateTimeTracked() {
		const minutes = this.getMinutesTracked();
		const {hours, minutes: mins} = App.helper.parseTime(minutes);
		this.ref('time-tracked').innerText = `+${hours}:${mins.toString().padEnd(2, '0')}`;
	}


	getMinutesTracked() {
		return [...this.scheduleLines].reduce((total, dateLine) => {
			return dateLine.checkbox.checked ? total + dateLine.timepicker.totalMinutes : total;
		}, 0);
	}

	loadMore() {
		const lastDate = this.lastDate.toString().length === 13 ? Math.floor(this.lastDate / 1000) : this.lastDate;
		$.ajax({
			type: 'GET',
			url: App.helper.endpoint.task(this.taskId) + '?' + App.actions.get('task_schedule:show') + '&from_date=' + lastDate + '&only_body=1',
			success: (response) => {
				$(response).each((_, line) => {
					const lineElem = $(line)[0];
					this.ref('days-list').appendChild(lineElem);
					this.setupCallbacks([lineElem]);
				})
			}
		});

		const newDate = App.helper.date.from_timestamp(this.lastDate);
		newDate.setDate(newDate.getDate() + 14);
		this.lastDate = newDate.getTime();
		this.ref('week-counter').innerText = `${App.helper.date.get_week_number(newDate)} - ${App.helper.date.get_week_number(newDate) + 1}`;

		this.loadedDays += 14;
		if (this.loadedDays >= 90) {
			this.ref('more-btn').style.display = 'none';
		}
	}

	saveDateLine(dateLine) {
		this.saveQueue[dateLine.date] = {
			id_user_to: this.userIdTo,
			is_on: App.helper.parseBoolean(dateLine.checkbox.checked) ? 1 : 0,
			time: App.helper.minutesToFraction(dateLine.timepicker.totalMinutes),
			dt: dateLine.date
		};
		this.flushQueueFn();
	}

	flushSaveQueue() {
		const values = Object.values(this.saveQueue);
		this.saveQueue = {};
		if (!values.length) {
			return;
		}
		if (values.length === 1) {
			return this.saveSingleDateLine(values[0]);
		} else {
			return this.saveBulkDateLines(values);
		}
	}

	saveBulkDateLines(values) {
		$.ajax({
			type: 'POST',
			url: App.helper.endpoint.task(this.taskId) + '?' + App.actions.get('task_schedule:do_bulk'),
			data: {records: values},
			success: (response) => {
				eval(response)
			}
		});
	}

	saveSingleDateLine(data) {
		$.ajax({
			type: 'POST',
			url: App.helper.endpoint.task(this.taskId) + '?' + App.actions.get('task_schedule:do'),
			data,
			success: (response) => {
				eval(response)
			}
		});
	}

	selectUser(e, anchor) {
		const _this = this;
		App.controls.user_select_and_go.show_context(anchor || e.currentTarget, App.helper.url.add_query(
			App.helper.endpoint.project(this.idProject), App.actions.get('user_select_and_go:show')), {
			backup: true,
			request: {
				type: 'task_schedule',
				template: 'menu',
				assign_user_id: this.userIdFrom,
				select_user_id: this.userIdTo,
			},
			onload() {
				$(this).on('click', '.menu__item', function () {
					App.modal.show({
						url: App.helper.endpoint.task(_this.taskId) + '?' + App.actions.get('task_schedule:show') + '&id_user_to' + '=' + $(this).data('id'),
						reload: true
					})

				})
			}
		});
	}

	disableDateLine(dateline) {
		dateline.progressbar.disable();
		dateline.timepicker.disable();

	}

	enableDateLine(dateline) {
		dateline.progressbar.enable();
		dateline.timepicker.enable();
	}

	applyPreset() {
		this.scheduleLines.forEach(dateLine => {
			const value = this.getPresetMinutes(dateLine);
			if (dateLine.timepicker.totalMinutes) {
				const previousChecked = dateLine.checkbox.checked;
				dateLine.timepicker.setTotalMinutes(value);
				if (!previousChecked) {
					dateLine.checkbox.uncheck();
				}
			}
		});
		this.updateTimeTracked();
	}

}

App.classes.schedule_view = ScheduleView;
;App.classes = App.classes || {};

class TaskContextMenu extends EventSubscriberMixin() {

	/**
	 * @type {App.classes.TaskContextMenuComponentsContainer}
	 */
	context_menu = null;
	input_menu_command = null;

	constructor({
					components = [],
					input = null,
				}) {
		super();
		this.components = components;
		this.input = input;
		this.init();
	}

	init() {
		this.initContextMenu();
		this.bindSelfEvents();
		this.initInputMenuCommand();
	}

	initContextMenu() {
		this.context_menu = new App.classes.task_context_menu_components_container();
		this.components.forEach((component) => {
			const componentInstance = this.initComponent(component);
			this.context_menu.addComponent(componentInstance);
		});
	}

	initInputMenuCommand() {
		this.input_menu_command = new App.classes.input_menu_command({
			input: this.input,
			menu: this.context_menu?.element(),
			onApply(text, menu) {
				App.context_search.menu.apply(text, menu);
			}
		})
		this.on('task_context_menu.component.selected', () => {
			this.close();
		});
		this.on('task_context_menu.component.cleared', () => {
			this.close();
		});
	}

	close() {
		this.input_menu_command.hide();
	}

	initComponent(config) {
		const component = App.classes.base_task_context_menu_component.createByType(config.type);
		if (config.hasOwnProperty('canBeRemoved')) {
			component.canBeRemoved = config.canBeRemoved;
		}
		component.selected = config.selected || false;
		this.bindComponentEvents(component);
		return component;
	}

	bindComponentEvents(component) {
		component.element().on('click', (e) => {
			if (component.selected && component.canBeRemoved) {
				this.emit('task_context_menu.component.clear', component);
			} else {
				this.emit('task_context_menu.component.show', component);
			}
		});
		component.removeBtn().on('click', (e) => {
			e.stopPropagation();
			this.emit('task_context_menu.component.clear', component);
		});
	}

	bindSelfEvents() {
		this.on('task_context_menu.component.selected', (type) => {
			this.context_menu.getComponent(type).selected = true;
		});
		this.on('task_context_menu.component.cleared', (type) => {
			this.context_menu.getComponent(type).selected = false;
		});
	}

}

App.classes.task_context_menu = TaskContextMenu
;App.classes = App.classes || {};

App.classes.base_task_context_menu_component = class BaseTaskContextMenuComponent {

	constructor({container, icon, text, type, canBeRemoved}) {
		this.container = container;
		this.icon = icon;
		this.text = text;
		this.type = type;
		this.canBeRemoved = canBeRemoved;
	}

	_element;
	_selected = false;

	render() {
		return App.helper.template.fill('task_context_menu_item', {
			icon: this.icon,
			text: this.text,
			container: this.container,
			canBeRemoved: this.canBeRemoved,
		});
	}

	element() {
		return this._element || (this._element = $(this.render()));
	}

	removeBtn() {
		return this.element().find('ws-btn[data-role=remove]');
	}

	get selected() {
		return this._selected;
	}

	set selected(value) {
		this._selected = value;
		this.element().toggleClass('menu__item-active', value);
	}

	static createByType(type) {
		if (!configs()[type]) {
			throw new Error(`Unknown component type: ${type}`);
		}
		const config = configs()[type];
		return new BaseTaskContextMenuComponent({
			container: config.container,
			icon: config.icon,
			text: config.text,
			canBeRemoved: config.canBeRemoved !== false,
			type: type
		});
	}
}

const configs = () => ({
	project: {
		icon: 'project_sm',
		text: App.object_state.locale.task_menu.project,
		container: 'project'
	},
	parent: {
		icon: 'parent',
		text: App.object_state.locale.task_menu.parent,
		container: 'parent',
		canBeRemoved: false
	},
	terms: {
		icon: 'calendar_date',
		text: App.object_state.locale.task_menu.terms,
		container: 'terms'
	},
	priority: {
		icon: 'priority-circle',
		text: App.object_state.locale.task_menu.priority,
		container: 'priority'
	},
	assignee: {
		icon: 'person',
		text: App.object_state.locale.task_menu.assignee,
		container: 'assignee'
	},
	stage: {
		icon: 'task_workflow/started-50',
		text: App.object_state.locale.task_menu.stage,
		container: 'stage',
		canBeRemoved: false
	},
	labels: {
		icon: 'tag',
		text: App.object_state.locale.task_menu.labels,
		container: 'labels'
	},
	limits: {
		icon: 'clockface',
		text: App.object_state.locale.task_menu.limits,
		container: 'limits'
	},
	subscribers: {
		icon: 'notifications',
		text: App.object_state.locale.task_menu.subscribers,
		container: 'subscribers',
		canBeRemoved: false
	},
	visibility: {
		icon: 'lock_open_alt',
		text: App.object_state.locale.task_menu.visibility,
		container: 'hidden'
	},
});




;App.classes = App.classes || {};

App.classes.task_context_menu_components_container = class TaskContextMenuComponentsContainer {

	/**
	 * @type {{[componentType: string]: BaseTaskContextMenuComponent}}
	 */
	components = {};

	_element = null;

	/**
	 * @param {BaseTaskContextMenuComponent} component
	 */
	addComponent(component) {
		this.components[component.type] = component;
	}

	getComponent(type) {
		return this.components[type];
	}


	render() {
		const container = $(App.helper.template.fill('task_context_menu'));
		const componentsContainer = container.find('[data-container=components]');
		for (const key in this.components) {
			const component = this.components[key];
			componentsContainer.append(component.element());
		}
		return container;
	}

	element() {
		return this._element || (this._element = this.render());
	}


}
;class UIStateManager extends EventSubscriberMixin(SingletonMixin()) {

	static CATEGORIES = Object.freeze({
		DEFAULT: 'default',
		MOBILE: 'mobile'
	});

	constructor({state = {}} = {}) {
		super();
		this._state = state;
	}

	_state = {};

	_requestedStateCategories = {};
	_requestStateDebounce = App.helper.debounce(() => {
		this._requestState()
	});

	_stateToSave = {};
	_saveStateDebounce = App.helper.debounce(() => {
		this._saveState()
	}, 100)

	getState(key, category = 'default') {
		return this._state[category]?.[key];
	}

	setState(key, value, category = 'default') {
		this.setStateSilent(key, value, category, false);
		this.saveState(key, value, category);
	}

	setStateSilent(key, value, category = 'default', withSync = true) {
		if (!this._state[category]) {
			this._state[category] = {};
		}
		this._state[category][key] = value;
		withSync && this.syncStateToOtherTabs();
	}

	mergeStateSilent(state) {
		for (const category in state) {
			for (const key in state[category]) {
				if (!this._state[category]) {
					this._state[category] = {};
				}
				this._state[category][key] = state[category][key];
			}
		}
	}

	loadState({
				  category = 'default',
				  onLoaded = () => {
				  }
			  } = {}) {
		if (this._state[category]) {
			onLoaded(this._state[category]);
			return;
		}
		let onLoadedCalls = [onLoaded];
		if (this._requestedStateCategories[category]) {
			onLoadedCalls = this._requestedStateCategories[category].onLoadedCalls.concat(onLoaded);
		}
		this._requestedStateCategories[category] = {onLoadedCalls};
		this._requestStateDebounce();
	}

	_requestState() {
		const categories = Object.keys(this._requestedStateCategories);
		if (categories.length === 0) return;
		$.ajax({
			url: App.helper.endpoint.ajax() + '?'
				+ App.actions.get('ui_state:load'),
			type: 'POST',
			data: {
				categories: categories,
			},
			success: (data) => {
				for (const category of categories) {
					this._state[category] = {...(this._state[category] || {}), ...(data[category] || {})};
					const {onLoadedCalls} = this._requestedStateCategories[category];
					onLoadedCalls.forEach(cb => cb(this._state[category]));
					delete this._requestedStateCategories[category];
				}
			}
		});
	}

	saveState(key, value, category = 'default') {
		if (!this._stateToSave[category]) {
			this._stateToSave[category] = {};
		}
		this._stateToSave[category][key] = value;
		this._saveStateDebounce();
	}

	_saveState() {
		const data = this._stateToSave;
		this._stateToSave = {};
		$.ajax({
			url: App.helper.endpoint.ajax() + '?'
				+ App.actions.get('ui_state:save'),
			type: 'POST',
			data: {
				state: data,
			},
			success: () => {
				this.syncStateToOtherTabs();
			}
		});
	}

	syncStateToOtherTabs() {
		App.core.send_message_to_other_tabs({ command: 'ui_state', data: { state: this._state } });
	}

}

App.classes.ui_state_manager = UIStateManager;
;class QueryCodec {
	/**
	 * @param {string} str
	 * @returns {Object}
	 * */
	static parse(str) {
		if (!str) return {};
		if (str[0] === '?') str = str.slice(1);
		const out = {};
		for (const pair of str.split('&')) {
			if (!pair) continue;
			const eq = pair.indexOf('=');
			const key = App.helper.url.safe_decode(eq === -1 ? pair : pair.slice(0, eq));
			const value = App.helper.url.safe_decode(eq === -1 ? '' : pair.slice(eq + 1));
			QueryCodec.#assign(out, QueryCodec.#bracketed(key), value);
		}
		return out;
	}

	/**
	 * @param {Object} data
	 * @returns {string}
	 * */
	static stringify(data) {
		return App.helper.object.toFormUrlEncoded(data);
	}

	/**
	 * @param {string} key
	 * @returns {string[]}
	 * */
	static #bracketed(key) {
		const out = [];
		let i = 0, buf = '';
		while (i < key.length && key[i] !== '[') buf += key[i++];
		if (buf) out.push(buf);
		while (i < key.length) {
			if (key[i++] !== '[') continue;
			buf = '';
			while (i < key.length && key[i] !== ']') buf += key[i++];
			i++;
			out.push(buf);
		}
		return out;
	}

	/**
	 * @param {Object|Array} target
	 * @param {string[]} keys
	 * @param {*} value
	 * */
	static #assign(target, keys, value) {
		let cur = target;
		for (let i = 0; i < keys.length - 1; i++) {
			const k = keys[i];
			const nextIsPush = keys[i + 1] === '';
			if (cur[k] == null) cur[k] = nextIsPush ? [] : {};
			cur = cur[k];
		}
		const last = keys.at(-1);
		if (last === '' && Array.isArray(cur)) cur.push(value);
		else cur[last] = value;
	}
}


class Query {
	/** @type {Record<string,*>} */
	data = {};

	/** @param {Query|Object|string} [input] */
	constructor(input) {
		this.data = Query.#dataOf(input);
	}

	/**
	 * @param {string|string[]} path
	 * @returns {*}
	 * */
	get(path) {
		return Array.isArray(path)
			? Query.#walk(this.data, path)
			: App.helper.object.readByPath(this.data, path);
	}

	/**
	 * @param {string|string[]} path
	 * @param {*} value
	 * @returns {this}
	 * */
	set(path, value) {
		if (Array.isArray(path)) Query.#deepSet(this.data, path, value);
		else App.helper.object.setByPath(this.data, path, value);
		return this;
	}

	/**
	 * @param {string|string[]} path
	 * @returns {boolean}
	 * */
	has(path) {
		let cur = this.data;
		for (const k of Query.#pathArr(path)) {
			if ((!App.helper.object.is_plain(cur) && !Array.isArray(cur)) || !(k in cur)) return false;
			cur = cur[k];
		}
		return true;
	}

	/**
	 * @param {string|string[]} path
	 * @returns {this}
	 * */
	delete(path) {
		const keys = Query.#pathArr(path);
		if (!keys.length) return this;
		let cur = this.data;
		for (let i = 0; i < keys.length - 1; i++) {
			cur = cur?.[keys[i]];
			if (cur == null) return this;
		}
		delete cur[keys.at(-1)];
		return this;
	}

	/**
	 * @param {Query|Object|string} other
	 * @returns {this}
	 * */
	merge(other) {
		App.helper.object.deep_append(this.data, Query.#dataOf(other));
		return this;
	}

	clone() {
		return new Query(this);
	}

	toJSON() {
		return this.data;
	}

	toString() {
		return QueryCodec.stringify(this.data);
	}

	/**
	 * @param {Query|Object|string} input
	 * @returns {Object}
	 * */
	static #dataOf(input) {
		if (input instanceof Query) return structuredClone(input.data);
		if (typeof input === 'string') return QueryCodec.parse(input);
		if (App.helper.object.is_plain(input)) return structuredClone(input);
		return {};
	}

	/**
	 * @param {string|string[]} path
	 * @returns {string[]}
	 * */
	static #pathArr(path) {
		if (Array.isArray(path)) return path.map(String);
		if (typeof path !== 'string') return [String(path)];
		return path.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '').split('.');
	}

	static #walk(data, keys) {
		let cur = data;
		for (const k of keys) {
			if (cur == null) return undefined;
			cur = cur[k];
		}
		return cur;
	}

	static #deepSet(target, keys, value) {
		let cur = target;
		for (let i = 0; i < keys.length - 1; i++) {
			const k = keys[i];
			if (cur[k] == null) cur[k] = Number.isInteger(+keys[i + 1]) ? [] : {};
			cur = cur[k];
		}
		cur[keys.at(-1)] = value;
	}
}


class URLParser {
	/**
	 * @param {string} input
	 * @returns {{parts: URLParts, errors: string[]}}
	 * */
	static parse(input) {
		const errors = [];
		/** @type {URLParts} */
		const parts = {};
		if (typeof input !== 'string') {
			errors.push('not a string');
			return {parts, errors};
		}

		let s = input.trim();
		if (!s) return {parts, errors};

		s = URLParser.#takeFragment(s, parts);
		s = URLParser.#takeQuery(s, parts);
		s = URLParser.#takeScheme(s, parts);
		s = URLParser.#promoteAuthority(s);
		s = URLParser.#takeAuthority(s, parts, errors);
		URLParser.#takePath(s, parts);

		return {parts, errors};
	}

	static detect(input) {
		if (!input) return {parts: {}, errors: []};
		const t = input.trim();
		if (t[0] === '?') return {parts: {query: new Query(t.slice(1))}, errors: []};
		if (t[0] === '#') return {parts: {fragment: t.slice(1)}, errors: []};
		if (!t.includes('/') && !t.includes('://') && t.includes('=')) {
			return {parts: {query: new Query(t)}, errors: []};
		}
		return URLParser.parse(t);
	}

	static #takeFragment(s, parts) {
		const i = s.indexOf('#');
		if (i === -1) return s;
		parts.fragment = s.slice(i + 1);
		return s.slice(0, i);
	}

	static #takeQuery(s, parts) {
		const i = s.indexOf('?');
		if (i === -1) return s;
		parts.query = new Query(s.slice(i + 1));
		return s.slice(0, i);
	}

	static #takeScheme(s, parts) {
		const m = /^([a-zA-Z][a-zA-Z0-9+\-.]*):/.exec(s);
		if (!m) return s;
		if (/^\d+(\/|$)/.test(s.slice(m[0].length))) return s;
		parts.scheme = m[1];
		return s.slice(m[0].length);
	}

	static #promoteAuthority(s) {
		if (!s) return s;
		if (s.startsWith('//') || s.startsWith('/') || s.startsWith('./') || s.startsWith('../')) return s;
		const slash = s.indexOf('/');
		const first = slash === -1 ? s : s.slice(0, slash);
		return URLParser.#looksLikeHost(first) ? '//' + s : s;
	}

	static #looksLikeHost(seg) {
		if (!seg) return false;
		if (seg.startsWith('[') && seg.includes(']')) return true;
		const colon = seg.lastIndexOf(':');
		if (colon !== -1 && /^\d+$/.test(seg.slice(colon + 1))) return true;
		return seg.includes('.') && !seg.startsWith('.') && !seg.endsWith('.');
	}

	static #takeAuthority(s, parts, errors) {
		if (!s.startsWith('//')) return s;
		parts.hasAuthority = true;
		s = s.slice(2);
		const slash = s.indexOf('/');
		const auth = slash === -1 ? s : s.slice(0, slash);
		const rest = slash === -1 ? '' : s.slice(slash);
		URLParser.#splitAuthority(auth, parts, errors);
		return rest;
	}

	static #takePath(s, parts) {
		if (!s) return;
		if (s[0] === '/') s = s.slice(1);
		if (s) parts.path = s.split('/');
	}

	static #splitAuthority(auth, parts, errors) {
		const at = auth.lastIndexOf('@');
		let hostPort = auth;
		if (at !== -1) {
			parts.userinfo = auth.slice(0, at);
			hostPort = auth.slice(at + 1);
		}
		if (!hostPort) return;

		if (hostPort.startsWith('[')) {
			const close = hostPort.indexOf(']');
			if (close === -1) {
				errors.push('unclosed ipv6 bracket');
				parts.host = hostPort;
				return;
			}
			parts.host = hostPort.slice(0, close + 1);
			const tail = hostPort.slice(close + 1);
			if (tail.startsWith(':')) URLParser.#takePort(tail.slice(1), parts, errors);
			return;
		}

		const colon = hostPort.lastIndexOf(':');
		if (colon !== -1 && /^\d+$/.test(hostPort.slice(colon + 1))) {
			parts.host = hostPort.slice(0, colon);
			parts.port = Number(hostPort.slice(colon + 1));
		} else {
			parts.host = hostPort;
		}
	}

	static #takePort(str, parts, errors) {
		const n = Number(str);
		if (Number.isFinite(n)) parts.port = n;
		else errors.push('invalid port');
	}
}

class URLBuilder {
	scheme = '';
	userinfo = '';
	host = '';
	/** @type {number|null} */
	port = null;
	/** @type {string[]} */
	path = [];
	/** @type {Query} */
	query = new Query();
	fragment = '';
	hasAuthority = false;
	/** @type {string[]} */
	errors = [];


	/** @typedef {'override'|'merge'|'patch'} ApplyStrategy */
	static APPLY_STRATEGIES = Object.freeze({
		patch(target, parts) {
			if (parts.path !== undefined) target.path = [...parts.path];
			if (parts.query !== undefined) target.query.merge(parts.query);
		},
		override(target, parts) {
			if (parts.path !== undefined) target.path = [...parts.path];
			if (parts.query !== undefined) target.query = new Query(parts.query);
		},
		merge(target, parts) {
			if (parts.path !== undefined) target.path = [...target.path, ...parts.path];
			if (parts.query !== undefined) target.query.merge(parts.query);
		},
	});

	static SCALAR_FIELDS = ['scheme', 'userinfo', 'host', 'port', 'fragment', 'hasAuthority'];


	/** @param {string|URLParts|URLBuilder} [input] */
	constructor(input) {
		const {parts, errors} = URLBuilder.#partsOf(input);
		this.errors = errors;
		this.#assign(parts);
	}

	static parse(input) {
		return new URLBuilder(input);
	}

	get valid() {
		return this.errors.length === 0;
	}

	/**
	 * @param {string|URLParts|URLBuilder} input
	 * @param {{strategy?: ApplyStrategy}} [opts]
	 * @returns {this}
	 */
	apply(input, {strategy = 'patch'} = {}) {
		const apply = URLBuilder.APPLY_STRATEGIES[strategy];
		if (!apply) throw new Error(`Unknown strategy: ${strategy}`);

		const {parts} = URLBuilder.#partsOf(input, {detect: true});
		this.#assignScalars(parts);
		apply(this, parts);
		return this;
	}


	/**
	 * @param {String} action
	 * @return {URLBuilder}
	 */
	useAction(action) {
		return this.apply(App.actions.get(action));
	}

	clone() {
		return new URLBuilder(this);
	}

	/** @returns {URLParts} */
	toParts() {
		return {
			scheme: this.scheme,
			userinfo: this.userinfo,
			host: this.host,
			port: this.port,
			path: [...this.path],
			query: structuredClone(this.query.data),
			fragment: this.fragment,
			hasAuthority: this.hasAuthority,
		};
	}

	toString() {
		const out = [];
		if (this.scheme) out.push(this.scheme, ':');
		if (this.#hasAuth()) {
			if (this.scheme) out.push('//');
			if (this.userinfo) out.push(this.userinfo, '@');
			out.push(this.host);
			if (this.port != null) out.push(':', String(this.port));
		}
		if (this.path.length) out.push('/', this.path.join('/'));
		const q = this.query.toString();
		if (q) out.push('?', q);
		if (this.fragment) out.push('#', this.fragment);
		return out.join('');
	}

	#hasAuth() {
		return this.hasAuthority || !!this.host || !!this.userinfo || this.port != null;
	}

	/** @param {URLParts} parts */
	#assign(parts) {
		this.#assignScalars(parts);
		if (parts.path !== undefined) this.path = [...parts.path];
		if (parts.query !== undefined) this.query = new Query(parts.query);
	}

	/** @param {URLParts} parts */
	#assignScalars(parts) {
		for (const k of URLBuilder.SCALAR_FIELDS) if (parts[k] !== undefined) this[k] = parts[k];
	}

	/** @returns {{parts: URLParts, errors: string[]}} */
	static #partsOf(input, {detect = false} = {}) {
		if (input instanceof URLBuilder) return {parts: input.toParts(), errors: []};
		if (typeof input === 'string') return detect ? URLParser.detect(input) : URLParser.parse(input);
		if (App.helper.object.is_plain(input)) return {parts: input, errors: []};
		return {parts: {}, errors: []};
	}
}

/**
 * @typedef {{
 *   scheme?: string, userinfo?: string, host?: string, port?: number|null,
 *   path?: string[], query?: Query|Object|string,
 *   fragment?: string, hasAuthority?: boolean,
 * }} URLParts
 *
 * @typedef {'override'|'merge'} ApplyStrategy
 */


;class AttributeObserver {
	/**
	 * @param {HTMLElement} hostElement
	 * @param {Object} [options]
	 * @param {AttributeModifierRunner} [options.runModifier]
	 */
	constructor(hostElement, {runModifier} = {}) {
		/** @type {HTMLElement} */
		this._hostElement = hostElement;

		/** @type {AttributeModifierRunner | undefined} */
		this._runModifier = runModifier;

		/** @type {Object<string, ObserveAttributeOptions>} */
		this._runtimeObservedAttributes = Object.create(null);
	}

	/**
	 * @param {string} attributeName
	 * @param {ObserveAttributeOptions} [options]
	 * @returns {void}
	 */
	observeAttribute(attributeName, options = {}) {
		this._runtimeObservedAttributes[attributeName] = options;
	}

	/**
	 * @param {string} attributeName
	 * @returns {boolean}
	 */
	isObserved(attributeName) {
		return Boolean(this._runtimeObservedAttributes[attributeName]);
	}

	/**
	 * @param {string} attributeName
	 * @param {*} value
	 * @returns {*}
	 */
	applyModifiers(attributeName, value) {
		const options = this._runtimeObservedAttributes[attributeName];
		if (!options) return value;

		const modifiers = options.modifiers || [];
		let result = value;

		for (const modifierName of modifiers) {
			result = this._runModifier(modifierName, result);
		}

		return result;
	}

	/** @returns {void} */
	parseObserveAttributesAttribute() {
		const raw = this._hostElement.getAttribute('observe-attributes');
		if (!raw) return;

		raw.split(',')
			.map(part => part.trim())
			.filter(Boolean)
			.forEach(part => {
				const [namePart, modifiersPart] = part.split('|').map(s => s.trim());
				if (!namePart) return;

				const modifiers = modifiersPart
					? modifiersPart.split(',').map(s => s.trim()).filter(Boolean)
					: [];

				this.observeAttribute(namePart, {modifiers});
			});
	}
}

/**
 * @typedef {Object} ObserveAttributeOptions
 * @property {string[]} [modifiers]
 */

/**
 * @callback AttributeModifierRunner
 * @param {string} modifierName
 * @param {*}      value
 * @returns {*}
 */
;class BindingFrame {
	/**
	 * @param {Node}                target
	 * @param {RenderableComponent} component
	 */
	constructor(target, component) {
		/** @type {Node} */
		this.$target = target;
		/** @type {RenderableComponent} */
		this.$component = component;
	}
}

class EventFrame {
	/**
	 * @param {Node}                target
	 * @param {RenderableComponent} component
	 */
	constructor(target, component) {
		/** @type {Node} */
		this.$target = target;
		/** @type {RenderableComponent} */
		this.$component = component;
		/** @type {Event | null} */
		this.$event = null;
	}

	/**
	 * @param {Event} event
	 * @returns {this}
	 */
	fire(event) {
		this.$event = event;
		return this;
	}

	/** @returns {void} */
	release() {
		this.$event = null;
	}
}

App.classes.binding_frame = BindingFrame;
App.classes.event_frame = EventFrame;

class BindingManager {
	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 * @param {Object}                            [options]
	 * @param {DirectiveRegistry}                 [options.directiveRegistry]
	 */
	constructor(hostElement, {
		directiveRegistry,
	} = {}) {
		/** @type {RenderableComponent & HTMLElement} */
		this._hostElement = hostElement;

		/** @type {DirectiveRegistry} */
		this._directiveRegistry = directiveRegistry;

		/** @type {BindingTracker} */
		this._tracker = new BindingTracker();

		/** @type {Binding[]} */
		this._bindings = [];

		/** @type {Map<Node, Binding[]>} */
		this._bindingsByElement = new Map();

		/** @type {Set<Element> | null} */
		this._hydratedNodes = null;
	}

	/**
	 * @param {Element} node
	 * @returns {void}
	 */
	markHydrated(node) {
		(this._hydratedNodes ??= new Set()).add(node);
	}

	/** @returns {void} */
	rebuildAll() {
		this.disposeAll();
		this._collectBindings(this._hostElement, true, null);
		this._hydratedNodes = null;
	}

	/**
	 * @param {Object<string, *>}  context
	 * @param {Set<string> | null} [dirtyKeys=null]  — `null` = force-update all bindings
	 * @param {boolean}            [skipTracking=false]
	 * @returns {void}
	 */
	updateAll(context, dirtyKeys = null, skipTracking = false) {
		const tracker = this._tracker;
		const forceAll = dirtyKeys === null;

		for (let bi = 0; bi < this._bindings.length; bi++) {
			const binding = this._bindings[bi];
			if (!binding || typeof binding.update !== 'function' || binding._disposed) continue;
			if (!forceAll && binding.deps !== null && !this._isBindingDirty(binding, dirtyKeys)) continue;

			const scope = binding.ephemeral?.context ?? context;
			const frame = binding.frame;

			if (skipTracking) {
				try {
					binding.update(scope, frame);
				} catch (error) {
					console.error('Error updating binding', binding, error);
				}
				continue;
			}

			const trackedScope = tracker.wrapScope(scope);
			tracker.beginTracking(binding.deps);
			let threw = false;
			try {
				binding.update(trackedScope, frame);
			} catch (error) {
				console.error('Error updating binding', binding, error);
				threw = true;
			} finally {
				binding.deps = threw ? null : tracker.endTracking();
			}
		}

		this._clearEphemeralDirty();
	}

	/** @returns {void} */
	sleepAll() {
		for (let i = 0; i < this._bindings.length; i++) {
			this._bindings[i].sleep?.();
		}
	}

	/** @returns {void} */
	wakeupAll() {
		for (let i = 0; i < this._bindings.length; i++) {
			this._bindings[i].wakeup?.();
		}
	}

	/**
	 * @param {PlanEntry[]}         plan
	 * @param {Element}             root
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {void}
	 */
	executePlan(plan, root, ephemeral = null) {
		const len = plan.length;
		/** @type {(Element | null)[]} */
		const nodes = new Array(len);
		for (let pi = 0; pi < len; pi++) {
			nodes[pi] = this._resolveByPath(root, plan[pi].path);
		}

		for (let pi = 0; pi < len; pi++) {
			const node = nodes[pi];
			if (!node) continue;

			this._directiveRegistry.processElementFromPlan({
				entry: plan[pi],
				node,
				component: this._hostElement,
				addBinding: (binding, to = node) => this._addBinding(to, binding, ephemeral),
				ephemeral,
				bindingManager: this,
				makeFrame: (target) => new BindingFrame(target, this._hostElement),
				makeEventFrame: (target) => new EventFrame(target, this._hostElement),
			});
		}
	}

	/**
	 * @param {Element}  root
	 * @param {number[]} path
	 * @returns {Element | null}
	 */
	_resolveByPath(root, path) {
		let node = root;
		for (let i = 0; i < path.length; i++) {
			node = node.children?.[path[i]] ?? null;
			if (!node) return null;
		}
		return node;
	}

	/**
	 * @returns {WeakMap<EphemeralScope, Set<string>> | null}
	 */
	_flushEphemeralDirty() {
		/** @type {WeakMap<EphemeralScope, Set<string>> | null} */
		let map = null;
		for (let i = 0; i < this._bindings.length; i++) {
			const binding = this._bindings[i];
			if (!binding?.ephemeral) continue;
			if (!map) map = new WeakMap();
			if (map.has(binding.ephemeral)) continue;
			map.set(binding.ephemeral, binding.ephemeral.flushDirty());
		}
		return map;
	}

	/**
	 * @param {Binding}     binding
	 * @param {Set<string>} parentDirty
	 * @returns {boolean}
	 */
	_isBindingDirty(binding, parentDirty) {
		const ephemeral = binding.ephemeral;
		for (const dep of binding.deps) {
			if (parentDirty.has(dep) || ephemeral?.hasDirty(dep)) return true;
		}
		return false;
	}

	/** @returns {void} */
	_clearEphemeralDirty() {
		/** @type {Set<EphemeralScope> | null} */
		let seen = null;
		for (let i = 0; i < this._bindings.length; i++) {
			const ephemeral = this._bindings[i]?.ephemeral;
			if (!ephemeral) continue;
			(seen ??= new Set()).add(ephemeral);
		}
		seen?.forEach(e => e.clearDirty());
	}


	/** @returns {void} */
	disposeAll() {
		for (const binding of this._bindings) {
			this._disposeBinding(binding);
		}
		this._bindings = [];
		this._bindingsByElement.clear();
	}

	/**
	 * @param {Node} startAnchor
	 * @param {Node} endAnchor
	 * @returns {void}
	 */
	disposeInRange(startAnchor, endAnchor) {
		/** @type {Set<Binding>} */
		const bindingsToRemove = new Set();
		for (const node of this._iterateRange(startAnchor, endAnchor)) {
			this._disposeNodeBindings(node, bindingsToRemove);
		}
		this._removeDisposedFromList(bindingsToRemove);
	}

	/**
	 * @param {Node}                startAnchor
	 * @param {Node}                endAnchor
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {void}
	 */
	collectInRange(startAnchor, endAnchor, ephemeral = null) {
		for (const element of this._iterateTopLevelElementsBetween(startAnchor, endAnchor)) {
			this._collectBindings(element, false, ephemeral);
		}
	}

	/**
	 * @param {Node}                rootNode
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {Number}
	 */
	collectInSubtree(rootNode, ephemeral = null) {
		return this._collectBindings(rootNode, false, ephemeral);
	}

	/**
	 * @param {Node} rootNode
	 * @returns {void}
	 */
	disposeInSubtree(rootNode) {
		/** @type {Set<Binding>} */
		const bindingsToRemove = new Set();
		for (const node of this._iterateSubtree(rootNode)) {
			this._disposeNodeBindings(node, bindingsToRemove);
		}
		this._removeDisposedFromList(bindingsToRemove);
	}

	/**
	 * @param {Node}                rootNode
	 * @param {boolean}             isHostRoot
	 * @param {EphemeralScope|null} ephemeral
	 * @returns {Number}
	 */
	_collectBindings(rootNode, isHostRoot, ephemeral) {
		let addedCount = 0;
		/** @type {AddBindingFn} */
		const addBinding = (binding, to = rootNode) => {
			addedCount++;
			this._addBinding(to, binding, ephemeral);
		}
		const host = this._hostElement;
		const makeFrame = (target) => new BindingFrame(target, host);
		const makeEventFrame = (target) => new EventFrame(target, host);

		/**
		 * @param {Node}    node
		 * @param {boolean} isRootElement
		 */
		const walk = (node, isRootElement) => {
			if (!(node instanceof Element)) return;
			if (this._hydratedNodes?.has(node)) return;

			const {shouldSkipChildren} = this._directiveRegistry.processElement({
				element: node,
				component: this._hostElement,
				addBinding,
				customPrefix: (isRootElement && isHostRoot) ? 'ws-host-' : 'ws-',
				ephemeral,
				bindingManager: this,
				makeFrame,
				makeEventFrame,
			});

			if (!isRootElement && this._isBoundaryComponent(node)) return;
			if (shouldSkipChildren) return;
			if (isRootElement && !isHostRoot && this._isBoundaryComponent(node)) return;
			const snapshot = Array.from(node.children);
			for (let i = 0; i < snapshot.length; i++) walk(snapshot[i], false);
		};

		walk(rootNode, true);
		return addedCount;
	}

	/**
	 * @param {Node}                node
	 * @param {Binding}             binding
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {void}
	 */
	_addBinding(node, binding, ephemeral = null) {
		if (ephemeral) binding.ephemeral = ephemeral;
		binding.element = node;
		binding.deps = null;
		if (!binding.frame) binding.frame = new BindingFrame(node, this._hostElement);

		this._bindings.push(binding);

		let list = this._bindingsByElement.get(node);
		if (!list) this._bindingsByElement.set(node, (list = []));
		list.push(binding);
	}

	/**
	 * @param {Node}         node
	 * @param {Set<Binding>} bindingsToRemove
	 * @returns {void}
	 */
	_disposeNodeBindings(node, bindingsToRemove) {
		const nodeBindings = this._bindingsByElement.get(node);
		if (!nodeBindings || nodeBindings.length === 0) return;

		for (const binding of nodeBindings) {
			this._disposeBinding(binding);
			bindingsToRemove.add(binding);
		}

		this._bindingsByElement.delete(node);
	}

	/**
	 * @param {Set<Binding>} bindingsToRemove
	 * @returns {void}
	 */
	_removeDisposedFromList(bindingsToRemove) {
		if (!bindingsToRemove || bindingsToRemove.size === 0) return;
		this._bindings = this._bindings.filter(binding => !bindingsToRemove.has(binding));
	}

	/**
	 * @param {Binding} binding
	 * @returns {void}
	 */
	_disposeBinding(binding) {
		if (!binding || typeof binding.dispose !== 'function') return;
		try {
			binding.dispose();
			binding._disposed = true;
		} catch (error) {
			console.error(error);
		}
	}

	/**
	 * @param {Node} startAnchor
	 * @param {Node} endAnchor
	 * @yields {Node}
	 * @returns {Generator<Node, void, unknown>}
	 */
	* _iterateRange(startAnchor, endAnchor) {
		const root = startAnchor?.parentNode;
		if (!root) return;

		if (endAnchor?.parentNode !== root) {
			console.error('BindingManager: start and end anchors have different parents');
			return;
		}

		const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT);
		walker.currentNode = startAnchor;
		let node = walker.nextNode();

		while (node && node !== endAnchor) {
			yield node;
			if (node.nodeType === Node.ELEMENT_NODE && this._isBoundaryComponent(node)) {
				node = walker.nextSibling();
				continue;
			}
			node = walker.nextNode();
		}
	}

	/**
	 * @param {Node} rootNode
	 * @yields {Node}
	 * @returns {Generator<Node, void, unknown>}
	 */
	* _iterateSubtree(rootNode) {
		yield rootNode;
		if (!(rootNode instanceof Element)) return;

		const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT);
		let node = walker.firstChild();

		while (node) {
			yield node;

			if (node.nodeType === Node.ELEMENT_NODE && this._isBoundaryComponent(node)) {
				node = walker.nextSibling();
				continue;
			}

			const child = walker.firstChild();
			if (child) {
				node = child;
				continue;
			}

			let sibling = walker.nextSibling();
			while (!sibling) {
				const parent = walker.parentNode();
				if (!parent || parent === rootNode) {
					node = null;
					break;
				}
				sibling = walker.nextSibling();
			}
			node = sibling;
		}
	}

	/**
	 * @param {Node} startAnchor
	 * @param {Node} endAnchor
	 * @yields {Element}
	 * @returns {Generator<Element, void, unknown>}
	 */
	* _iterateTopLevelElementsBetween(startAnchor, endAnchor) {
		let node = startAnchor?.nextSibling;
		while (node && node !== endAnchor) {
			if (node.nodeType === Node.ELEMENT_NODE) yield /** @type {Element} */ (node);
			node = node.nextSibling;
		}
	}

	/**
	 * @param {Node} node
	 * @returns {boolean}
	 */
	_isBoundaryComponent(node) {
		return node instanceof RenderableComponent;
	}
}

class BindingTracker {
	constructor() {
		/** @type {WeakMap<Object, Object>} */
		this._proxyByScope = new WeakMap();

		/** @type {Set<string> | null} */
		this._current = null;
	}

	/**
	 * @param {Object} scope
	 * @returns {Object}
	 */
	wrapScope(scope) {
		let proxy = this._proxyByScope.get(scope);
		if (proxy) return proxy;

		const tracker = this;
		proxy = new Proxy(scope, {
			has(target, key) {
				return key in target;
			},
			get(target, key, receiver) {
				if (typeof key === 'string') tracker._current?.add(key);
				return Reflect.get(target, key, receiver);
			},
		});

		this._proxyByScope.set(scope, proxy);
		return proxy;
	}

	/**
	 * @param {Set<string> | null} [existing=null]
	 * @returns {void}
	 */
	beginTracking(existing = null) {
		if (existing) {
			existing.clear();
			this._current = existing;
		} else {
			this._current = new Set();
		}
	}

	/** @returns {Set<string>} */
	endTracking() {
		const deps = this._current;
		this._current = null;
		return deps;
	}
}

/**
 * @typedef {Object} Binding
 * @property {(scope: Object<string, *>, frame: BindingFrame) => void} update
 * @property {() => void}       [dispose]
 * @property {() => void}       [sleep]
 * @property {() => void}       [wakeup]
 * @property {EphemeralScope}   [ephemeral]
 * @property {Node}             [element]
 * @property {BindingFrame}     [frame]
 * @property {Set<string>|null} [deps]
 * @property {string}           [label]
 * @property {boolean}          [_disposed]
 */

/**
 * @callback AddBindingFn
 * @param {Binding} binding
 * @param {Node}    [forElement]
 * @returns {void}
 */
;class ComponentScope {
	constructor() {
		/** @type {Map<string, ScopeSourceFn>} */
		this._sources = new Map();

		/** @type {Object<string, *>} */
		this._context = Object.create(null);

		/** @type {Set<string>} */
		this._knownKeys = new Set();

		/** @type {Set<string>} */
		this._staticKeys = new Set();

		/** @type {Set<string>} */
		this._nextKeysBuf = new Set();

		/** @type {Set<string>} */
		this._changedBuf = new Set();
	}

	/**
	 * @param {string}        name
	 * @param {ScopeSourceFn} fn
	 * @returns {void}
	 */
	addSource(name, fn) {
		this._sources.set(name, fn);
	}

	/**
	 * @param {string} name
	 * @returns {void}
	 */
	removeSource(name) {
		this._sources.delete(name);
	}

	/**
	 * @param {string} key
	 * @param {*}      value
	 * @returns {void}
	 */
	setStatic(key, value) {
		this._context[key] = value;
		this._staticKeys.add(key);
	}

	/**
	 * @param {string}        name
	 * @param {ScopeSourceFn} fn
	 * @returns {void}
	 */
	addStaticSource(name, fn) {
		let data;
		try {
			data = fn();
		} catch (e) {
			console.error(`[ComponentScope] static source "${name}" threw`, e);
			return;
		}

		if (!data || typeof data !== 'object') return;

		for (const [key, value] of Object.entries(data)) {
			this._context[key] = value;
			this._staticKeys.add(key);
		}
	}

	/**
	 * @returns {Set<string>} — keys whose values changed since last `update()` (including removed keys)
	 */
	update() {
		const changed  = this._changedBuf;
		const nextKeys = this._nextKeysBuf;
		changed.clear();
		nextKeys.clear();

		this._sources.forEach((fn, name) => {
			let data;
			try {
				data = fn();
			} catch (e) {
				console.error(`[ComponentScope] source "${name}" threw`, e);
				return;
			}

			if (!data || typeof data !== 'object') return;

			const keys = Object.keys(data);
			for (let i = 0; i < keys.length; i++) {
				const key  = keys[i];
				const next = data[key];
				if (!Object.is(this._context[key], next)) changed.add(key);
				this._context[key] = next;
				nextKeys.add(key);
			}
		});

		this._knownKeys.forEach(key => {
			if (!nextKeys.has(key) && !this._staticKeys.has(key)) {
				changed.add(key);
				delete this._context[key];
			}
		});

		this._nextKeysBuf = this._knownKeys;
		this._knownKeys   = nextKeys;

		return changed;
	}

	/** @returns {Object<string, *>} */
	get context() {
		return this._context;
	}

	/** @returns {void} */
	dispose() {
		this._sources.clear();
		this._knownKeys.clear();
		this._staticKeys.clear();
	}
}

class EphemeralScope {
	/**
	 * @param {Object<string, *>} parentContext
	 */
	constructor(parentContext) {
		/** @type {Object<string, *>} */
		this._context = Object.create(parentContext);

		/** @type {Set<string>} */
		this._dirtyKeys = new Set();
	}

	/**
	 * @param {string} key
	 * @param {*}      value
	 * @returns {void}
	 */
	set(key, value) {
		if (!Object.is(this._context[key], value)) this._dirtyKeys.add(key);
		this._context[key] = value;
	}

	/**
	 * @param {string} key
	 * @returns {void}
	 */
	markDirty(key) {
		this._dirtyKeys.add(key);
	}

	/**
	 * @param {string} key
	 * @returns {boolean}
	 */
	hasDirty(key) {
		return this._dirtyKeys.has(key);
	}

	/** @returns {void} */
	clearDirty() {
		this._dirtyKeys.clear();
	}

	/** @returns {boolean} */
	get isDirty() {
		return this._dirtyKeys.size > 0;
	}

	/** @returns {Object<string, *>} */
	get context() {
		return this._context;
	}

	/** @returns {void} */
	dispose() {
		Object.setPrototypeOf(this._context, null);
		this._dirtyKeys.clear();
	}
}

App.classes.ephemeral_scope = EphemeralScope;
App.classes.component_scope = ComponentScope;

/**
 * @callback ScopeSourceFn
 * @returns {Object<string, *> | null | undefined}
 */
;class ComponentChannel {
	/** @type {string} */
	static HANDSHAKE_TOPIC = 'use-provided';

	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 */
	constructor(hostElement) {
		/** @type {RenderableComponent & HTMLElement} */
		this._hostElement = hostElement;

		/** @type {Map<string, ProviderResolver>} */
		this._resolversByKey = new Map();

		/** @type {Map<string, *>} */
		this._lastEmittedValueByKey = new Map();

		/** @type {Map<string, Map<symbol, ProviderSubscriber>>} */
		this._subscribersByKey = new Map();

		/** @type {Set<string>} */
		this._dirtyKeys = new Set();

		/** @type {(() => void) | null} */
		this._unsubHandshake = null;
		this._mountOnce();
	}

	/**
	 * @param {string}           key
	 * @param {ProviderResolver} resolver
	 * @returns {void}
	 */
	provide(key, resolver) {
		if (typeof resolver !== 'function') {
			throw new Error(`ComponentChannel.provide("${key}") expects a resolver function`);
		}
		this._resolversByKey.set(String(key), resolver);
		this._dirtyKeys.add(String(key));
	}

	/**
	 * @param {string} key
	 * @param {*}      value
	 * @returns {void}
	 */
	provideValue(key, value) {
		const normalizedKey = String(key);
		this._resolversByKey.set(normalizedKey, () => value);
		this._dirtyKeys.add(normalizedKey);
	}

	/**
	 * @param {Object<string, ProviderResolver | *>} providesObject
	 * @returns {void}
	 */
	provideMany(providesObject) {
		if (!providesObject || typeof providesObject !== 'object') return;
		for (const [key, valueOrResolver] of Object.entries(providesObject)) {
			if (typeof valueOrResolver === 'function') this.provide(key, valueOrResolver);
			else this.provideValue(key, valueOrResolver);
		}
	}

	/**
	 * @param {string} key
	 * @returns {void}
	 */
	unprovide(key) {
		const normalizedKey = String(key);
		this._resolversByKey.delete(normalizedKey);
		this._lastEmittedValueByKey.delete(normalizedKey);
		this._subscribersByKey.delete(normalizedKey);
		this._dirtyKeys.delete(normalizedKey);
	}

	/**
	 * @param {string} key
	 * @returns {void}
	 */
	notifyProvided(key) {
		this._dirtyKeys.add(String(key));
	}

	/** @returns {void} */
	notifySubscribers() {
		const keysToCheck = new Set([
			...this._subscribersByKey.keys(),
			...this._dirtyKeys,
		]);

		if (keysToCheck.size === 0) return;

		for (const key of keysToCheck) {
			const resolver = this._resolversByKey.get(key);
			if (!resolver) continue;

			/** @type {*} */
			let nextValue;
			try {
				nextValue = resolver({});
			} catch (error) {
				console.error(`[ComponentChannel] resolver error for "${key}"`, error);
				continue;
			}

			const prevValue = this._lastEmittedValueByKey.get(key);
			const changed = !App.helper.object.shallowEqual(prevValue, nextValue);

			if (!changed) {
				this._dirtyKeys.delete(key);
				continue;
			}

			this._lastEmittedValueByKey.set(key, nextValue);
			this._dirtyKeys.delete(key);

			const subscribers = this._subscribersByKey.get(key);
			if (!subscribers) continue;

			for (const [subscriberId, subscriber] of subscribers) {
				let shouldUnsubscribe = false;
				try {
					shouldUnsubscribe = subscriber(nextValue) === false;
				} catch (error) {
					shouldUnsubscribe = true;
					console.error(`[ComponentChannel] subscriber error for "${key}"`, error);
				}

				if (shouldUnsubscribe) {
					subscribers.delete(subscriberId);
				}
			}

			if (subscribers.size === 0) {
				this._subscribersByKey.delete(key);
			}
		}
	}

	/** @returns {void} */
	close() {
		if (this._unsubHandshake) {
			this._unsubHandshake();
			this._unsubHandshake = null;
		}
		this._resolversByKey.clear();
		this._lastEmittedValueByKey.clear();
		this._subscribersByKey.clear();
		this._dirtyKeys.clear();
	}

	/** @returns {void} */
	_mountOnce() {
		this._unsubHandshake = this._hostElement.onHandshake(ComponentChannel.HANDSHAKE_TOPIC, (event) => {
			const requestedKey = event.detail?.requestedKey;
			const request = event.detail?.request || {};
			if (!requestedKey) return undefined;

			const key = String(requestedKey);
			const resolver = this._resolversByKey.get(key);
			if (!resolver) return undefined;

			if (request.subscribe && typeof request.onUpdate === 'function') {
				return this._addSubscriber(key, request.onUpdate);
			}

			return resolver(request);
		});
	}

	/**
	 * @param {string}             key
	 * @param {ProviderSubscriber} onUpdate
	 * @returns {() => void}
	 */
	_addSubscriber(key, onUpdate) {
		let subscribers = this._subscribersByKey.get(key);
		if (!subscribers) this._subscribersByKey.set(key, (subscribers = new Map()));

		const subscriberId = Symbol(key);
		subscribers.set(subscriberId, onUpdate);

		return () => {
			const map = this._subscribersByKey.get(key);
			if (!map) return;
			map.delete(subscriberId);
			if (map.size === 0) {
				this._subscribersByKey.delete(key);
				this._lastEmittedValueByKey.delete(key);
				this._dirtyKeys.delete(key);
			}
		};
	}
}

App.classes.component_channel = ComponentChannel;

class DeepProvider extends ComponentChannel {
	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 */
	constructor(hostElement) {
		super(hostElement,);

		/** @type {Map<string, () => void>} */
		this._consumerUnsubByKey = new Map();

		/** @type {Map<string, *>} */
		this._consumerCacheByKey = new Map();

		/** @type {Set<string>} */
		this._consumerDirtyKeys = new Set();

		/** @type {Object | undefined} */
		this._providedProxy = undefined;
	}

	/**
	 * @param {string}  key
	 * @param {Object}  [request]
	 * @returns {*}
	 */
	useProvided(key, request = undefined) {
		return this._hostElement.requestHandshake(ComponentChannel.HANDSHAKE_TOPIC, {
			details: {requestedKey: String(key), request}
		});
	}

	/**
	 * @param {string}             key
	 * @param {ProviderSubscriber} onUpdate
	 * @param {Object}             [request]
	 * @returns {() => void}
	 */
	subscribeProvided(key, onUpdate, request = undefined) {
		const unsubscribe = this._hostElement.requestHandshake(ComponentChannel.HANDSHAKE_TOPIC, {
			details: {requestedKey: String(key), request: {...request, subscribe: true, onUpdate}}
		});
		return typeof unsubscribe === 'function' ? unsubscribe : () => {};
	}

	/**
	 * @param {ProvidedProxyInvalidator} onInvalidate
	 * @returns {Object}
	 */
	getProvidedProxy(onInvalidate) {
		const self = this;

		return this._providedProxy || (this._providedProxy = new Proxy(Object.create(null), {
			get(_, prop) {
				const key = String(prop);

				if (!self._consumerUnsubByKey.has(key)) {
					const unsubscribe = self.subscribeProvided(key, () => {
						self._consumerDirtyKeys.add(key);
						try {
							onInvalidate(key);
						} catch (error) {
							console.error(error)
						}
					});
					self._consumerUnsubByKey.set(key, unsubscribe);
					self._consumerDirtyKeys.add(key);
				}

				if (!self._consumerDirtyKeys.has(key) && self._consumerCacheByKey.has(key)) {
					return self._consumerCacheByKey.get(key);
				}

				const value = self.useProvided(key);
				self._consumerCacheByKey.set(key, value);
				self._consumerDirtyKeys.delete(key);
				return value;
			}
		}));
	}

	/** @returns {void} */
	disposeConsumers() {
		for (const unsubscribe of this._consumerUnsubByKey.values()) {
			try {
				unsubscribe();
			} catch (error) {
				console.error('[DeepProvider] consumer unsubscribe error', error);
			}
		}
		this._consumerUnsubByKey.clear();
		this._consumerCacheByKey.clear();
		this._consumerDirtyKeys.clear();
	}

	/** @returns {void} */
	dispose() {
		this.disposeConsumers();
		this.close();
	}
}

App.classes.deep_provider = DeepProvider;

/**
 * @callback ProviderResolver
 * @param {Object} request
 * @returns {*}
 */

/**
 * @callback ProviderSubscriber
 * @param {*} nextValue
 * @returns {false | void}
 */

/**
 * @callback ProvidedProxyInvalidator
 * @param {string} key
 * @returns {void}
 */

/**
 * @typedef {Object} HandshakeRequest
 * @property {string}              requestedKey
 * @property {Object}              [request]
 * @property {boolean}             [subscribe]
 * @property {ProviderSubscriber}  [onUpdate]
 */
;class DirectiveRegistry extends SingletonMixin() {
	/** @type {DirectiveDefinition[]} */
	_definitions = [];

	/**
	 * @type {ApplyMemoFn}
	 */
	_applyMemo = (evaluate, apply, areEqual) =>
		this._applyMemoHelper(evaluate, apply, areEqual ?? Object.is);

	/**
	 * @type {DefaultFrameFactory}
	 */
	_defaultMakeFrame = (target) => ({$target: target, $component: null});

	/**
	 * @type {DefaultFrameFactory}
	 */
	_defaultMakeEventFrame = (target) => ({$target: target, $component: null, $event: null});

	/**
	 * @param {DirectiveDefinition} definition
	 * @returns {void}
	 */
	register(definition) {
		this._definitions.push(definition);
	}

	/**
	 * @param {Element} root
	 * @returns {void}
	 */
	normalizeForHydration(root) {
		for (const def of this._definitions) {
			def.normalizeHydration?.(root);
		}
		for (const tpl of root.querySelectorAll('template')) {
			const inner = tpl.content?.firstElementChild;
			if (inner) this.normalizeForHydration(inner);
		}
	}

	/**
	 * @param {ProcessElementOptions} options
	 * @returns {ProcessElementResult}
	 */
	processElement({
					   element,
					   component,
					   addBinding,
					   customPrefix = 'ws-',
					   ephemeral = null,
					   bindingManager = component.bindingManager,
					   makeFrame = this._defaultMakeFrame,
					   makeEventFrame = this._defaultMakeEventFrame,

				   }) {
		const directives = this._parseDirectives(element, customPrefix);

		return this._executeDirectives(directives, element, {
			component, addBinding, ephemeral, bindingManager,
			makeFrame, makeEventFrame,
			subPlan: null
		});
	}

	/**
	 * @param {ProcessElementFromPlanOptions} options
	 * @returns {ProcessElementResult}
	 */
	processElementFromPlan({
							   entry,
							   node,
							   component,
							   addBinding,
							   ephemeral,
							   bindingManager,
							   makeFrame = this._defaultMakeFrame,
							   makeEventFrame = this._defaultMakeEventFrame,
						   }) {
		return this._executeDirectives(entry.directives, node, {
			component, addBinding, ephemeral, bindingManager,
			makeFrame, makeEventFrame,
			subPlan: entry.subPlan
		});
	}


	/**
	 * @param {PlanDirective[]}     directives
	 * @param {Element}             node
	 * @param {Object}              ctx
	 * @param {RenderableComponent} ctx.component
	 * @param {AddBindingFn}  ctx.addBinding
	 * @param {EphemeralScope|null} ctx.ephemeral
	 * @param {BindingManager}      ctx.bindingManager
	 * @param {PlanEntry[]|null}    ctx.subPlan
	 * @returns {ProcessElementResult}
	 */
	_executeDirectives(directives, node, {
		component, addBinding, ephemeral, bindingManager,
		makeFrame, makeEventFrame,
		subPlan
	}) {
		const ctor = component.constructor;
		const cache = ctor._expressionCache ??= new Map();
		/** @type {EvaluateExpressionFn} */
		const evaluateExpression = (expression, scope, frame) =>
			frame
				? App.helper.eval_safe_context(expression, scope, frame, cache)
				: App.helper.eval_safe_context(expression, scope, cache);

		const applyMemo = this._applyMemo;

		let shouldSkipChildren = false;

		/**
		 * @param {PlanDirective} directive
		 * @returns {void}
		 */
		const runDirective = ({name, attrName, value, def}) => {
			def.init({
				element: node,
				component,
				name,
				value,
				addBinding: (binding, to = node) => {
					binding.label = name;
					addBinding(binding, to);
				},
				ephemeral,
				evaluateExpression,
				applyMemo,
				bindingManager,
				subPlan,
				makeFrame,
				makeEventFrame,
			});
			if (def.controlsSubtree) shouldSkipChildren = true;
			if (def.removeAttribute !== false) node.removeAttribute(attrName);
		};

		const owner = directives.find(d => d.def.controlsElement) ?? null;

		if (owner) {
			runDirective(owner);
			return {shouldSkipChildren, didOwnElement: true};
		}

		for (let i = 0; i < directives.length; i++) runDirective(directives[i]);

		return {shouldSkipChildren, didOwnElement: false};
	}

	/**
	 * @param {Element} element
	 * @param {string}  prefix
	 * @returns {PlanDirective[]}
	 */
	_parseDirectives(element, prefix) {
		/** @type {PlanDirective[]} */
		const directives = [];

		for (const attr of element.attributes) {
			if (!attr.name.startsWith(prefix)) continue;

			const name = prefix !== 'ws-'
				? attr.name.replace(prefix, 'ws-')
				: attr.name;

			const def = this._definitions.find(d => d.matches(name));
			if (def) directives.push({name, attrName: attr.name, value: attr.value, def});
		}

		return directives;
	}

	/**
	 * @template T
	 * @param {(scope: Object, frame: BindingFrame) => T}            evaluate
	 * @param {(value: T, scope: Object, frame: BindingFrame) => void} apply
	 * @param {(a: T, b: T) => boolean}           [areEqual]
	 * @returns {(scope: Object, frame: BindingFrame) => void}
	 */
	_applyMemoHelper(evaluate, apply, areEqual = Object.is) {
		/** @type {boolean} */
		let hasValue = false;
		/** @type {T} */
		let lastValue;

		return function update(scope, frame) {
			const nextValue = evaluate(scope, frame);
			if (hasValue && areEqual(nextValue, lastValue)) {
				return;
			}
			hasValue = true;
			lastValue = nextValue;
			apply(nextValue, scope, frame);
		};
	}

	/**
	 * @param {Element} root
	 * @param {string}  [rootPrefix='ws-']
	 * @param {string}  [childPrefix='ws-']
	 * @returns {PlanEntry[]}
	 */
	buildBindingPlan(root, rootPrefix = 'ws-', childPrefix = 'ws-') {
		/** @type {PlanEntry[]} */
		const plan = [];
		this._walkForPlan(root, [], rootPrefix, childPrefix, plan, true);
		return plan;
	}

	/**
	 * @param {Element}     node
	 * @param {number[]}    path
	 * @param {string}      prefix
	 * @param {string}      childPrefix
	 * @param {PlanEntry[]} targetPlan
	 * @param {boolean}     [isRoot=false]
	 * @returns {void}
	 */
	_walkForPlan(node, path, prefix, childPrefix, targetPlan, isRoot = false) {
		if (!(node instanceof Element)) return;
		if (!isRoot && node instanceof RenderableComponent) return;

		const effectivePrefix = isRoot ? prefix : childPrefix;
		/** @type {PlanDirective[]} */
		const directives = [];
		/** @type {PlanDirective | null} */
		let owner = null;

		for (const attr of node.attributes) {
			if (!attr.name.startsWith(effectivePrefix)) continue;

			const name = effectivePrefix !== 'ws-'
				? attr.name.replace(effectivePrefix, 'ws-')
				: attr.name;

			const def = this._definitions.find(d => d.matches(name));
			if (!def) continue;

			if (!owner && def.controlsElement) owner = {name, attrName: attr.name, value: attr.value, def};
			directives.push({name, attrName: attr.name, value: attr.value, def});
		}

		if (owner) {
			const templateNode = owner.def.getTemplateNode?.(node) ?? null;
			const subPlan = templateNode
				? this.buildBindingPlan(templateNode, childPrefix, childPrefix)
				: null;

			targetPlan.push({path, directives, subPlan});
			return;
		}

		if (directives.length) {
			targetPlan.push({path, directives, subPlan: null});
		}

		if (directives.some(d => d.def.controlsSubtree)) return;

		for (let i = 0; i < node.children.length; i++) {
			this._walkForPlan(node.children[i], [...path, i], prefix, childPrefix, targetPlan, false);
		}
	}

}

App.classes.directive_registry = DirectiveRegistry;

/* ===========================	REACTIVE DIRECTIVES =========================== */
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-show',
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			let originalDisplay = element.style.display || '';
			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(value, scope, frame),
					(show) => element.style.display = show ? '' : 'none'
				),
				dispose() {
					element.style.display = originalDisplay;
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-text',
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			let [expression, modifiers] = value.split(':').map(s => s?.trim());
			let modifierSet = new Set();
			if (modifiers) {
				modifiers.split(',').forEach(mod => modifierSet.add(mod.trim()));
			}
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						let result = evaluateExpression(expression, scope, frame);
						result = result === null || result === undefined ? '' : String(result);
						modifierSet.forEach((mod) => {
							result = App.helper.template.run_modifier(mod, result);
						});
						return result;
					},
					(result) => element.textContent = result
				),
				dispose: () => {
					element.textContent = '';
				}
			});
		}
	}));
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-html',
		removeAttribute: true,

		/**
		 * @param {string|Node|DocumentFragment|null} value
		 * @returns {DocumentFragment}
		 */
		_toFragment(value) {
			if (value instanceof DocumentFragment) {
				return value.cloneNode(true);
			}

			if (value instanceof Node) {
				const fragment = document.createDocumentFragment();
				fragment.appendChild(value.cloneNode(true));
				return fragment;
			}

			const template = document.createElement('template');
			template.innerHTML = value || '';
			return template.content;
		},

		init({element, component, value, addBinding, evaluateExpression, applyMemo, bindingManager}) {
			const toFragment = (v) => this._toFragment(v);
			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(value, scope, frame) ?? '',
					(next) => {
						for (const childNode of Array.from(element.childNodes)) {
							bindingManager.disposeInSubtree(childNode);
						}
						element.replaceChildren(toFragment(next));
						let addedCount = 0;
						for (const childElement of Array.from(element.children)) {
							addedCount += bindingManager.collectInSubtree(childElement);
						}
						addedCount && component.invalidateLifecycle('ws-html', LifecycleInvalidations.BINDINGS);
					}
				),
				dispose() {
					for (const childNode of Array.from(element.childNodes)) {
						bindingManager.disposeInSubtree(childNode);
					}
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-attr-'),
		removeAttribute: true,
		init({element, name, value, addBinding, evaluateExpression, applyMemo}) {
			const attrName = name.slice(8);
			let [expression, modifiers] = value.split(':').map(s => s?.trim());
			let modifierSet = new Set();
			if (modifiers) {
				modifiers.split(',').forEach(mod => modifierSet.add(mod.trim()));
			}
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						let result = evaluateExpression(expression, scope, frame);
						modifierSet.forEach((mod) => {
							result = App.helper.template.run_modifier(mod, result);
						});
						return result;
					},
					(result) => {
						if (result == null || result === false) {
							element.removeAttribute(attrName);
						} else {
							element.setAttribute(attrName, String(result));
						}
					}
				),
				dispose() {
					element.removeAttribute(attrName);
				},
				el: element,
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-prop-'),
		removeAttribute: true,
		init({element, name, value, addBinding, evaluateExpression, applyMemo}) {
			const propName = name.slice(8);
			const originalValue = element[propName];
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						return evaluateExpression(value, scope, frame);
					},
					(result) => {
						element[propName] = result;
					}
				),
				dispose() {
					element[propName] = originalValue;
				},
				el: element,
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-slot-'),
		removeAttribute: true,
		init({element, name, value, addBinding, evaluateExpression, applyMemo}) {
			const slotName = name.slice(8);
			let [expression, modifiers] = value.split(':').map(s => s?.trim());
			let modifierSet = new Set();
			if (modifiers) {
				modifiers.split(',').forEach(mod => modifierSet.add(mod.trim()));
			}
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						return evaluateExpression(expression, scope, frame);
					},
					(result) => {
						element.setSlot(slotName, result);
					}
				),
				dispose() {
					element.setSlot(slotName, '');
				},
			});
		}
	}));


App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-proxy-attr-'),
		removeAttribute: true,
		init({element, component, name, value, addBinding, applyMemo}) {
			const attrName = name.slice('ws-proxy-attr-'.length);
			addBinding({
				update: applyMemo(
					(scope, frame) => component.getAttribute(value),
					(attrVal) => {
						if (attrVal == null || attrVal === false) {
							element.removeAttribute(attrName);
						} else {
							element.setAttribute(attrName, attrVal);
						}
					}
				),
				dispose() {
					element.removeAttribute(attrName);
				}
			});
		}
	}));
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-proxy-attrs',
		removeAttribute: true,
		init({element, component, value, addBinding, applyMemo}) {
			const specifiedAttrs = value
				.split(',')
				.map(s => s.trim())
				.filter(Boolean);

			const proxyAll = specifiedAttrs.length === 0;

			/** @type {Map<string, string|null>} */
			let lastApplied = new Map();

			/** @returns {Map<string, string|null>} */
			const snapshot = () => {
				/** @type {Map<string, string|null>} */
				const map = new Map();
				if (proxyAll) {
					for (const {name, value} of Array.from(component.attributes)) {
						map.set(name, value);
					}
				} else {
					for (const name of specifiedAttrs) {
						map.set(name, component.hasAttribute(name)
							? component.getAttribute(name)
							: null
						);
					}
				}
				return map;
			};

			/**
			 * @param {Map<string, string|null>} map
			 * @returns {string}
			 */
			const signature = (map) =>
				Array.from(map.entries())
					.map(([k, v]) => `${k}\x00${v ?? '\x01'}`)
					.join('\x02');

			/**
			 * @param {Map<string, string|null>} map
			 * @returns {void}
			 */
			const applyMap = (map) => {
				for (const [name] of lastApplied) {
					if (!map.has(name) || map.get(name) === null) {
						element.removeAttribute(name);
					}
				}
				for (const [name, val] of map) {
					if (val === null) {
						if (lastApplied.has(name) && lastApplied.get(name) !== null) {
							element.removeAttribute(name);
						}
					} else {
						if (lastApplied.get(name) !== val) {
							element.setAttribute(name, val);
						}
					}
				}
				lastApplied = map;
			};

			addBinding({
				update: applyMemo(
					(scope, frame) => {
						const map = snapshot();
						return {map, sig: signature(map)};
					},
					({map}) => applyMap(map),
					(a, b) => a.sig === b.sig
				),
				dispose() {
					for (const [name] of lastApplied) {
						element.removeAttribute(name);
					}
					lastApplied = new Map();
				}
			});
		}
	})
);
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-if',
		removeAttribute: true,
		controlsElement: true,
		controlsSubtree: true,

		getTemplateNode(element) {
			const clone = element.cloneNode(true);
			clone.removeAttribute('ws-if');
			return clone;
		},

		init({element, value, addBinding, evaluateExpression, applyMemo, bindingManager, subPlan}) {

			const hydrating = element instanceof HTMLTemplateElement;

			/** @type {Element} */
			let originalElement;
			/** @type {Comment} */
			let placeholder;
			/** @type {boolean} */
			let isInDOM;

			if (hydrating) {
				originalElement = element.content.firstElementChild;
				placeholder = document.createComment('ws-if placeholder');
				element.replaceWith(placeholder);
				isInDOM = false;
			} else {
				originalElement = element;
				placeholder = document.createComment('ws-if placeholder');
				isInDOM = true;
			}

			if (subPlan) {
				bindingManager.executePlan(subPlan, originalElement);
			} else {
				originalElement.removeAttribute('ws-if');
				bindingManager.collectInSubtree(originalElement);
			}

			const removeElement = () => {
				if (isInDOM) {
					originalElement.replaceWith(placeholder);
					isInDOM = false;
				}
			};
			const insertElement = () => {
				if (!isInDOM) {
					placeholder.replaceWith(originalElement);
					isInDOM = true;
				}
			};

			let disposing = false;
			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(value, scope, frame),
					(show) => {
						if (show) {
							insertElement();
						} else {
							removeElement();
						}
					}
				),
				dispose() {
					if (!disposing) {
						disposing = true;
						bindingManager.disposeInSubtree(placeholder);
					}
					placeholder = null;
				}
			});
			addBinding({
				dispose() {
					if (!disposing) {
						disposing = true;
						bindingManager.disposeInSubtree(originalElement);
					}
					originalElement = null;
				}
			}, placeholder);
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches(attributeName) {
			return attributeName === 'ws-class';
		},
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			const raw = value.trim();
			if (!raw) return;
			/** @type {Set<string>} */
			let lastApplied = new Set();
			/** @type {Set<string>} */
			let nextBuffer = new Set();

			/**
			 * @param {string[]} classList
			 * @returns {void}
			 */
			const applyClasses = (classList) => {
				const next = nextBuffer;
				next.clear();
				for (const c of classList) next.add(c);

				for (const c of lastApplied) if (!next.has(c) && element.classList.contains(c)) element.classList.remove(c);
				for (const c of next) if (!lastApplied.has(c) && !element.classList.contains(c)) element.classList.add(c);

				nextBuffer = lastApplied;
				lastApplied = next;
			};


			addBinding({
				update: applyMemo(
					(scope, frame) => {
						const arr = evaluateExpression(raw, scope, frame);
						if (!Array.isArray(arr)) return {signature: '', list: []};

						const list = arr
							.map(x => (typeof x === 'string' ? x.trim() : null))
							.filter(Boolean);
						return {signature: list.join('\u0001'), list};
					},
					(result) => applyClasses(result.list),
					(a, b) => a.signature === b.signature
				),
				dispose() {
					applyClasses([]);
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches(attributeName) {
			return attributeName === 'ws-style';
		},
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			const raw = (value || '').trim();
			if (!raw) return;

			const looksLikeSingleExpression =
				!raw.includes(';') && raw.indexOf(':') === -1;

			/**
			 * @param {*} styleValue
			 * @returns {string | null}
			 */
			const normalizeStyleValue = (styleValue) => {
				if (styleValue == null || styleValue === false) return null;
				return String(styleValue);
			};

			/**
			 * @param {string} prop
			 * @returns {string}
			 */
			const toCssProp = (prop) => App.helper.string.to_kebab_case(prop);

			/** @type {Map<string, string|null>} */
			let lastValuesByProp = new Map();

			/**
			 * @param {Object<string, *> | null} map
			 * @returns {void}
			 */
			const applyMap = (map) => {
				/** @type {Map<string, string|null>} */
				const nextValuesByProp = new Map();

				if (map && typeof map === 'object') {
					for (const key in map) {
						if (!Object.prototype.hasOwnProperty.call(map, key)) continue;

						const cssProp = toCssProp(key);
						const nextValue = normalizeStyleValue(map[key]);

						nextValuesByProp.set(cssProp, nextValue);

						const prevValue = lastValuesByProp.get(cssProp);
						if (Object.is(prevValue, nextValue)) continue;


						if (nextValue === null) {
							element.style.removeProperty(cssProp);
						} else {
							const [value, priority] = nextValue.split('!').map(s => s.trim());
							element.style.setProperty(cssProp, value, priority);
						}
					}
				}

				for (const [prevProp, prevValue] of lastValuesByProp) {
					if (!nextValuesByProp.has(prevProp)) {
						if (prevValue !== null) element.style.removeProperty(prevProp);
					}
				}

				lastValuesByProp = nextValuesByProp;
			};

			/**
			 * @param {string} cssText
			 * @returns {Object<string, string>}
			 */
			const parseCssTextToMap = (cssText) => {
				/** @type {Object<string, string>} */
				const map = {};
				if (!cssText) return map;

				cssText
					.split(';')
					.map((chunk) => chunk.trim())
					.filter(Boolean)
					.forEach((chunk) => {
						const colonIndex = chunk.indexOf(':');
						if (colonIndex === -1) return;

						const prop = chunk.slice(0, colonIndex).trim();
						const val = chunk.slice(colonIndex + 1).trim();
						if (!prop) return;

						map[prop] = val;
					});

				return map;
			};

			/** @type {Array<{propPart: string, expression: string}> | null} */
			let compiledRules = null;

			if (!looksLikeSingleExpression) {
				compiledRules = raw
					.split(';')
					.map((chunk) => chunk.trim())
					.filter(Boolean)
					.map((chunk) => {
						const colonIndex = chunk.indexOf(':');
						if (colonIndex === -1) return null;

						const propPart = chunk.slice(0, colonIndex).trim();
						const exprPart = chunk.slice(colonIndex + 1).trim();

						if (!propPart) return null;

						return {
							propPart,
							expression: exprPart || 'null',
						};
					})
					.filter(Boolean);
			}

			const updateSingleExpression = applyMemo(
				(scope, frame) => evaluateExpression(raw, scope, frame),
				(styleValue) => {
					if (typeof styleValue === 'string') {
						applyMap(parseCssTextToMap(styleValue));
						return;
					}
					applyMap(styleValue || {});
				},
				(prev, next) => Object.is(prev, next)
			);

			const updateRules = applyMemo(
				(scope, frame) => {
					/** @type {Object<string, *>} */
					const map = {};
					let signature = '';

					for (const rule of compiledRules) {
						const evaluatedValue = evaluateExpression(rule.expression, scope, frame);
						map[rule.propPart] = evaluatedValue;
						const normalizedValue = normalizeStyleValue(evaluatedValue);
						signature += rule.propPart + '=' + (normalizedValue === null ? '' : normalizedValue) + ';';
					}

					return {signature, map};
				},
				(result) => applyMap(result.map),
				(prev, next) => prev.signature === next.signature
			);

			addBinding({
				update(scope, frame) {
					try {
						if (looksLikeSingleExpression) {
							updateSingleExpression(scope, frame);
							return;
						}
						updateRules(scope, frame);
					} catch (error) {
						console.error('ws-style error', raw, error);
					}
				},
				dispose() {
					applyMap({});
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches(attributeName) {
			return attributeName === 'ws-model';
		},
		removeAttribute: true,
		init({element, value, component, addBinding, applyMemo}) {
			const parts = value.split('|').map(s => s.trim());
			/** @type {Object<string, string>} */
			const config = {};
			parts.forEach(part => {
				const [key, val] = part.split(':').map(s => s.trim());
				if (key && val !== undefined) {
					config[key] = val;
				}
			});
			const {event = 'change', attr, bind = 'value', path = 'value'} = config;
			if (!attr) {
				console.error('ws-model requires "attr" configuration');
				return;
			}

			/** @param {Event} e */
			const onModelEvent = (e) => {
				/** @type {*} */
				let newValue;
				if (path) {
					newValue = App.helper.object.readByPath(e, path);
				} else {
					newValue = e.target.value;
				}

				if (newValue == null) {
					component.removeAttribute(attr);
				} else {
					component.setAttribute(attr, String(newValue));
				}
			};

			const {unbind} = element.addEventListenerWs(event, onModelEvent);
			addBinding({
				update: applyMemo(
					(scope, frame) => component.getAttribute(attr),
					(modelValue) => {
						if (modelValue == null) element.removeAttribute(bind);
						else element.setAttribute(bind, modelValue);
					}
				),
				dispose() {
					unbind();
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-for',
		removeAttribute: true,
		controlsSubtree: true,
		controlsElement: true,
		getTemplateNode(element) {
			const clone = element.cloneNode(true);
			clone.removeAttribute('ws-for');
			return clone;
		},

		/** @param {Element} root */
		normalizeHydration(root) {
			for (const tpl of root.querySelectorAll('template[ws-for-src]')) {
				let node = tpl.nextSibling;
				while (node && !(node.nodeType === Node.COMMENT_NODE && node.data === 'ws-for:start')) {
					node = node.nextSibling;
				}
				if (!node) continue;

				const fragment = document.createDocumentFragment();
				while (node) {
					const next = node.nextSibling;
					const isEnd = node.nodeType === Node.COMMENT_NODE && node.data === 'ws-for:end';
					fragment.appendChild(node);
					if (isEnd) break;
					node = next;
				}
				tpl._hydrateFragment = fragment;
			}
		},
		init({
				 element,
				 value,
				 component,
				 ephemeral: parentEphemeral = null,
				 addBinding,
				 evaluateExpression,
				 applyMemo,
				 subPlan = null,
				 bindingManager,
			 }) {

			/**
			 * @param {string} raw
			 * @returns {WsForConfig}
			 */
			const parse = (raw) => {
				const [headRaw, ...tailParts] = String(raw || '').split('|').map(s => s.trim()).filter(Boolean);
				const head = headRaw || '';

				const inMatch = head.match(/^(.*?)\s+in\s+(.+)$/);
				if (!inMatch) throw new Error(`ws-for: invalid syntax "${raw}". Expected "item in items"`);

				const varsPart = inMatch[1].trim();
				const listExpression = inMatch[2].trim();

				const vars = varsPart.split(',').map(s => s.trim()).filter(Boolean);
				const itemVarName = vars[0] || 'item';
				const indexVarName = vars[1] || 'index';

				/** @type {Object<string, string>} */
				const options = {};
				for (const part of tailParts) {
					const [key, ...rest] = part.split(':');
					if (!key) continue;
					options[key.trim()] = rest.join(':').trim();
				}

				return {
					itemVarName,
					indexVarName,
					listExpression,
					keyExpression: options.key || null,
				};
			};

			const config = parse(value);

			const hydrating = element instanceof HTMLTemplateElement;

			const templateElement = hydrating
				? element.content.firstElementChild
				: element.cloneNode(true);

			if (!hydrating) templateElement.removeAttribute('ws-for');

			/** @type {Comment} */
			let loopStart;
			/** @type {Comment} */
			let loopEnd;

			if (hydrating) {
				const holding = element._hydrateFragment;
				if (holding) {
					element.parentNode.insertBefore(holding, element.nextSibling);
					element._hydrateFragment = null;
				}
				const anchors = this._findForAnchors(element);
				loopStart = anchors.start;
				loopEnd = anchors.end;
				element.remove();
			} else {
				loopStart = document.createComment('ws-for:start');
				loopEnd = document.createComment('ws-for:end');
				element.replaceWith(loopStart, loopEnd);
			}

			/**
			 * @param {Comment} rangeStart
			 * @param {Comment} rangeEnd
			 * @returns {DocumentFragment}
			 */
			const extractRangeToFragment = (rangeStart, rangeEnd) => {
				const fragment = document.createDocumentFragment();
				const parent = rangeStart.parentNode;
				if (!parent || rangeEnd.parentNode !== parent) return fragment;

				let node = rangeStart;
				while (node) {
					const next = node.nextSibling;
					fragment.appendChild(node);
					if (node === rangeEnd) break;
					node = next;
				}
				return fragment;
			};

			/**
			 * @param {Comment} rangeStart
			 * @param {Comment} rangeEnd
			 * @returns {void}
			 */
			const removeRange = (rangeStart, rangeEnd) => {
				const parent = rangeStart.parentNode;
				if (!parent || rangeEnd.parentNode !== parent) return;
				let node = rangeStart;
				while (node) {
					const next = node.nextSibling;
					parent.removeChild(node);
					if (node === rangeEnd) break;
					node = next;
				}
			};

			/** @type {Map<string, WsForInstance>} */
			const instancesByKey = new Map();
			/** @type {number} */
			let renderTick = 0;

			/**
			 * @param {string}  key
			 * @param {Element} root
			 * @param {Node}    insertBeforeNode
			 * @returns {WsForInstance}
			 */
			const createInstance = (key, root, insertBeforeNode) => {
				const itemStart = document.createComment(`ws-for:item:${key}:start`);
				const itemEnd = document.createComment(`ws-for:item:${key}:end`);

				const fragment = document.createDocumentFragment();
				fragment.appendChild(itemStart);
				fragment.appendChild(root);
				fragment.appendChild(itemEnd);
				loopEnd.parentNode.insertBefore(fragment, insertBeforeNode);

				return this._buildInstance({
					key, root, itemStart, itemEnd,
					config, component, parentEphemeral,
					subPlan, bindingManager,
				});
			};

			/**
			 * @param {string} key
			 * @param {Node}   insertBeforeNode
			 * @returns {WsForInstance}
			 */
			const ensureInstance = (key, insertBeforeNode) => {
				let instance = instancesByKey.get(key);
				if (instance) return instance;

				const clone = templateElement.cloneNode(true);
				const instance_ = createInstance(key, clone, insertBeforeNode);
				instancesByKey.set(key, instance_);
				return instance_;
			};

			if (hydrating) {
				this._adoptExistingInstances({
					loopStart, loopEnd,
					config, component, parentEphemeral,
					subPlan, bindingManager, instancesByKey,
				});
			}

			/**
			 * @param {WsForInstance} instance
			 * @param {Node}          insertBeforeNode
			 * @returns {void}
			 */
			const moveInstanceBefore = (instance, insertBeforeNode) => {
				if (instance.end.nextSibling === insertBeforeNode) return;
				const fragment = extractRangeToFragment(instance.start, instance.end);
				loopEnd.parentNode.insertBefore(fragment, insertBeforeNode);
			};

			/**
			 * @param {*}      keyValue
			 * @param {number} fallbackIndex
			 * @returns {string}
			 */
			const normalizeKey = (keyValue, fallbackIndex) => {
				if (keyValue == null) return String(fallbackIndex);
				const keyType = typeof keyValue;
				if (keyType === 'string' || keyType === 'number') return String(keyValue);
				console.warn('ws-for: invalid key type, expected string|number. Fallback to index.', keyValue);
				return String(fallbackIndex);
			};

			/**
			 * @param {WsForInstance} instance
			 * @returns {void}
			 */
			const disposeInstance = (instance) => {
				instance.owned.release();
				removeRange(instance.start, instance.end);
			};

			/**
			 * @param {WsForInstance} instance
			 * @returns {void}
			 */
			const collectDeferred = (instance) => {
				if (!instance._needsCollect) return;
				instance._needsCollect = false;

				bindingManager._hydratedNodes?.delete(instance.root);
				subPlan
					? component.bindingManager.executePlan(subPlan, instance.root, instance.ephemeral)
					: component.bindingManager.collectInSubtree(instance.root, instance.ephemeral);
			};

			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(config.listExpression, scope, frame),
					(source, scope, frame) => {
						renderTick++;

						/** @type {*[]} */
						let items = [];
						if (Array.isArray(source)) items = source;
						else if (source && typeof source === 'object') items = Object.values(source);

						const total = items.length;

						const keyScope = config.keyExpression
							? Object.create(scope)
							: null;

						/** @type {WsForInstance[]} */
						const orderedInstances = new Array(total);
						for (let i = 0; i < total; i++) {
							const item = items[i];

							let key = String(i);
							if (keyScope) {
								keyScope[config.itemVarName] = item;
								keyScope[config.indexVarName] = i;
								key = normalizeKey(evaluateExpression(config.keyExpression, keyScope, frame), i);
							}

							const instance = ensureInstance(key, loopEnd);
							instance._seenTick = renderTick;
							instance.item = item;
							instance.index = i;
							instance.total = total;
							instance.syncContext();
							collectDeferred(instance);

							orderedInstances[i] = instance;
						}

						for (const [key, instance] of instancesByKey) {
							if (instance._seenTick === renderTick) continue;
							disposeInstance(instance);
							instancesByKey.delete(key);
						}

						/** @type {Node} */
						let cursor = loopEnd;
						for (let i = total - 1; i >= 0; i--) {
							const instance = orderedInstances[i];
							moveInstanceBefore(instance, cursor);
							if (instance.ephemeral.isDirty) instance.provider.notifySubscribers();
							cursor = instance.start;
						}
					}
				),

				dispose() {
					for (const [, instance] of instancesByKey) {
						disposeInstance(instance);
					}
					instancesByKey.clear();
				}
			}, loopStart);
		},

		/**
		 * @param {Object}              options
		 * @param {string}              options.key
		 * @param {Element}             options.root
		 * @param {Comment}             options.itemStart
		 * @param {Comment}             options.itemEnd
		 * @param {WsForConfig}         options.config
		 * @param {RenderableComponent} options.component
		 * @param {EphemeralScope|null} options.parentEphemeral
		 * @param {PlanEntry[]|null}    options.subPlan
		 * @param {BindingManager}      options.bindingManager
		 * @param {boolean}             [options.skipCollect=false]
		 * @returns {WsForInstance}
		 */
		_buildInstance({
						   key, root, itemStart, itemEnd,
						   config, component, parentEphemeral,
						   subPlan, bindingManager,
						   skipCollect = false,
					   }) {
			const parentContext = parentEphemeral ? parentEphemeral.context : component.scope.context;
			const ephemeral = new EphemeralScope(parentContext);
			const loop = {};

			ephemeral.set(config.itemVarName, null);
			ephemeral.set(config.indexVarName, 0);
			ephemeral.set('loop', loop);

			/** @type {WsForInstance} */
			const instance = {
				key,
				start: itemStart,
				end: itemEnd,
				root,
				ephemeral,
				loop,
				item: null,
				index: 0,
				total: 0,
			};

			instance.syncContext = () => {
				ephemeral.set(config.itemVarName, instance.item);
				ephemeral.set(config.indexVarName, instance.index);

				const nextFirst = instance.index === 0;
				const nextLast = instance.index === instance.total - 1;
				const nextLength = instance.total;

				if (
					loop.index !== instance.index ||
					loop.first !== nextFirst ||
					loop.last !== nextLast ||
					loop.length !== nextLength
				) {
					ephemeral.markDirty('loop');
				}

				loop.index = instance.index;
				loop.key = instance.key;
				loop.first = nextFirst;
				loop.last = nextLast;
				loop.length = nextLength;
			};

			instance.provider = new ComponentChannel(root);
			instance.provider.provideMany({
				[config.itemVarName]: () => instance.item,
				[config.indexVarName]: () => instance.index,
				loop: () => instance.loop,
			});

			if (skipCollect) {
				instance._needsCollect = true;
			} else {
				bindingManager._hydratedNodes?.delete(root);
				subPlan
					? component.bindingManager.executePlan(subPlan, root, ephemeral)
					: component.bindingManager.collectInSubtree(root, ephemeral);
			}

			instance.owned = new OwnedResources(`ws-for:${key}`);
			instance.owned.add('channel', () => instance.provider.close());
			instance.owned.add('bindings', () => component.bindingManager.disposeInRange(instance.start, instance.end));
			instance.owned.add('ephemeral', () => instance.ephemeral.dispose());

			return instance;
		},

		/**
		 * @param {Object}                       options
		 * @param {Comment}                      options.loopStart
		 * @param {Comment}                      options.loopEnd
		 * @param {WsForConfig}                  options.config
		 * @param {RenderableComponent}          options.component
		 * @param {EphemeralScope|null}          options.parentEphemeral
		 * @param {PlanEntry[]|null}             options.subPlan
		 * @param {BindingManager}               options.bindingManager
		 * @param {Map<string, WsForInstance>}   options.instancesByKey
		 * @returns {void}
		 */
		_adoptExistingInstances({
									loopStart, loopEnd,
									config, component, parentEphemeral,
									subPlan, bindingManager, instancesByKey,
								}) {
			const ITEM_PREFIX = 'ws-for:item:';
			const SUFFIX_START = ':start';
			const SUFFIX_END = ':end';

			let node = loopStart.nextSibling;
			while (node && node !== loopEnd) {
				if (node.nodeType !== Node.COMMENT_NODE || !node.textContent.startsWith(ITEM_PREFIX)) {
					node = node.nextSibling;
					continue;
				}

				const text = node.textContent;
				if (!text.endsWith(SUFFIX_START)) {
					node = node.nextSibling;
					continue;
				}

				const key = text.slice(ITEM_PREFIX.length, -SUFFIX_START.length);
				const itemStart = node;
				const expectedEnd = `${ITEM_PREFIX}${key}${SUFFIX_END}`;

				/** @type {Element | null} */
				let root = null;
				/** @type {Comment | null} */
				let itemEnd = null;
				let cursor = itemStart.nextSibling;

				while (cursor && cursor !== loopEnd) {
					if (cursor.nodeType === Node.COMMENT_NODE && cursor.textContent === expectedEnd) {
						itemEnd = cursor;
						break;
					}
					if (!root && cursor.nodeType === Node.ELEMENT_NODE) {
						root = /** @type {Element} */ (cursor);
					}
					cursor = cursor.nextSibling;
				}

				if (!root || !itemEnd) {
					node = cursor?.nextSibling ?? null;
					continue;
				}

				bindingManager.markHydrated(root);

				const instance = this._buildInstance({
					key, root, itemStart, itemEnd,
					config, component, parentEphemeral,
					subPlan, bindingManager,
					skipCollect: true,
				});

				instancesByKey.set(key, instance);
				node = itemEnd.nextSibling;
			}
		},

		/**
		 * @param {HTMLTemplateElement} templateElement
		 * @returns {{start: Comment, end: Comment}}
		 */
		_findForAnchors(templateElement) {
			/** @type {Comment | null} */
			let start = null;
			/** @type {Comment | null} */
			let end = null;

			let node = templateElement.nextSibling;
			while (node) {
				if (node.nodeType === Node.COMMENT_NODE) {
					if (node.textContent === 'ws-for:start') start = /** @type {Comment} */ (node);
					if (node.textContent === 'ws-for:end') { end = /** @type {Comment} */ (node); break; }
				}
				node = node.nextSibling;
			}

			if (!start || !end) {
				throw new Error('ws-for hydration: missing <!--ws-for:start--> / <!--ws-for:end--> anchors');
			}

			return {start, end};
		},
	}));


/* ===========================	END OF REACTIVE DIRECTIVES =========================== */
/* ===========================	ONE-TIME DIRECTIVES =========================== */
App.classes.directive_registry.getInstance().register({
	matches: name => name.startsWith('ws-on-'),
	removeAttribute: true,
	/** @param {DirectiveInitOptions} options */
	init({element, value, name, component, addBinding, applyMemo, evaluateExpression, makeEventFrame}) {
		const eventName = name.slice(6);
		const handlerName = value.trim();
		const handlerLooksLikeCall = handlerName.endsWith(')');
		const eventFrame = makeEventFrame(element);
		/** @type {Object | null} */
		let lastScope = null;

		const {unbind} = element.addEventListenerWs(eventName, event => {
			const scope = lastScope ?? component.bakeTemplateContext();
			if (handlerLooksLikeCall) {
				eventFrame.fire(event);
				try { evaluateExpression(handlerName, scope, eventFrame); }
				finally { eventFrame.release(); }
				return;
			}
			const handler = App.helper.object.readByPath(scope, handlerName) ?? App.helper.object.readByPath(component, handlerName)?.bind(component);
			if (typeof handler === 'function') handler(event, element, scope);
			else console.warn(`Handler "${handlerName}" not found for ws-on-${eventName}`);
		});

		addBinding({
			update: applyMemo((scope) => scope, (scope) => {
				lastScope = scope;
			}, Object.is),
			dispose() {
				lastScope = null;
				unbind();
			}
		});
	}
});

App.classes.directive_registry.getInstance().register({
	matches: name => name === 'ws-long-press',
	removeAttribute: true,
	/** @param {DirectiveInitOptions} options */
	init({element, value, applyMemo, component, addBinding}) {
		/** @type {ReturnType<typeof setInterval> | undefined} */
		let pressInterval;
		const handlerName = value.trim();
		/** @type {Object | null} */
		let lastScope = null;

		const {unbind: unbindSet} = element.addMultipleEventListener(['mousedown', 'touchstart'], event => {
			clearInterval(pressInterval);
			pressInterval = setInterval(() => {
				const scope = lastScope || component.bakeTemplateContext();
				const handler = scope[handlerName] || component[handlerName]?.bind(component);
				if (typeof handler === 'function') handler(event, element, scope);
				else console.warn(`Handler "${handlerName}" not found for ws-on-long-click`);
			}, 200);
		})
		const {unbind: unbindClear} = element.addMultipleEventListener(['mouseup', 'mouseleave', 'touchend', 'touchcancel'], () => {
			clearInterval(pressInterval);
		});
		addBinding({
			update: applyMemo((scope) => scope, (scope) => {
				lastScope = scope;
			}, Object.is),
			dispose() {
				unbindSet();
				unbindClear();
				clearInterval(pressInterval);
				lastScope = null;
			}
		});
	}
});

App.classes.directive_registry.getInstance().register({
	matches: name => name.startsWith('ws-delegated-on-'),
	removeAttribute: true,
	/** @param {DirectiveInitOptions} options */
	init({element, value, name, component, addBinding, applyMemo}) {
		const eventName = name.slice('ws-delegated-on-'.length);
		const [selector, handlerName] = value.split('|').map(s => s.trim());
		/** @type {Object | null} */
		let lastScope = null;

		/** @param {Event} event */
		const onEvent = (event) => {
			const start = (typeof event.composedPath === 'function' && event.composedPath()[0]) ?? event.target;
			const match = start?.closest?.(selector);
			if (!match || !element.contains(match)) return;

			const scope = lastScope ?? component.bakeTemplateContext();
			const handler = App.helper.object.readByPath(scope, handlerName) || App.helper.object.readByPath(component, handlerName)?.bind(component);

			if (typeof handler === 'function') handler(event, match, scope);
			else console.warn(`Handler "${handlerName}" not found for ws-delegated-on-${eventName}`);
		};

		GlobalEventHub.getInstance().register(element, eventName, onEvent);

		addBinding({
			update: applyMemo((scope) => scope, (scope) => {
				lastScope = scope;
			}, Object.is),
			sleep() {
				GlobalEventHub.getInstance().unregister(element, eventName, onEvent);
			},
			wakeup() {
				GlobalEventHub.getInstance().register(element, eventName, onEvent);
			},
			dispose() {
				lastScope = null;
				GlobalEventHub.getInstance().unregister(element, eventName, onEvent);
			},
		});
	},
});

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-onkey-'),
		removeAttribute: true,
		init({element, name, value, component, addBinding, applyMemo, evaluateExpression, makeEventFrame}) {
			const keyPart = name.slice('ws-onkey-'.length).toLowerCase();
			const handlerName = value.trim();
			const handlerLooksLikeCall = handlerName.endsWith(')');
			const eventFrame = makeEventFrame(element);
			/** @type {Object | null} */
			let lastScope = null;

			/** @param {KeyboardEvent} event */
			const onKeydown = (event) => {
				if (event.key.toLowerCase() !== keyPart) return;

				const scope = lastScope ?? component.bakeTemplateContext();
				if (handlerLooksLikeCall) {
					eventFrame.fire(event);
					try { evaluateExpression(handlerName, scope, eventFrame); }
					finally { eventFrame.release(); }
					return;
				}

				const handler =
					App.helper.object.readByPath(scope, handlerName) ??
					App.helper.object.readByPath(component, handlerName)?.bind(component);

				if (typeof handler === 'function') handler(event, element, scope);
				else console.warn(`Handler "${handlerName}" not found for ws-onkey-${keyPart}`);
			};

			GlobalEventHub.getInstance().register(element, 'keydown', onKeydown);

			addBinding({
				update: applyMemo((scope) => scope, (scope) => {
					lastScope = scope;
				}, Object.is),
				sleep() {
					GlobalEventHub.getInstance().unregister(element, 'keydown', onKeydown);
				},
				wakeup() {
					GlobalEventHub.getInstance().register(element, 'keydown', onKeydown);
				},
				dispose() {
					lastScope = null;
					GlobalEventHub.getInstance().unregister(element, 'keydown', onKeydown);
				},
			});
		},
	})
);
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-click-outside',
		removeAttribute: true,
		init({element, value, component, addBinding, applyMemo, evaluateExpression, makeEventFrame}) {
			const pipeIndex = value.indexOf('|');
			const handlerExpr = (pipeIndex === -1 ? value : value.slice(0, pipeIndex)).trim();
			const conditionExpr = pipeIndex === -1 ? '' : value.slice(pipeIndex + 1).trim();
			const hasCondition = conditionExpr !== '';
			const handlerLooksLikeCall = handlerExpr.endsWith(')');

			if (!handlerExpr) return;

			const eventFrame = makeEventFrame(element);
			/** @type {Object | null} */
			let lastScope = null;
			let lastActive = !hasCondition;
			let registered = false;

			/** @param {PointerEvent} event */
			const onClickOutside = (event) => {
				if (element.contains(event.target)) return;

				const scope = lastScope ?? component.bakeTemplateContext();

				if (handlerLooksLikeCall) {
					eventFrame.fire(event);
					try { evaluateExpression(handlerExpr, scope, eventFrame); }
					finally { eventFrame.release(); }
					return;
				}

				const handler =
					App.helper.object.readByPath(scope, handlerExpr) ??
					App.helper.object.readByPath(component, handlerExpr)?.bind(component);

				if (typeof handler === 'function') handler(event, element, scope);
				else console.warn(`Handler "${handlerExpr}" not found for ws-click-outside`);
			};

			const attach = () => {
				if (registered) return;
				GlobalEventHub.getInstance().register(document, 'pointerdown', onClickOutside, true);
				registered = true;
			};

			const detach = () => {
				if (!registered) return;
				GlobalEventHub.getInstance().unregister(document, 'pointerdown', onClickOutside, true);
				registered = false;
			};

			if (!hasCondition) attach();

			addBinding({
				update: applyMemo(
					(scope, frame) => {
						lastScope = scope;
						return hasCondition ? !!evaluateExpression(conditionExpr, scope, frame) : true;
					},
					(active) => {
						lastActive = active;
						active ? attach() : detach();
					},
					Object.is
				),
				sleep: detach,
				wakeup() { if (lastActive) attach(); },
				dispose() {
					lastScope = null;
					detach();
				},
			});
		},
	})
);
/* ===========================	END OF ONE-TIME DIRECTIVES =========================== */



/**
 * @typedef {Object} DirectiveDefinition
 * @property {(attributeName: string) => boolean} matches
 *           — predicate; receives normalized name (e.g. `'ws-text'`) and returns `true` to claim the attribute
 * @property {boolean} [removeAttribute=true]
 *           — when `true` (default), the matched attribute is removed from the DOM after `init()`
 * @property {boolean} [controlsSubtree]
 *           — when `true`, child-element walking is skipped; the directive manages descendant bindings itself (e.g. `ws-html`)
 * @property {boolean} [controlsElement]
 *           — when `true`, this directive is the sole owner of the element; no other directives execute on it (e.g. `ws-if`, `ws-for`)
 * @property {(element: Element) => Element | null} [getTemplateNode]
 *           — for `controlsElement` directives: return a detached clone suitable for precompiled binding plan generation
 * @property {(root: Element) => void} [normalizeHydration]
 *           — SSR hydration pre-pass hook; called once per root before binding collection
 * @property {(options: DirectiveInitOptions) => void} init
 *           — set up the directive: create bindings, attach event listeners, configure DOM
 */

/**
 * @typedef {Object} DirectiveInitOptions
 * @property {Element}                element          — the DOM element the directive is bound to
 * @property {RenderableComponent}    component        — the host component instance
 * @property {string}                 name             — normalized directive name (e.g. `'ws-text'`, `'ws-attr-href'`)
 * @property {string}                 value            — raw attribute value (expression string)
 * @property {AddBindingFn}           addBinding       — register a binding; `label` is auto-set to `name` before forwarding
 * @property {EvaluateExpressionFn}   evaluateExpression — compile+eval a JS expression string against a context object
 * @property {ApplyMemoFn}            applyMemo        — create a memoized update function with optional custom equality
 * @property {BindingManager}         bindingManager   — the host's binding manager for sub-collection/disposal
 * @property {EphemeralScope | null}  ephemeral        — `ws-for` iteration scope, or `null` at component root
 * @property {PlanEntry[] | null}     [subPlan]        — precompiled sub-plan for `controlsElement` directives; `null` when running in walk mode
 */

/**
 * @callback EvaluateExpressionFn
 * @param {string}          expression — JS expression string (e.g. `'item.name'`, `'count > 0'`, `'onClick($event)'`)
 * @param {Object<string,*>} context   — scope context object; expression variables resolve against this
 * @returns {*}                          evaluated result
 */

/**
 * @callback ApplyMemoFn
 * @template T
 * @param {(context: Object<string,*>) => T}            evaluate — extract a value from context each cycle
 * @param {(value: T, context: Object<string,*>) => void} apply  — side-effect to run when value changes
 * @param {((a: T, b: T) => boolean)}                   [areEqual=Object.is] — custom equality; return `true` to skip `apply`
 * @returns {(context: Object<string,*>) => void}          memoized update function suitable as `Binding.update`
 */

/**
 * @typedef {Object} PlanDirective
 * @property {string}              name     — normalized directive name (e.g. `'ws-text'`)
 * @property {string}              attrName — original attribute name as it appeared in DOM (e.g. `'ws-host-text'`)
 * @property {string}              value    — raw attribute value (expression string)
 * @property {DirectiveDefinition} def      — reference to the matched directive definition
 */

/**
 * @typedef {Object} PlanEntry
 * @property {number[]}          path       — child-index path from root: `root.children[path[0]].children[path[1]]...`
 * @property {PlanDirective[]}   directives — all directives found on the target node
 * @property {PlanEntry[] | null} subPlan   — nested plan for `controlsElement` directives with `getTemplateNode`; `null` otherwise
 */

/**
 * @typedef {Object} ProcessElementResult
 * @property {boolean} shouldSkipChildren — `true` if a `controlsSubtree` directive claimed the element
 * @property {boolean} didOwnElement      — `true` if a `controlsElement` directive claimed the element
 */

/**
 * @typedef {Object} ProcessElementOptions
 * @property {Element}              element                      — DOM element to process
 * @property {RenderableComponent}  component                    — host component
 * @property {AddBindingFn}         addBinding                   — binding registration callback
 * @property {string}               [customPrefix='ws-']         — attribute prefix to scan (e.g. `'ws-host-'` for root element)
 * @property {EphemeralScope | null} [ephemeral=null]            — iteration scope for `ws-for` children
 * @property {BindingManager}       [bindingManager=component.bindingManager] — binding manager instance
 */

/**
 * @typedef {Object} ProcessElementFromPlanOptions
 * @property {PlanEntry}            entry          — precompiled plan entry with path-resolved node
 * @property {Element}              node           — the resolved DOM element
 * @property {RenderableComponent}  component      — host component
 * @property {AddBindingFn}         addBinding     — binding registration callback
 * @property {EphemeralScope | null} ephemeral     — iteration scope
 * @property {BindingManager}       bindingManager — binding manager instance
 */

/**
 * @typedef {Object} WsForConfig
 * @property {string}      itemVarName    — loop item variable name (default `'item'`)
 * @property {string}      indexVarName   — loop index variable name (default `'index'`)
 * @property {string}      listExpression — expression string evaluated to produce the iterable
 * @property {string|null} keyExpression  — expression for stable key extraction; `null` = use index as key
 */

/**
 * @typedef {Object} WsForInstance
 * @property {string}           key         — stable string key for this iteration
 * @property {Comment}          start       — `<!--ws-for:item:{key}:start-->` anchor
 * @property {Comment}          end         — `<!--ws-for:item:{key}:end-->` anchor
 * @property {Element}          root        — the cloned template root element
 * @property {EphemeralScope}   ephemeral   — scoped context for this iteration (prototype-linked to parent)
 * @property {Object}           loop        — `{index, key, first, last, length}` metadata object exposed as `loop` in template
 * @property {*}                item        — current item value from the source array/object
 * @property {number}           index       — current zero-based index
 * @property {number}           total       — total number of items in this render cycle
 * @property {() => void}       syncContext — push `item`/`index`/`loop` into ephemeral scope and mark dirty if changed
 * @property {ComponentChannel} provider    — per-instance provider for `item`/`index`/`loop` to child components
 * @property {OwnedResources}   owned       — lifetime resources (channel, bindings, ephemeral disposal)
 * @property {boolean}          [_needsCollect] — `true` for hydrated instances awaiting first `collectDeferred()`
 * @property {number}           [_seenTick]     — render-tick counter for stale-instance detection
 */
;class LifecycleEngine {
	/**
	 * @param {Object}              options
	 * @param {() => boolean}       options.isConnected
	 * @param {() => HTMLElement}   options.getHost
	 * @param {LifecycleHub}        options.hub
	 * @param {number}              [options.traceLimit=30]
	 */
	constructor({
					isConnected,
					getHost,
					hub,
					traceLimit = 30,
				}) {
		/** @type {() => boolean} */
		this._isConnected = isConnected;

		/** @type {() => HTMLElement} */
		this._getHost = getHost;

		/** @type {LifecycleHub} */
		this._hub = hub;

		/** @type {LifecycleState} */
		this._state = /** @type {LifecycleState} */ ('constructed');
		this._hub.emit('constructed');

		/** @type {boolean} */
		this._mountedOnce = false;

		/** @type {boolean} */
		this._dirty = false;

		/** @type {Set<string>} */
		this._reasons = new Set();

		/** @type {number} */
		this._invalidations = 0;

		/** @type {boolean} */
		this._flushing = false;

		/** @type {TraceEntry[]} */
		this._trace = [];

		/** @type {number} */
		this._traceLimit = traceLimit;
	}

	/** @returns {void} */
	onConnected() {
		if (this._isState('connected') || this._isState('mounted')) {
			return;
		}
		this._setState('connected');
		this._hub.emit('connected');
		this.invalidate('connected', LifecycleInvalidations.NONE);
	}

	/** @returns {void} */
	onDisconnected() {
		if (this._isState('disconnected')) {
			return;
		}

		if (this._mountedOnce) {
			this._hub.emit('unmount:before');
			this._hub.emit('unmount');
			this._hub.emit('unmount:after');
		}

		this._dirty = false;
		this._reasons.clear();
		this._invalidations = 0;

		this._setState('disconnected');
		this._hub.emit('disconnected');
	}

	/** @returns {void} */
	onRemove() {
		this._hub.emit('remove');
	}

	/**
	 * @param {string} [reason='unknown']
	 * @param {number} [invalidationMask=0] — bitmask of `LifecycleInvalidations` flags
	 * @returns {void}
	 */
	invalidate(reason = 'unknown', invalidationMask = 0) {
		if (this._isState('disconnected')) {
			return;
		}

		this._dirty = true;
		this._reasons.add(reason);
		this._invalidations |= invalidationMask;

		this._pushTrace(reason);
		ComponentsScheduler.getInstance().markDirty(this, reason);
	}

	/** @returns {void} */
	flush() {
		if (!this._isConnected()) {
			this._dirty = false;
			this._reasons.clear();
			this._invalidations = 0;
			return;
		}
		if (this._flushing) return;
		if (!this._dirty) return;

		const reasons = Array.from(this._reasons);
		const invalidations = this._invalidations;

		this._dirty = false;
		this._reasons.clear();
		this._invalidations = 0;

		try {
			this._flushing = true;

			if (!this._mountedOnce) {
				this._hub.emit('mount:before', {reasons});
				this._hub.emit('mount', {reasons});
				this._mountedOnce = true;
				this._setState('mounted');
				this._hub.emit('mount:after', {reasons});
				return;
			}

			this._hub.emit('update:before', {reasons, invalidations});
			this._hub.emit('update', {reasons, invalidations});
			this._hub.emit('update:after', {reasons, invalidations});
		} finally {
			this._flushing = false;
		}
	}

	/**
	 * @param {number} flushesInTurn
	 * @param {number} maxFlushesPerTurn
	 * @returns {void}
	 */
	onBurstLimitReached(flushesInTurn, maxFlushesPerTurn) {
		/** @type {LifecycleBlockedPayload} */
		const info = {
			type: 'burst-limit',
			flushesInTurn,
			maxFlushesPerTurn,
			trace: this.getTrace(),
		};
		this._hub.emit('blocked', info);
	}

	/** @returns {HTMLElement} */
	getHost() {
		return this._getHost();
	}

	/** @returns {TraceEntry[]} */
	getTrace() {
		return this._trace.slice();
	}

	/** @returns {LifecycleState} */
	getState() {
		return this._state;
	}

	/** @returns {boolean} */
	isInitialized() {
		return this._mountedOnce;
	}

	/**
	 * @param {string} reason
	 * @returns {void}
	 */
	_pushTrace(reason) {
		this._trace.push({at: performance.now(), reason});
		if (this._trace.length > this._traceLimit) this._trace.shift();
	}

	/**
	 * @param {LifecycleState} newState
	 * @returns {void}
	 */
	_setState(newState) {
		this._state = newState;
	}

	/**
	 * @param {LifecycleState} state
	 * @returns {boolean}
	 */
	_isState(state) {
		return this._state === state;
	}
}


class LifecycleHub {
	constructor() {
		/** @type {Map<string, Set<LifecycleHandler>>} */
		this._handlersByPhase = new Map();
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void} unsubscribe function
	 */
	on(phase, handler) {
		let handlers = this._handlersByPhase.get(phase);
		if (!handlers) this._handlersByPhase.set(phase, (handlers = new Set()));
		handlers.add(handler);
		return () => this.off(phase, handler);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void} unsubscribe function
	 */
	once(phase, handler) {
		const unsubscribe = this.on(phase, (...args) => {
			unsubscribe();
			handler(...args);
		});
		return unsubscribe;
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {void}
	 */
	off(phase, handler) {
		const handlers = this._handlersByPhase.get(phase);
		if (!handlers) return;
		handlers.delete(handler);
		if (handlers.size === 0) this._handlersByPhase.delete(phase);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecyclePayload} [payload]
	 * @returns {void}
	 */
	emit(phase, payload) {
		const handlers = this._handlersByPhase.get(phase);
		if (!handlers || handlers.size === 0) return;

		for (const handler of Array.from(handlers)) {
			handler(payload);
		}
	}

	/** @returns {void} */
	clear() {
		this._handlersByPhase.clear();
	}
}

/**
 * Bitmask flags for selective invalidation of component subsystems.
 * Combine with bitwise OR: `LifecycleInvalidations.PROPS | LifecycleInvalidations.ATTRS`
 *
 * @readonly
 * @enum {number}
 */
const LifecycleInvalidations = Object.freeze({
	/** No subsystem invalidated; triggers a flush with zero invalidation bits */
	NONE:     0,
	/** State props changed via `PropsManager` */
	PROPS:    1 << 0,   // 1
	/** DOM attributes changed (observed or runtime-observed) */
	ATTRS:    1 << 1,   // 2
	/** Slot content was replaced or appended */
	SLOTS:    1 << 2,   // 4
	/** A `provided` value changed upstream */
	PROVIDED: 1 << 3,   // 8
	/** Binding topology changed (e.g. `ws-html` rebuilt subtree); forces full re-collect */
	BINDINGS: 1 << 4,   // 16
	/** Bypass dirty-key optimization — force-update every binding */
	FORCE:    1 << 30,  // 1073741824
});

class ComponentsScheduler extends SingletonMixin() {

	/**
	 * @param {Object} [options]
	 * @param {number}  [options.maxFlushesPerTurn=25] — per-engine safety limit within one macro-turn
	 * @param {number}  [options.yieldEvery=200]       — yield to main thread every N flushes via `setTimeout(0)`
	 */
	constructor({
					maxFlushesPerTurn = 25,
					yieldEvery = 200,
				} = {}) {
		super();

		/** @type {number} */
		this._maxFlushesPerTurn = maxFlushesPerTurn;

		/** @type {number} */
		this._yieldEvery = yieldEvery;

		/** @type {Set<LifecycleEngine>} */
		this._dirtyEngines = new Set();

		/** @type {Map<LifecycleEngine, number>} */
		this._flushesInTurn = new Map();

		/** @type {boolean} */
		this._scheduled = false;

		/** @type {boolean} */
		this._turnResetScheduled = false;

		/** @type {Set<WeakRef<AfterFlushCallback>>} */
		this._afterFlushHooks = new Set();
	}

	/**
	 * @param {LifecycleEngine} engine
	 * @param {string}          reason
	 * @returns {void}
	 */
	markDirty(engine, reason) {
		this._dirtyEngines.add(engine);
		this._scheduleFlush(reason);
	}

	/**
	 * @param {AfterFlushCallback} callback
	 * @returns {() => void} unsubscribe function
	 */
	onAfterFlush(callback) {
		const ref = new WeakRef(callback);
		this._afterFlushHooks.add(ref);
		return () => this._afterFlushHooks.delete(ref);
	}

	/**
	 * @param {string} _reason
	 * @returns {void}
	 */
	_scheduleFlush(_reason) {
		this._scheduleTurnResetOnce();

		if (this._scheduled) return;
		this._scheduled = true;

		queueMicrotask(() => {
			this._scheduled = false;
			this._flushAll();
		});
	}

	/** @returns {Promise<void>} */
	async _flushAll() {
		if (this._dirtyEngines.size === 0) return;

		const sorted = this._sortByDomOrder(Array.from(this._dirtyEngines));
		this._dirtyEngines.clear();

		for (let i = 0; i < sorted.length; i++) {
			const engine = sorted[i];
			const flushes = (this._flushesInTurn.get(engine) || 0) + 1;
			this._flushesInTurn.set(engine, flushes);

			if (flushes > this._maxFlushesPerTurn) {
				engine.onBurstLimitReached(flushes, this._maxFlushesPerTurn);
				continue;
			}

			engine.flush();

			if (i % this._yieldEvery === 0) await new Promise(resolve => setTimeout(resolve, 0));
		}

		this._notifyAfterFlushHooks({ engines: sorted, duration: 0 });
	}

	/**
	 * @param {AfterFlushPayload} payload
	 * @returns {void}
	 */
	_notifyAfterFlushHooks(payload) {
		if (this._afterFlushHooks.size === 0) return;

		for (const ref of this._afterFlushHooks) {
			const callback = ref.deref();
			if (!callback) {
				this._afterFlushHooks.delete(ref);
				continue;
			}
			try {
				callback(payload);
			} catch (error) {
				console.error('[ComponentsScheduler] afterFlush hook error', error);
			}
		}
	}

	/**
	 * @param {LifecycleEngine[]} engines
	 * @returns {LifecycleEngine[]}
	 */
	_sortByDomOrder(engines) {
		return engines.sort((a, b) => {
			const hostA = a.getHost();
			const hostB = b.getHost();
			if (!hostA || !hostB) return 0;

			const position = hostA.compareDocumentPosition(hostB);

			if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
			if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
			return 0;
		});
	}

	/** @returns {void} */
	_scheduleTurnResetOnce() {
		if (this._turnResetScheduled) return;
		this._turnResetScheduled = true;

		setTimeout(() => {
			this._flushesInTurn.clear();
			this._turnResetScheduled = false;
		}, 0);
	}
}

App.classes.components_scheduler = ComponentsScheduler;

App.classes.components.lifecycle_engine = LifecycleEngine;
App.classes.components.lifecycle_hub = LifecycleHub;
App.classes.components.lifecycle_invalidations = LifecycleInvalidations;

/**
 * @typedef {'constructed' | 'connected' | 'mounted' | 'disconnected'} LifecycleState
 */

/**
 * @typedef {'constructed'
 *   | 'connected'
 *   | 'disconnected'
 *   | 'mount:before'
 *   | 'mount'
 *   | 'mount:after'
 *   | 'update:before'
 *   | 'update'
 *   | 'update:after'
 *   | 'unmount:before'
 *   | 'unmount'
 *   | 'unmount:after'
 *   | 'remove'
 *   | 'blocked'
 * } LifecyclePhase
 */

/**
 * @typedef {Object} LifecycleMountPayload
 * @property {string[]} reasons
 */

/**
 * @typedef {Object} LifecycleUpdatePayload
 * @property {string[]} reasons
 * @property {number}   invalidations
 */

/**
 * @typedef {Object} LifecycleBlockedPayload
 * @property {'burst-limit'}  type
 * @property {number}         flushesInTurn
 * @property {number}         maxFlushesPerTurn
 * @property {TraceEntry[]}   trace
 */

/**
 * @typedef {LifecycleMountPayload | LifecycleUpdatePayload | LifecycleBlockedPayload | undefined} LifecyclePayload
 */

/**
 * @callback LifecycleHandler
 * @param {LifecyclePayload} [payload]
 * @returns {void}
 */

/**
 * @typedef {Object} LifecycleRegister
 * @property {(phase: LifecyclePhase, handler: LifecycleHandler) => (() => void)} on
 * @property {(phase: LifecyclePhase, handler: LifecycleHandler) => (() => void)} once
 * @property {(reason: string, invalidationMask?: number) => void} invalidate
 */

/**
 * @callback LifecycleLayer
 * @this RenderableComponent
 * @param {LifecycleRegister} register
 * @returns {void}
 */

/**
 * @typedef {Object} AfterFlushPayload
 * @property {LifecycleEngine[]} engines
 * @property {number}            duration
 */

/**
 * @callback AfterFlushCallback
 * @param {AfterFlushPayload} payload
 * @returns {void}
 */

/**
 * @typedef {Object} TraceEntry
 * @property {number} at
 * @property {string} reason
 */
;class OwnedResources {
	/**
	 * @param {string} [label]
	 */
	constructor(label = '') {
		/** @type {string} */
		this._label = label;

		/** @type {DisposerEntry[]} */
		this._disposers = [];

		/** @type {boolean} */
		this._released = false;
	}

	/**
	 * @param {string}     label
	 * @param {() => void} disposer
	 * @returns {void}
	 */
	add(label, disposer) {
		if (typeof disposer !== 'function') {
			console.warn(`[OwnedResources:${this._label}] add("${label}") — disposer is not a function`);
			return;
		}

		if (this._released) {
			try { disposer(); } catch (e) { console.error(`[OwnedResources:${this._label}] disposer "${label}" threw after release`, e); }
			return;
		}

		this._disposers.push({label, disposer});
	}

	/** @returns {void} */
	release() {
		if (this._released) return;
		this._released = true;

		for (let i = this._disposers.length - 1; i >= 0; i--) {
			const {label, disposer} = this._disposers[i];
			try {
				disposer();
			} catch (e) {
				console.error(`[OwnedResources:${this._label}] disposer "${label}" threw`, e);
			}
		}

		this._disposers = [];
	}

	/** @returns {string[]} */
	inspect() {
		if (this._released) return [`[released]`];
		return this._disposers.map(d => d.label);
	}

	/** @returns {boolean} */
	get isReleased() {
		return this._released;
	}
}

App.classes.owned_resources = OwnedResources;

/**
 * @typedef {Object} DisposerEntry
 * @property {string}     label    — human-readable identifier for debug/inspect output
 * @property {() => void} disposer — teardown callback; executed in reverse-registration order on `release()`
 */
;App.classes = App.classes || {};

class PropsManager {
	/**
	 * @param {RenderableComponent & HTMLElement} component
	 * @param {Object}                            [options]
	 * @param {PropAttrReader}                    [options.readAttr]
	 * @param {PropInvalidator}                   [options.invalidateComponent]
	 */
	constructor(component, {readAttr, invalidateComponent} = {}) {
		/** @type {RenderableComponent & HTMLElement} */
		this._component = component;

		/** @type {PropAttrReader} */
		this._readAttr = readAttr || ((name, fallback) => {
			return component.hasAttribute(name) ? component.getAttribute(name) : fallback;
		});

		/** @type {PropInvalidator} */
		this._invalidateComponent = invalidateComponent;

		/** @type {boolean} */
		this._isInitialized = false;

		/** @type {Object<string, *>} */
		this._stateProps = {};
	}

	/** @returns {void} */
	initOnce() {
		if (this._isInitialized) return;
		this._isInitialized = true;

		this._stateProps = this._readInitialJsonPropsFromDom() || {};
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	get(name, fallback = null) {
		if (this._component.hasAttribute(name)) {
			return this._readAttr(name, fallback);
		}

		if (Object.prototype.hasOwnProperty.call(this._stateProps, name)) {
			return this._stateProps[name];
		}

		return fallback;
	}

	/**
	 * @param {string}  name
	 * @param {boolean} [fallback=false]
	 * @returns {boolean}
	 */
	getBool(name, fallback = false) {
		const raw = this.get(name, null);
		if (raw == null) return fallback;
		return App.helper.parseBoolean(raw);
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	getInt(name, fallback = 0) {
		const raw = this.get(name, null);
		if (raw == null || raw === '') return fallback;
		const parsed = parseInt(String(raw), 10);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	getFloat(name, fallback = 0) {
		const raw = this.get(name, null);
		if (raw == null || raw === '') return fallback;
		const parsed = parseFloat(String(raw));
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	getJson(name, fallback = null) {
		const value = this.get(name, null);
		if (value && typeof value === 'object') return value;
		if (value == null || value === '') return fallback;
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (!trimmed) return fallback;

			try {
				return JSON.parse(trimmed);
			} catch (_) {
				return fallback;
			}
		}

		return fallback;
	}

	/** @returns {Object<string, *>} */
	getAll() {
		return this._stateProps;
	}

	/**
	 * @param {string}         name
	 * @param {*}              value
	 * @param {SetPropOptions} [options]
	 * @returns {void}
	 */
	set(name, value, options = {}) {
		const {to = 'auto', silent = false} = options;

		/** @type {PropTarget} */
		const target = (to === 'auto')
			? this._chooseTargetForName(name)
			: to;

		if (target === 'attr') {
			this._writeToAttribute(name, value);
			return;
		}

		const oldValue = this._stateProps[name];
		this._stateProps[name] = value;

		if (!silent && oldValue !== value) {
			this._invalidateComponent(`props:set:${name}`);
		}
	}

	/**
	 * @param {Object<string, *>} nextStateProps
	 * @param {string}            [reason='replaceState']
	 * @returns {void}
	 */
	replaceState(nextStateProps, reason = 'replaceState') {
		this._stateProps = App.helper.object.sanitize(nextStateProps);
		this._invalidateComponent(`props:${reason}`);
	}

	/**
	 * @param {Object<string, *>} partialStateProps
	 * @param {string}            [reason='assignState']
	 * @returns {void}
	 */
	assignState(partialStateProps, reason = 'assignState') {
		if (!App.helper.object.is_plain(partialStateProps)) return;
		Object.assign(this._stateProps, partialStateProps);
		this._invalidateComponent(`props:${reason}`);
	}

	/**
	 * @param {Object<string, *>} partial
	 * @returns {boolean} — `true` if at least one value changed (`!==` comparison)
	 */
	mergeState(partial) {
		let changed = false;
		for (const key in partial) {
			const next = partial[key];
			if (this.get(key) !== next) {
				this.set(key, next);
				changed = true;
			}
		}
		return changed;
	}

	/**
	 * @param {string} path
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	getStatePath(path, fallback = null) {
		return App.helper.object.readByPath(this._stateProps, path, fallback);
	}

	/**
	 * @param {string} path
	 * @param {*}      value
	 * @param {string} [reason='setStatePath']
	 * @returns {void}
	 */
	setStatePath(path, value, reason = 'setStatePath') {
		App.helper.object.setByPath(this._stateProps, path, value);
		this._invalidateComponent(`props:${reason}`);
	}

	/**
	 * @param {string} name
	 * @returns {PropTarget}
	 */
	_chooseTargetForName(name) {
		if (this._component.constructor.observedAttributes?.includes(name) || this._component.hasAttribute(name)) {
			return 'attr';
		}

		return 'data';
	}

	/**
	 * @param {string} name
	 * @param {*}      value
	 * @returns {void}
	 */
	_writeToAttribute(name, value) {
		if (value == null || value === false) {
			this._component.removeAttribute(name);
			return;
		}

		if (value === true) {
			this._component.setAttribute(name, 'true');
			return;
		}

		this._component.setAttribute(name, String(value));
	}

	/**
	 * @returns {Object<string, *>}
	 */
	_readInitialJsonPropsFromDom() {
		const scriptNode = this._component.querySelector(':scope > script[data-ws-props]');
		if (!scriptNode) return {};

		const rawJson = (scriptNode.textContent || '').trim();
		scriptNode.remove();

		if (!rawJson) return {};

		try {
			const parsed = JSON.parse(rawJson);
			return App.helper.object.sanitize(parsed);
		} catch (error) {
			console.error('Invalid ws-props JSON payload in component', this._component, error);
			return {};
		}
	}
}

App.classes.props_manager = PropsManager;

/**
 * @typedef {Object} SetPropOptions
 * @property {PropTarget} [to='auto']   — routing strategy: `'auto'` picks `attr` if the attribute exists
 *                                        or is in `observedAttributes`, otherwise `'data'`
 * @property {boolean}    [silent=false] — when `true`, skip invalidation even if the value changed
 */

/**
 * @typedef {'auto' | 'attr' | 'data'} PropTarget
 * `'attr'`  — write to DOM attribute via `setAttribute`/`removeAttribute`
 * `'data'`  — write to internal `_stateProps` object
 * `'auto'`  — resolved at runtime: `'attr'` if attribute exists or is in `observedAttributes`, else `'data'`
 */

/**
 * @callback PropAttrReader
 * @param {string} name     — attribute name
 * @param {*}      fallback — value to return when the attribute is absent
 * @returns {*}               attribute value (possibly transformed by `AttributeObserver` modifiers)
 */

/**
 * @callback PropInvalidator
 * @param {string} reason — invalidation reason string (e.g. `'props:set:title'`, `'props:replaceState'`)
 * @returns {void}
 */
;App.classes = App.classes || {};

class RenderableComponent extends HasRefsMixin(HasEventCallbacksMixin(HTMLElement)) {

	/**
	 * @type {{
	 *   owned: OwnedResources | null,
	 *   scope: ComponentScope | null,
	 *   slotManager: SlotManager | null,
	 *   bindingManager: BindingManager | null,
	 *   deepProvider: DeepProvider | null,
	 *   propsManager: PropsManager | null,
	 *   attributeObserver: AttributeObserver | null,
	 *   lifecycle: LifecycleHub | null,
	 *   lifecycleEngine: LifecycleEngine | null,
	 * }}
	 */
	_m = {
		owned: null,
		scope: null,
		slotManager: null,
		bindingManager: null,
		deepProvider: null,
		propsManager: null,
		attributeObserver: null,
		lifecycle: null,
		lifecycleEngine: null,
	}


	constructor() {
		super();
		this._attachLifecycleCoreHandlers();
		this._attachLifecycleSubclassHandlers();
	}


	/** @returns {void} */
	connectedCallback() {
		this.lifecycleEngine.onConnected();
	}

	/** @returns {void} */
	disconnectedCallback() {
		this.lifecycleEngine.onDisconnected();
	}

	/** @returns {void} */
	remove() {
		super.remove();
		this.lifecycleEngine.onRemove();
	}

	/** @returns {void} */
	_attachLifecycleCoreHandlers() {

		this.useConnection('wakeUpSleepBindings', {
			connect:  () => this.bindingManager.wakeupAll(),
			disconnect: () => this.bindingManager.sleepAll()
		})

		this.onceLifecycle('connected', () => {
			this.owned.add('bindingManager', () => this.bindingManager.disposeAll());
			this.owned.add('deepProvider', () => this.deepProvider.dispose());
			this.owned.add('scope', () => this.scope.dispose());
			this.owned.add('lifecycle', () => this.lifecycle.clear());

			this.propsManager.initOnce();
			this.attributeObserver.parseObserveAttributesAttribute();
			this.provideMany(this.provides());
		});

		this.onceLifecycle('mount', () => {
			this._initScope();

			if (this.hasAttribute('ws-server')) {
				this._hydrateDOM();
			} else {
				this._renderDOM();
			}

			this._applyBindings(null, true);
			this.deepProvider.notifySubscribers();
		});

		this.onLifecycle('update', ({invalidations}) => {
			const dirtyKeys = this._computeDirtyKeys(invalidations);
			this._applyBindings(dirtyKeys);
			this.deepProvider.notifySubscribers();
		});
		this.onLifecycle('unmount', () => {
			this.bindingManager.sleepAll();
			this.deepProvider.disposeConsumers();
		});
		this.onLifecycle('remove', () => {
			this.owned.release();
		});
	}

	/** @returns {void} */
	_initScope() {
		this.scope.addStaticSource('base', () => this._baseTemplateContext());
		this.scope.addSource('props', () => this.propsManager.getAll());
		this.scope.addSource('template', () => this.templateContext());
		this.scope.setStatic('provided', this.deepProvider.getProvidedProxy(
			() => this.invalidateLifecycle('providedChanged', LifecycleInvalidations.PROVIDED)
		));
		this.scope.update();
	}

	/**
	 * @param {number} invalidations
	 * @returns {Set<string> | null}
	 */
	_computeDirtyKeys(invalidations) {
		if (invalidations & LifecycleInvalidations.FORCE) return null;
		if (invalidations & LifecycleInvalidations.BINDINGS) return null;

		const changed = this.scope.update();
		if (invalidations & LifecycleInvalidations.PROVIDED) changed.add('provided');

		return changed;
	}

	/**
	 * @param {Set<string> | null} [dirtyKeys=null]
	 * @param {boolean}            [skipTracking=false]
	 * @returns {void}
	 */
	_applyBindings(dirtyKeys = null, skipTracking = false) {
		this.bindingManager.updateAll(this.scope.context, dirtyKeys, skipTracking);
	}

	/** @returns {void} */
	_renderDOM() {
		this.slotManager.captureOriginalChildren();
		const rawTemplate = this.template();
		/** @type {Element} */
		let selfElement;
		if (this.constructor.precompile) {
			selfElement = this._resolveTemplateNode(rawTemplate, this.scope.context);
		} else {
			selfElement = Node.fromSingle(App.helper.template.fill(rawTemplate, this.scope.context).trim());
		}
		if (!selfElement) throw new Error('Template must contain <self> as root');

		App.helper.dom.merge_attributes(this, selfElement);

		const fragment = document.createDocumentFragment();
		while (selfElement.firstChild) fragment.append(selfElement.firstChild);
		this.replaceChildren(fragment);

		this.slotManager.mountSlots();
		this.slotManager.read().length > 0 && this.scope.update();

		if (this.constructor.precompile) {
			this.bindingManager.executePlan(this._getOrBuildPlan(), this);
			for (const [, {start, end}] of this.slotManager.anchors()) {
				this.bindingManager.collectInRange(start, end);
			}
		} else {
			this.bindingManager.rebuildAll();
		}
	}

	/** @returns {void} */
	_hydrateDOM() {
		this.slotManager.hydrateSlots();
		if (this.constructor.precompile) {
			App.classes.directive_registry.getInstance().normalizeForHydration(this);
			this.bindingManager.executePlan(this._getOrBuildPlan(), this);
			for (const [, {start, end}] of this.slotManager.anchors()) {
				this.bindingManager.collectInRange(start, end);
			}
		} else {
			this.bindingManager.rebuildAll();
		}

		this.removeAttribute('ws-server');
	}

	/**
	 * @param {string} rawTemplate
	 * @param {Object} context
	 * @returns {Element}
	 */
	_resolveTemplateNode(rawTemplate, context) {
		const cache = this.constructor._templateCache ??= {deps: null, nodes: new Map()};

		if (!cache.deps) {
			/** @type {Set<string>} */
			const deps = new Set();
			const tracked = new Proxy(context, {
				get(target, key) {
					if (typeof key === 'string') deps.add(key);
					return Reflect.get(target, key);
				}
			});

			const rendered = App.helper.template.fill(rawTemplate, tracked).trim();
			const node = Node.fromSingle(rendered);

			cache.deps = deps;
			cache.nodes.set(this._templateCacheKey(context, deps), node);
			return node.cloneNode(true);
		}

		const key = this._templateCacheKey(context, cache.deps);
		let node = cache.nodes.get(key);

		if (!node) {
			node = Node.fromSingle(App.helper.template.fill(rawTemplate, context).trim());
			cache.nodes.set(key, node);
		}

		return node.cloneNode(true);
	}

	/**
	 * @param {Object}      context
	 * @param {Set<string>} deps
	 * @returns {string}
	 */
	_templateCacheKey(context, deps) {
		if (this.constructor.templateVariantKey) {
			return String(this.constructor.templateVariantKey(context));
		}

		let key = '';
		deps.forEach(dep => {
			key += `${dep}:${context[dep]}\x00`;
		});
		return key;
	}

	/** @returns {PlanEntry[]} */
	_getOrBuildPlan() {
		const ctor = this.constructor;
		/** @type {Map<string, PlanEntry[]>} */
		const plans = ctor._bindingPlans ??= new Map();
		const raw = this.template();

		if (!ctor._templateCache) {
			this._resolveTemplateNode(raw, this.scope.context);
		}

		const key = this._templateCacheKey(this.scope.context, ctor._templateCache.deps);

		if (!plans.has(key)) {
			const node = this._resolveTemplateNode(raw, this.scope.context);
			plans.set(key, App.classes.directive_registry.getInstance()
				.buildBindingPlan(node, 'ws-host-', 'ws-'));
		}
		return plans.get(key);
	}

	/** @returns {void} */
	_attachLifecycleSubclassHandlers() {
		/** @type {LifecycleRegister} */
		const register = {
			on: (phase, handler) => this.onLifecycle(phase, handler),
			once: (phase, handler) => this.onceLifecycle(phase, handler),
			invalidate: (reason, mask) => this.invalidateLifecycle(reason, mask),
		};
		/** @type {Function[]} */
		const constructors = [];
		let current = this.constructor;
		while (current && current !== RenderableComponent) {
			constructors.push(current);
			current = Object.getPrototypeOf(current);
		}
		constructors.reverse();

		for (const ctor of constructors) {
			const lifecycleFactory = ctor.defineLifecycle;
			if (typeof lifecycleFactory !== 'function') continue;
			/** @type {LifecycleLayer | Object<string, LifecycleHandler>} */
			const layer = lifecycleFactory.call(ctor);
			if (typeof layer === 'function') {
				layer.call(this, register);
				continue;
			}
			if (layer && typeof layer === 'object') {
				for (const [phase, handler] of Object.entries(layer)) {
					register.on(phase, handler.bind(this));
				}
			}
		}
	}


	/**
	 * @param {string} label
	 * @param {((this: RenderableComponent) => (() => void)) | ConnectionHandler} handler
	 * @returns {void}
	 */
	useConnection(label, handler) {
		/** @type {(() => void) | null} */
		let teardown = null;

		const isObject = typeof handler === 'object';
		const connectFn = isObject ? handler.connect : handler;
		const disconnectFn = isObject ? handler.disconnect : null;

		const activate = () => {
			teardown = connectFn.call(this);
		};

		const deactivate = () => {
			if (disconnectFn) {
				disconnectFn.call(this);
			} else if (teardown) {
				teardown();
			}
			teardown = null;
		};

		this.onLifecycle('connected', activate);
		this.onLifecycle('disconnected', deactivate);
		this.owned.add(label, deactivate);
		this.isConnected && activate();
	}


	/**
	 * @param {string}          label
	 * @param {Store}           store
	 * @param {UseStoreOptions} [options]
	 * @returns {void}
	 */
	useStore(label, store, options = {}) {
		const {scope, select, sync, invalidate} = options;
		const sourceKey = `store:${label}`;

		if (!select && !sync) {
			/**
			 * @param {ReactiveStatePendingMap} changeset
			 * @returns {void}
			 */
			const onNotify = (changeset) => {
				if (!invalidate) return;
				if (invalidate(changeset) === true) this.invalidateLifecycle(`${sourceKey}:changed`);
			};
			this.useConnection(sourceKey, () => {
				return scope
					? store.$watch(scope, onNotify)
					: store.$subscribe(onNotify);
			});
			return;
		}

		/** @type {Object<string, *>} */
		const stores = this._stores ??= Object.create(null);

		/** @returns {void} */
		const refresh = () => {
			let changed = false;

			if (select) {
				const next = select(store);
				if (next === stores[label]) return;
				stores[label] = next;
				changed = true;
			}

			if (sync) {
				const ref = select ? stores[label] : store;
				const data = sync(ref);
				if (data && typeof data === 'object') {
					changed = this.propsManager.mergeState(data) || changed;
				}
			}

			if (changed) this.invalidateLifecycle(`${sourceKey}:changed`);
		};

		this.useConnection(sourceKey, () => {
			refresh();
			return scope
				? store.$watch(scope, refresh)
				: store.$subscribe(refresh);
		});
	}

	/**
	 * @param {string} label
	 * @returns {*}
	 */
	store(label) {
		return this._stores?.[label];
	}


	/**
	 * @returns {string}
	 * @abstract
	 */
	template() {
		throw new Error('Implement template() in subclass');
	}

	/** @returns {Object} */
	templateContext() {
		return {};
	}

	/** @returns {Object} */
	bakeTemplateContext() {
		this.scope.update();
		return this.scope.context;
	}

	/** @returns {Object} */
	_baseTemplateContext() {
		return {
			is_mobile: RenderableComponent._isMobile ??= App.device.is_mobile(),
			locale: App.object_state.locale,
		}
	}

	/**
	 * @returns {LifecycleLayer}
	 */
	static defineLifecycle() {}

	/** @returns {string[]} */
	static get observedAttributes() {
		return [];
	}

	/**
	 * @param {string} attributeName
	 * @returns {void}
	 */
	attributeChangedCallback(attributeName) {
		this.invalidateLifecycle(`attrChanged:${attributeName}`, LifecycleInvalidations.ATTRS);
	}

	/**
	 * @param {string} name
	 * @param {string} value
	 * @returns {void}
	 */
	setAttribute(name, value) {
		super.setAttribute(name, value);
		if (this.attributeObserver.isObserved(name)) {
			this.invalidateLifecycle(`runtimeObservedAttr:${name}`, LifecycleInvalidations.ATTRS);
		}
	}

	/**
	 * @param {string} name
	 * @returns {void}
	 */
	removeAttribute(name) {
		super.removeAttribute(name);
		if (this.attributeObserver.isObserved(name)) {
			this.invalidateLifecycle(`attrChanged:${name}`, LifecycleInvalidations.ATTRS);
		}
	}

	/**
	 * @param {string}  name
	 * @param {boolean} [force]
	 * @returns {void}
	 */
	toggleAttribute(name, force) {
		super.toggleAttribute(name, force);
		if (this.attributeObserver.isObserved(name)) {
			this.invalidateLifecycle(`attrChanged:${name}`, LifecycleInvalidations.ATTRS);
		}
	}

	/**
	 * @param {string} name
	 * @param {*}      [def=null]
	 * @returns {*}
	 */
	attr(name, def = null) {
		const rawValue = this.hasAttribute(name) ? this.getAttribute(name) : def;
		return this.attributeObserver.applyModifiers(name, rawValue);
	}

	/** @returns {Object<string, Function | *>} */
	provides() {
		return {};
	}

	/** @returns {OwnedResources} */
	get owned() {
		return this._m.owned ||= new OwnedResources(this.constructor.name);
	}

	/** @returns {ComponentScope} */
	get scope() {
		return this._m.scope ||= new ComponentScope();
	}

	/** @returns {SlotManager} */
	get slotManager() {
		return this._m.slotManager ||= new SlotManager(this);
	}

	/** @returns {BindingManager} */
	get bindingManager() {
		return this._m.bindingManager ||= new BindingManager(this, {
			directiveRegistry: App.classes.directive_registry.getInstance(),
		});
	}

	/** @returns {DeepProvider} */
	get deepProvider() {
		return this._m.deepProvider ||= new DeepProvider(this);
	}

	/** @returns {PropsManager} */
	get propsManager() {
		return this._m.propsManager ||= new PropsManager(this, {
			invalidateComponent: (reason) => this.invalidateLifecycle(reason, LifecycleInvalidations.PROPS),
			readAttr: (name, fallback) => this.attr(name, fallback),
		});
	}

	/** @returns {AttributeObserver} */
	get attributeObserver() {
		return this._m.attributeObserver ||= new AttributeObserver(this, {
			runModifier: (modifierName, value) => App.helper.template.run_modifier(modifierName, value),
		});
	}

	/** @returns {LifecycleHub} */
	get lifecycle() {
		return this._m.lifecycle ||= new LifecycleHub({});
	}

	/** @returns {LifecycleEngine} */
	get lifecycleEngine() {
		return this._m.lifecycleEngine ||= new LifecycleEngine({
			isConnected: () => this.isConnected,
			getHost: () => this,
			hub: this.lifecycle,
		});
	}

	/**
	 * @param {BuildOptions} [options]
	 * @returns {RenderableComponent}
	 */
	static build({
					 content = '',
					 attributes = {},
					 props = null
				 } = {}) {

		const self = new this();

		Node.from(content).forEach(node => self.append(node));

		for (const [key, value] of Object.entries(attributes)) {
			self.setAttribute(key, value);
		}

		props && self.replaceProps(props);

		return self;
	}

	/**
	 * @param {string|null}                   [name=null]
	 * @param {string | Node | Node[] | null} [content=null]
	 * @returns {void}
	 */
	setSlot(name = null, content = null) {

		if (!this.isLifecycleInitialized()) {
			this.slotManager.override(name, content);
			this.invalidateLifecycle('setSlot:preinit');
			return;
		}

		const {start, end} = this.slotManager.getSlotAnchors(name);

		this.bindingManager.disposeInRange(start, end);
		this.slotManager.replace(name, content);
		this.bindingManager.collectInRange(start, end);

		this.invalidateLifecycle('setSlot:rebindSlotOnly');
	}

	/**
	 * @param {string|null}                   [name=null]
	 * @param {string | Node | Node[] | null} [content=null]
	 * @returns {void}
	 */
	appendToSlot(name = null, content = null) {
		if (content == null) throw new Error('Slot content must be provided');

		if (!this.isLifecycleInitialized()) {
			this.slotManager.append(name, content);
			this.invalidateLifecycle('appendToSlot:preinit');
			return;
		}

		const {appendedNodes} = this.slotManager.append(name, content);

		for (const appendedNode of appendedNodes) {
			if (appendedNode && appendedNode.nodeType === Node.ELEMENT_NODE) {
				this.bindingManager.collectInSubtree(appendedNode);
			}
		}

		this.invalidateLifecycle('appendToSlot:rebindNewOnly');
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void}
	 */
	onLifecycle(phase, handler) {
		return this.lifecycle.on(phase, handler);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void}
	 */
	onceLifecycle(phase, handler) {
		return this.lifecycle.once(phase, handler);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {void}
	 */
	offLifecycle(phase, handler) {
		this.lifecycle.off(phase, handler);
	}

	/**
	 * @param {string} reason
	 * @param {number} [invalidationMask=0]
	 * @returns {void}
	 */
	invalidateLifecycle(reason, invalidationMask) {
		this.lifecycleEngine.invalidate(reason, invalidationMask);
	}

	/** @returns {boolean} */
	isLifecycleInitialized() {
		return this.lifecycleEngine.isInitialized();
	}

	/** @returns {LifecycleState} */
	getLifecycleState() {
		return this.lifecycleEngine.getState();
	}

	/** @returns {TraceEntry[]} */
	traceLifecycle() {
		return this.lifecycleEngine.getTrace();
	}

	/** @returns {Object<string, *>} */
	props() {
		return this.propsManager.getAll();
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	prop(name, fallback = null) {
		return this.propsManager.get(name, fallback);
	}

	/**
	 * @param {string}  name
	 * @param {boolean} [fallback=false]
	 * @returns {boolean}
	 */
	propBool(name, fallback = false) {
		return this.propsManager.getBool(name, fallback);
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	propInt(name, fallback = 0) {
		return this.propsManager.getInt(name, fallback);
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	propFloat(name, fallback = 0) {
		return this.propsManager.getFloat(name, fallback);
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	propJson(name, fallback = null) {
		return this.propsManager.getJson(name, fallback);
	}

	/**
	 * @param {string} path
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	getPropPath(path, fallback = null) {
		return this.propsManager.getStatePath(path, fallback);
	}

	/**
	 * @param {Object<string, *>} nextState
	 * @param {string}            [reason='replaceState']
	 * @returns {void}
	 */
	replaceProps(nextState, reason) {
		this.propsManager.replaceState(nextState, reason);
	}

	/**
	 * @param {Object<string, *>} partial
	 * @param {string}            [reason='assignState']
	 * @returns {void}
	 */
	setProps(partial, reason) {
		this.propsManager.assignState(partial, reason);
	}

	/**
	 * @param {Object<string, *>} partial
	 * @returns {boolean}
	 */
	mergeProps(partial) {
		return this.propsManager.mergeState(partial);
	}

	/**
	 * @param {string}         name
	 * @param {*}              value
	 * @param {SetPropOptions} [options]
	 * @returns {void}
	 */
	setProp(name, value, options) {
		this.propsManager.set(name, value, options);
	}

	/**
	 * @param {string} path
	 * @param {*}      value
	 * @param {string} [reason='setStatePath']
	 * @returns {void}
	 */
	setPropPath(path, value, reason) {
		this.propsManager.setStatePath(path, value, reason);
	}

	/**
	 * @param {string}                 key
	 * @param {(request: Object) => *} resolver
	 * @returns {void}
	 */
	provide(key, resolver) {
		this.deepProvider.provide(key, resolver);
	}

	/**
	 * @param {Object<string, Function | *>} providesObject
	 * @returns {void}
	 */
	provideMany(providesObject) {
		this.deepProvider.provideMany(providesObject);
	}

	/**
	 * @param {string}  key
	 * @param {Object}  [request]
	 * @returns {*}
	 */
	useProvided(key, request) {
		return this.deepProvider.useProvided(key, request);
	}

	/**
	 * @param {string}                          key
	 * @param {(value: *) => (false | void)}    onUpdate
	 * @param {Object}                          [request]
	 * @returns {() => void}
	 */
	subscribeProvided(key, onUpdate, request) {
		return this.deepProvider.subscribeProvided(key, onUpdate, request);
	}

	/**
	 * @param {string|null} [slotName]
	 * @returns {Node[]}
	 */
	slot(slotName) {
		return this.slotManager.read(slotName);
	}

	/**
	 * @param {string}                   attributeName
	 * @param {ObserveAttributeOptions}  [options]
	 * @returns {void}
	 */
	observeAttribute(attributeName, options) {
		this.attributeObserver.observeAttribute(attributeName, options);
	}

}

App.classes.renderable_component = RenderableComponent;

/**
 * @typedef {Object} ConnectionHandler
 * @property {(this: RenderableComponent) => (() => void) | void} connect
 *           — called on `connected`; return a teardown function or `undefined`.
 *             When a teardown is returned, it is called on `disconnected` (unless `disconnect` is also provided)
 * @property {(this: RenderableComponent) => void} [disconnect]
 *           — explicit disconnect handler; when provided, `connect`'s return value is ignored for teardown
 */

/**
 * @typedef {Object} UseStoreOptions
 * @property {string} [scope]
 *           — store watch scope passed to `store.$watch(scope, callback)`;
 *             when omitted, `store.$subscribe(callback)` is used instead
 * @property {(store: Store) => *} [select]
 *           — selector: extract a reference from the store (e.g. a single record);
 *             the return value is cached and accessible via `this.store(label)`
 * @property {(ref: *) => Object<string,*>} [sync]
 *           — sync mapper: receives the selected ref (or the store itself if `select` is absent)
 *             and returns a plain object whose entries are merged into `_stateProps` via `mergeState()`
 * @property {(changeset: ReactiveStatePendingMap) => boolean} [invalidate]
 *           — notification filter (when neither `select` nor `sync` are provided);
 *             return `true` to trigger an invalidation, `false` to ignore the changeset
 */

/**
 * @typedef {Object} BuildOptions
 * @property {string}                  [content='']    — HTML string parsed via `Node.from()` and appended as children
 * @property {Object<string, string>}  [attributes={}] — key-value pairs set via `setAttribute` on the new element
 * @property {Object<string, *>|null}  [props=null]    — state props passed to `replaceProps()` after construction
 */
/**
 * @callback StoreNotifyCallback
 * @param {ReactiveStatePendingMap} changeset — map of changed keys to their new values
 * @returns {void}
 */

/**
 * @typedef {Map<string, *>} ReactiveStatePendingMap
 */
;class SlotManager {
	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 */
	constructor(hostElement) {
		/** @type {RenderableComponent & HTMLElement} */
		this._hostElement = hostElement;

		/** @type {Map<string, SlotAnchors>} */
		this._anchorsByKey = new Map();

		/** @type {Map<string, Node[]>} */
		this._pendingOverridesByKey = new Map();

		/** @type {Map<string, Array<{name: string, value: string}>>} */
		this._slotTemplateAttributesByKey = new Map();

		/** @type {Node[]} */
		this._originalChildren = [];
	}

	/** @returns {void} */
	captureOriginalChildren() {
		this._originalChildren = Array.from(this._hostElement.childNodes);
	}

	/** @returns {void} */
	mountSlots() {
		this._anchorsByKey.clear();

		const defaultAssignmentsByKey = this._parseOriginalChildrenIntoAssignments(this._originalChildren);
		const slotElements = Array.from(this._hostElement.querySelectorAll('slot'));

		for (const slotElement of slotElements) {
			const key = this._normalizeSlotKey(slotElement.getAttribute('name'));

			const nodesForSlot = this._resolveNodesForSlot(key, defaultAssignmentsByKey);

			this._slotTemplateAttributesByKey.set(
				key,
				Array.from(slotElement.attributes).map(a => ({name: a.name, value: a.value}))
			);

			this._decorateSlotContentNodes(key, nodesForSlot);
			this._mountSingleSlot(slotElement, key, nodesForSlot);
		}

		this._pendingOverridesByKey.clear();
	}

	/** @returns {void} */
	hydrateSlots() {
		this._anchorsByKey.clear();
		this._scanForSlotAnchors(this._hostElement);
		this._pendingOverridesByKey.clear();
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @returns {Node[]}
	 */
	read(slotName = null) {
		const key = this._normalizeSlotKey(slotName);
		return this._readMountedSlotNodes(key);
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @param {SlotContent}   content
	 * @returns {string} — normalized slot key
	 */
	override(slotName = null, content) {
		if (content == null) throw new Error('Slot content must be provided');

		const key = this._normalizeSlotKey(slotName);
		this._pendingOverridesByKey.set(key, this._normalizeContentToNodes(content));
		return key;
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @param {SlotContent}   content
	 * @returns {SlotReplaceResult}
	 */
	replace(slotName = null, content) {
		if (content == null) throw new Error('Slot content must be provided');

		const key = this._normalizeSlotKey(slotName);
		const nodes = this._normalizeContentToNodes(content);

		const result = this._write(key, 'replace', nodes);
		return {changed: result.changed, key};
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @param {SlotContent}   content
	 * @returns {SlotAppendResult}
	 */
	append(slotName = null, content) {
		if (content == null) throw new Error('Slot content must be provided');

		const key = this._normalizeSlotKey(slotName);
		const nodes = this._normalizeContentToNodes(content);

		const result = this._write(key, 'append', nodes);
		return {changed: result.changed, key, appendedNodes: result.appendedNodes};
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @returns {SlotAnchors | undefined}
	 */
	getSlotAnchors(slotName = null) {
		return this._anchorsByKey.get(this._normalizeSlotKey(slotName));
	}

	/** @returns {IterableIterator<[string, SlotAnchors]>} */
	anchors() {
		return this._anchorsByKey.entries();
	}

	/**
	 * @param {Node[]} nodes
	 * @returns {Map<string, Node[]>}
	 */
	_parseOriginalChildrenIntoAssignments(nodes) {
		/** @type {Map<string, Node[]>} */
		const assignmentsByKey = new Map();

		for (const {slotName, node} of this._collectSlotAssignments(nodes)) {
			const key = this._normalizeSlotKey(slotName);
			if (!assignmentsByKey.has(key)) assignmentsByKey.set(key, []);
			assignmentsByKey.get(key).push(node);
		}

		return assignmentsByKey;
	}

	/**
	 * @param {string}              key
	 * @param {Map<string, Node[]>} defaultAssignmentsByKey
	 * @returns {Node[]}
	 */
	_resolveNodesForSlot(key, defaultAssignmentsByKey) {
		const overrideNodes = this._pendingOverridesByKey.get(key);
		if (overrideNodes) return overrideNodes.slice();

		return defaultAssignmentsByKey.get(key) || [];
	}

	/**
	 * @param {Node[]} nodes
	 * @returns {SlotAssignment[]}
	 */
	_collectSlotAssignments(nodes) {
		/** @type {SlotAssignment[]} */
		const assignments = [];

		/**
		 * @param {string | null} slotName
		 * @param {Node}          node
		 */
		const push = (slotName, node) => {
			if (node) assignments.push({slotName, node});
		};

		for (const node of nodes) {
			if (node.nodeType === Node.ELEMENT_NODE) {
				const tag = node.tagName.toLowerCase();

				if (tag === 'slot-content') {
					const wrapperSlotName = node.getAttribute('slot') || null;
					for (const child of Array.from(node.childNodes)) {
						push(child.getAttribute?.('slot') ?? wrapperSlotName, child);
					}
					continue;
				}

				if (node.hasAttribute('slot')) {
					push(node.getAttribute('slot'), node);
					continue;
				}
			}

			push(null, node);
		}

		return assignments;
	}

	/**
	 * @param {Node} parent
	 * @returns {void}
	 */
	_scanForSlotAnchors(parent) {
		const START_PREFIX = 'ws-slot:start:';

		for (let node = parent.firstChild; node; node = node.nextSibling) {
			if (node.nodeType === Node.COMMENT_NODE && node.textContent.startsWith(START_PREFIX)) {
				const key = node.textContent.slice(START_PREFIX.length);
				const end = this._findMatchingEndAnchor(node, key);
				if (end) this._anchorsByKey.set(key, {start: node, end});
				continue;
			}

			if (node.nodeType === Node.ELEMENT_NODE && !(node instanceof RenderableComponent)) {
				this._scanForSlotAnchors(node);
			}
		}
	}

	/**
	 * @param {Comment} startNode
	 * @param {string}  key
	 * @returns {Comment | null}
	 */
	_findMatchingEndAnchor(startNode, key) {
		const target = `ws-slot:end:${key}`;
		for (let node = startNode.nextSibling; node; node = node.nextSibling) {
			if (node.nodeType === Node.COMMENT_NODE && node.textContent === target) {
				return /** @type {Comment} */ (node);
			}
		}
		return null;
	}

	/**
	 * @param {Element} slotElement
	 * @param {string}  key
	 * @param {Node[]}  nodes
	 * @returns {void}
	 */
	_mountSingleSlot(slotElement, key, nodes) {
		const start = document.createComment(`ws-slot:start:${key}`);
		const end = document.createComment(`ws-slot:end:${key}`);
		const parent = slotElement.parentNode;

		parent.insertBefore(start, slotElement);
		parent.insertBefore(end, slotElement);
		slotElement.remove();

		parent.insertBefore(this._createFragment(nodes), end);

		this._anchorsByKey.set(key, {start, end});
	}

	/**
	 * @param {string} key
	 * @returns {Node[]}
	 */
	_readMountedSlotNodes(key) {
		const anchors = this._anchorsByKey.get(key);
		if (!anchors) return [];

		/** @type {Node[]} */
		const collected = [];
		this._iterateBetween(anchors.start, anchors.end, (node) => collected.push(node));
		return collected;
	}

	/**
	 * @param {string}             key
	 * @param {'replace' | 'append'} mode
	 * @param {Node[]}             nodes
	 * @returns {{changed: boolean, appendedNodes: Node[]}}
	 */
	_write(key, mode, nodes) {
		if (!this._anchorsByKey.has(key)) {
			if (mode === 'replace') {
				this._pendingOverridesByKey.set(key, nodes);
			} else {
				const existing = this._pendingOverridesByKey.get(key) || [];
				this._pendingOverridesByKey.set(key, existing.concat(nodes));
			}
			return {changed: false, appendedNodes: []};
		}

		this._decorateSlotContentNodes(key, nodes);

		if (mode === 'replace') {
			this._replaceMountedSlotNodes(key, nodes);
			return {changed: true, appendedNodes: []};
		}

		this._appendMountedSlotNodes(key, nodes);
		return {changed: true, appendedNodes: nodes};
	}

	/**
	 * @param {string} key
	 * @param {Node[]} nodes
	 * @returns {void}
	 */
	_replaceMountedSlotNodes(key, nodes) {
		const anchors = this._anchorsByKey.get(key);
		if (!anchors) throw new Error(`Slot anchors not found for key: ${key}`);

		this._iterateBetween(anchors.start, anchors.end, (node) => node.parentNode?.removeChild(node));
		anchors.end.parentNode.insertBefore(this._createFragment(nodes), anchors.end);
	}

	/**
	 * @param {string} key
	 * @param {Node[]} nodes
	 * @returns {void}
	 */
	_appendMountedSlotNodes(key, nodes) {
		const anchors = this._anchorsByKey.get(key);
		if (!anchors) throw new Error(`Slot anchors not found for key: ${key}`);

		anchors.end.parentNode.insertBefore(this._createFragment(nodes), anchors.end);
	}

	/**
	 * @param {Node}                 start
	 * @param {Node}                 end
	 * @param {(node: Node) => void} visitor
	 * @returns {void}
	 */
	_iterateBetween(start, end, visitor) {
		for (let node = start.nextSibling; node && node !== end;) {
			const next = node.nextSibling;
			visitor(node);
			node = next;
		}
	}

	/**
	 * @param {string | null} name
	 * @returns {string}
	 */
	_normalizeSlotKey(name) {
		return name != null ? String(name) : '__default__';
	}

	/**
	 * @param {SlotContent} content
	 * @returns {Node[]}
	 */
	_normalizeContentToNodes(content) {
		if (content == null) return [];

		if (typeof content === 'string') return Node.from(content);
		if (Array.isArray(content)) return content.filter(Boolean);
		if (content instanceof Node) return [content];

		return [document.createTextNode(String(content))];
	}

	/**
	 * @param {Node[]} nodes
	 * @returns {DocumentFragment}
	 */
	_createFragment(nodes) {
		const fragment = document.createDocumentFragment();
		for (const node of nodes) fragment.appendChild(node);
		return fragment;
	}

	/**
	 * @param {string} key
	 * @param {Node[]} nodes
	 * @returns {void}
	 */
	_decorateSlotContentNodes(key, nodes) {
		const attrs = this._slotTemplateAttributesByKey.get(key);
		if (!attrs) return;

		for (const node of nodes) {
			if (node.nodeType !== Node.ELEMENT_NODE) continue;
			App.helper.dom.merge_attributes(node, Array.from(attrs), {exclude: ['name']});
		}
	}
}

App.classes.slot_manager = SlotManager;

/**
 * @typedef {Object} SlotAnchors
 * @property {Comment} start — `<!--ws-slot:start:{key}-->` comment node
 * @property {Comment} end   — `<!--ws-slot:end:{key}-->` comment node
 */

/**
 * @typedef {Object} SlotReplaceResult
 * @property {boolean} changed — `true` if anchors existed and DOM was mutated; `false` if stored as pending override
 * @property {string}  key     — normalized slot key
 */

/**
 * @typedef {Object} SlotAppendResult
 * @property {boolean} changed       — `true` if anchors existed and DOM was mutated
 * @property {string}  key           — normalized slot key
 * @property {Node[]}  appendedNodes — the nodes actually inserted into the DOM (empty if pre-mount)
 */

/**
 * @typedef {Object} SlotAssignment
 * @property {string | null} slotName — target slot name; `null` = default slot
 * @property {Node}          node     — the DOM node assigned to this slot
 */

/**
 * @typedef {string | Node | Node[] | null} SlotContent
 * Accepted slot content formats:
 * - `string`  — parsed into DOM nodes via `Node.from()`
 * - `Node`    — single DOM node wrapped into an array
 * - `Node[]`  — array of DOM nodes used directly (falsy entries filtered)
 * - `null`    — throws; callers must guard
 */
;class FailException extends Error {}
class SkipException extends Error {}
class ValidationException extends Error {
	constructor(errors) {
		super('Validation failed');
		this.errors = errors;
	}
}
;class Rule extends FillableMixin() {
	static  ATTRIBUTE = 'attribute';

	constructor(params = {}) {
		super(params);
		this.messages = this.messages ?? {};
	}

	static make(params = {}) {
		return new this(params);
	}

	setMessages(messages) {
		this.messages = { ...this.messages, ...messages };
		return this;
	}

	apply(value) {
		throw new Error('Rule.apply() must be implemented');
	}

	/**
	 * @param {LocaleKey|string} key
	 * @param {Record<string, string|number>}  params
	 */
	message(key, params = {}) {
		let template = this.messages[key] ?? App.i18n.t(key) ?? 'invalid value';

		let attribute = this.messages[Rule.ATTRIBUTE];
		let localParams = params;
		if (attribute === undefined && params[Rule.ATTRIBUTE] !== undefined) {
			attribute = params[Rule.ATTRIBUTE];
			localParams = { ...params };
			delete localParams[Rule.ATTRIBUTE];
		}

		for (const k in localParams) {
			template = template.replaceAll(`:${k}:`, String(localParams[k]));
		}

		template = attribute
			? template.replaceAll(' :attr: ', ` "${attribute}" `)
			: template.replaceAll(' :attr: ', ' ');

		return template;
	}

	skip(reason = '') {
		throw new SkipException(reason);
	}

	/**
	 * @param {LocaleKey|string} message
	 * @param {Record<string, string|number>}  params
	 */
	fail(message = '', params = {}) {
		throw new FailException(this.message(message, params));
	}
}
;/**
 * @property {null|number} min
 * @property {null|number} max
 */
class StringRule extends Rule {

	apply(value) {
		if (typeof value !== 'number' && typeof value !== 'string') {
			this.fail('validation_messages.string');
		}

		value = String(value).trim();

		let length = 0;
		for (const _ of value) length++;

		if (this.max != null && length > this.max) {
			this.fail('validator_messages.string_max', { max: this.max });
		}

		if (this.min != null && length < this.min) {
			this.fail('validator_messages.string_min', { min: this.min });
		}

		return value;
	}
}

/**
 * @property {number|null} max
 * @property {number|null} min
 */
class NumberRule extends Rule {

	apply(value) {
		if (typeof value === 'string') {
			if (value.trim() === '' || !Number.isFinite(Number(value))) {
				this.fail('validator_messages.numeric');
			}
		} else if (typeof value !== 'number' || !Number.isFinite(value)) {
			this.fail('validator_messages.numeric');
		}

		value = Number(value);

		if (this.max != null && value > this.max) {
			this.fail('validator_messages.numeric_max', { max: this.max });
		}

		if (this.min != null && value < this.min) {
			this.fail('validator_messages.numeric_min', { min: this.min });
		}

		return value;
	}
}

class NullableRule extends Rule {
	apply(value) {
		if (value === null || value === '') {
			this.skip('null');
		}
		return value;
	}
}

/**
 * @property {RegExp|string} pattern
 */
class RegexRule extends Rule {

	apply(value) {
		if (typeof value !== 'string' && typeof value !== 'number') {
			this.fail('validator_messages.regex');
		}

		const pattern = this.pattern instanceof RegExp
			? this.pattern
			: new RegExp(this.pattern);

		if (!pattern.test(String(value))) {
			this.fail('validator_messages.regex');
		}

		return value;
	}
}

class EmailRule extends Rule {

	static MAX_LENGTH = 250;

	static #REGEX  = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
	apply(value) {
		if (typeof value !== 'string') this.fail('validation_messages.string');

		let length = 0;
		for (const _ of value) length++;

		if (length > EmailRule.MAX_LENGTH) {
			this.fail('validation_messages.string_max', { max: EmailRule.MAX_LENGTH });
		}

		if (!EmailRule.#REGEX.test(value)) {
			this.fail('validation_messages.email');
		}

		return value;
	}
}

;class Validator {

	/**
	 * @param {Record<string, any>} data
	 * @param {Record<string, Rule[]|Rule.constructor[]>}rules
	 * @param {Record<LocaleKey,string>} messages
	 */
	constructor(data, rules, messages = {}) {
		this.data = data;
		this.rules = rules;
		this.messages = messages;
		this.errors = {};
		this._validated = false;
		this._validatedData = null;
	}

	/**
	 * @param {Record<string, any>} data
	 * @param {Record<string, (Rule|typeof Rule)[]>} rules
	 * @param {Record<string,Record<string, string>>} messages
	 */
	static make(data, rules, messages = {}) {
		return new this(data, rules, messages);
	}

	validate(throwOnFail = false) {
		const validated = {};

		outer: for (const field in this.rules) {
			const fieldRules = this.rules[field];
			let value = this.data[field] ?? null;

			for (let i = 0; i < fieldRules.length; i++) {
				const rule = this._buildRule(fieldRules[i]);
				const messagesOverride = this.messages[field] ?? null;
				if (messagesOverride) rule.setMessages(messagesOverride);

				try {
					value = rule.apply(value);
				} catch (e) {
					if (e instanceof SkipException) {
						validated[field] = value;
						continue outer;
					}
					if (e instanceof FailException) {
						this.errors[field] ??= [];
						this.errors[field].push(e.message);
						continue outer;
					}
					throw e;
				}
			}

			validated[field] = value;
		}

		this._validated = true;
		this._validatedData = validated;

		if (throwOnFail && this.fails()) {
			throw new ValidationException(this.errors);
		}

		return !this.fails();
	}

	fails() {
		this._ensureValidated();
		return Object.keys(this.errors).length > 0;
	}

	/**
	 * @param {string} [field]
	 * @return {*|{}}
	 */
	getErrors(field) {
		this._ensureValidated();
		return field ? this.errors[field] : this.errors;
	}

	validated() {
		this._ensureValidated();
		const result = {};
		for (const field in this._validatedData) {
			if (this.errors[field] === undefined) {
				result[field] = this._validatedData[field];
			}
		}
		return result;
	}

	getData() {
		return this.data;
	}

	_buildRule(rule) {
		if (rule instanceof Rule) return rule;
		if (typeof rule === 'function' && rule.prototype instanceof Rule) {
			return new rule();
		}
		throw new Error('Invalid rule provided.');
	}

	_ensureValidated() {
		if (!this._validated) {
			throw new Error('You must call validate() before accessing this function.');
		}
	}
}
;const deferred = () => {
	let resolve, reject;
	const promise = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});
	return {promise, resolve, reject};
};
const once = (fn) => {
	let fired = false;
	return (...a) => {
		if (fired) return;
		fired = true;
		return fn(...a);
	};
};

class Step {
	constructor(name, {mount, run, unmount, rollback, condition}) {
		this.name = name;
		this.mount = mount || (({resolve}) => resolve());
		this.run = run || (() => ({}));
		this.unmount = unmount || (() => {
		});
		this.rollback = rollback || (() => {
		});
		this.condition = condition || (() => true);
	}
}

class AbstractWorkflow {
	constructor(initial = {}) {
		this.ctx = initial;
		this.steps = this.define();
		this.executed = [];
	}

	define() {
		throw new Error("Method 'define()' must be implemented.");
	}


	static run(initial = {}) {
		const workflow = new this(initial);
		return workflow.run();
	}

	async run() {
		try {
			for (const step of this.steps) {
				this.executed.push(step);
				if (!(step.condition({ctx: this.ctx}))) {
					console.debug(`Skipping step: ${step.name}`);
					continue;
				}

				const d = deferred();

				const control = {
					resolve: once((val) => {
						if (typeof val !== 'undefined') this.ctx[step.name] = val;
						d.resolve(val);
					}),
					reject: once((err) => d.reject(err || new Error('Step rejected'))),
				};

				const mountedResult = await new Promise((res, rej) => {
					step.mount({ctx: this.ctx, resolve: res, reject: control.reject, control});
				});

				step.run({ctx: this.ctx, mountedResult, resolve: control.resolve, reject: control.reject, control});

				await d.promise;

				await step.unmount({ctx: this.ctx, control});
			}
			return this.ctx;
		} catch (err) {
			await this.rollback(err);
			console.debug('Workflow failed:', err);
		}
	}

	async rollback(err) {
		console.debug("Rollback workflow due to error:", err);
		for (let i = this.executed.length - 1; i >= 0; i--) {
			const step = this.executed[i];
			try {
				await step.rollback({ctx: this.ctx, error: err});
				delete this.ctx[step.name];
			} catch (e) {
				console.debug(`Error during rollback of step ${step.name}:`, e);
			}
		}
	}
}


App.classes.workflows.step = Step;
App.classes.workflows.workflow = AbstractWorkflow;
;class ExportReportWorkflow extends AbstractWorkflow {
	define() {
		return [
			new Step("url", {
				condition({ctx}) {
					return !ctx.url;
				},
				run({resolve}) {
					resolve(App.helper.endpoint.ajax())
				}
			}),
			new Step("data", {
				condition({ctx}) {
					return !ctx.format || !ctx.type
				},
				mount({resolve, reject, ctx}) {
					const urlParams = new URLSearchParams({
						specials: ctx.special_formats ||  []
					});
					App.modal.show({
						url: ctx.url + '?' + App.actions.get('export_action:format') + '&' + urlParams.toString(),
						title: App.object_state.locale.export.do,
						group: 'white',
						width: 'auto',
						onload: function (modal) {
							const channel = new ComponentChannel(modal);
							channel.provide(
								'deliveryMessage',
								() => ctx.delivery === 'email' ? App.i18n.t('send') : App.i18n.t('download')
							);
							resolve(modal);
						},
						onhide: function () {
							reject(new Error("Export format selection was cancelled by user."));
						}
					});
				},
				run({mountedResult, resolve}) {
					mountedResult.addDelegatedEventListener('submit', 'ws-form', function (event) {
						event.preventDefault();
						resolve(event.detail.values);
					});
				},
				unmount() {
					App.modal.hide({immediate: true});
				},
				rollback() {
					App.modal.hide({immediate: true});
				}
			}),
			new Step("recipients", {
				condition({ctx}) {
					return ctx.delivery === 'email';
				},
				mount({ctx, resolve, reject}) {
					App.modal.show({
						url: ctx.url + '?' + App.actions.get('export_action:recipient'),
						width: 450,
						title: App.object_state.locale.export.do_mail,
						group: 'white',
						onload: function (modal) {
							App.mass_operations.init_search();
							resolve(modal);
						},
						onhide: function () {
							reject(new Error("Export format selection was cancelled by user."));
						}
					});
				},
				run({mountedResult, resolve, reject}) {
					const submitBtn = mountedResult.querySelector('ws-btn[data-role="submit"]');
					let user_id;
					mountedResult.addDelegatedEventListener('click', '.menu__item[data-id]', function (event, target) {
						App.controls.menu.item_set_active(target);
						user_id = target.getAttribute('data-id');
						submitBtn.toggle(user_id)
					});
					submitBtn.addEventListener('click', function () {
						resolve([user_id]);
					})
				},
				rollback() {
					App.modal.hide({immediate: true});
				}
			}),
			new Step("send", {
				run({ctx, resolve}) {
					const body = {
						'page': ctx.page,
						...ctx.data,
						'delivery': ctx.delivery,
						'recipients': ctx.recipients,
						'meta': {
							...(ctx.meta || {}),
							'ids': ctx.ids,
						}
					};
					const url = ctx.url + '?' + App.actions.get(`export_action:do`);
					switch (ctx.delivery) {
						case 'download':
							App.core.download_file_with_loader(
								url,
								App.helper.object.toFormUrlEncoded(body)
							);
							break;
						case 'email':
							$.ajax({
								url: url,
								method: 'POST',
								data: body,
								success: function () {
									App.modal.hide({immediate: true});
								}
							});
							break;
					}
					resolve();
				}
			})
		];
	}
}

App.classes.workflows.export_report = ExportReportWorkflow;
